import { ChoicePickerCapabilityRenderer } from './choicePicker';
import { RecordEditorNullToggle } from './recordEditorNullToggle';
import { RecordEditorStringSelection } from './recordEditorStringSelection';
import { OrderedSelectionCapabilityRenderer } from './orderedSelection';
import { GalleryFieldPresentation } from './GalleryFieldPresentation';
import { collectionToolbarActions } from './collectionActionPresentation';
import { BaseInline } from '../base/BaseInline';
import { GalleryPresentationFieldsSchema, normalizeGalleryPresentationSettings, validateGalleryPresentationSettings } from './galleryPresentationSettings';
import { RecordEditorArraySorting, RecordEditorSortableArrayItem } from './recordEditorArraySorting';
import { RuntimeTerminalCapabilityRenderer } from './runtimeTerminal';
import { parseTypedSecretInput } from './typed-secret-input';
import { TypedDisplayCell } from './typedDisplayCell';
import { LocalizedDisplayText, LocalizedMessageText, resolveI18nDisplayText, resolveLocalizedDisplayParameters } from './localizedDisplayValue';
import { useMonkeysLocale } from '../../provider/context';
import { renderDateTimeValue } from './dateTimeValue';
import { projectReadOnlyEditorFields } from './recordEditorReadOnly';
import { FilePickerCapabilityRenderer } from './filePicker';
import { applyEditorDraftPatch } from './recordEditorDraftPatch';
import { fromControlValue, toControlValue, type RecordEditorDateField } from './recordEditorDate';
import { renderTableMessageValue } from './tableMessageValue';
import { RecordEditorPathSchema, type RecordEditorDraftOperation } from '@inf-monkeys-tech/monkeys';
import { createRecordEditorDraft, recordEditorDraftIsDirty, recordEditorPathKey } from '@inf-monkeys-tech/monkeys/runtime';
import { applyEditorFieldChange, editorObjects, projectEditorFields, editorLeafFields, editorDraftErrors } from './recordEditorDraft';
import { RecordDetailCopyValue } from "./recordDetailCopyValue";
import { NumericSeriesCapabilityRenderer } from "./numericSeries";
import { SummaryChartCapabilityRenderer } from './summaryChart';
import type { JsonObject, JsonValue } from "@inf-monkeys-tech/monkeys";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Children,
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  BarChart3,
  Calendar,
  CalendarDays,
  Check,
  Clock3,
  Columns3,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  Database,
  Download,
  Edit,
  Eye,
  FileText,
  FolderTree,
  GitBranch,
  GitCommitHorizontal,
  GripVertical,
  History as HistoryLucide,
  LayoutDashboard,
  LayoutGrid,
  Link2,
  Loader2,
  HardDrive,
  Hash,
  Image as ImageIcon,
  Images,
  Maximize2,
  MoreHorizontal,
  PanelRightClose,
  Paperclip,
  Pencil,
  Plus,
  Puzzle,
  RefreshCw,
  Rows3,
  Save,
  Search,
  SlidersHorizontal,
  Table2,
  Table as TableIcon,
  Tag,
  Trash2,
  Type,
  Braces,
  X,
} from "lucide-react";

import {
  BaseBadge,
  BaseButton,
  BaseCheckbox,
  BaseCode,
  BaseDescriptionList,
  BaseDialog,
  BaseDropdownMenu,
  BaseField,
  BaseFieldset,
  BaseHeading,
  BaseInput,
  BaseSelect,
  BaseLayout,
  BaseLayoutPane,
  BaseListFooter,
  BaseMultiSelect,
  BaseNotice,
  BasePanel,
  BasePagination,
  BaseSectionHeader,
  BaseStack,
  BaseTable,
  BaseTableBody,
  BaseTableCell,
  BaseTableContainer,
  BaseTableHead,
  BaseTableHeader,
  BaseTableRow,
  BaseTextarea,
  BaseToolbar,
} from "../base";
import {
  DataExplorerButton,
  DataExplorerCheckbox,
  DataExplorerCollectionFooter,
  DataExplorerDetailShell,
  DataExplorerDraggableTree,
  DataExplorerDisplayActionMenu,
  DataExplorerDisplayCard,
  DataExplorerPage,
  DataExplorerRecordCard,
  DataExplorerToolbarActions,
  DataExplorerToolbarShell,
  DataExplorerTreeShell,
  DataExplorerViewCollection,
  DataExplorerViewShell,
  DataExplorerViewTree,
  type DataExplorerAppearance,
  type DataExplorerDisplayAction,
  type DataExplorerDisplayItem,
  type DataExplorerToolbarAction,
} from "../data-explorer";
import { DATA_EXPLORER_APPEARANCE_OPTIONS } from "../data-explorer/appearanceOptions";
import {
  WorkbenchContentToolbar,
  WorkbenchGalleryView,
  WorkbenchTableView,
  type WorkbenchGalleryColumn,
  type WorkbenchGallerySettingsValue,
  type WorkbenchTableViewColumn,
} from "../workbench";
import {
  computeRecordHistoryDag,
  RecordHistoryDagRail,
} from "./recordHistoryDag";
import {
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FloatingPopover,
  FloatingPopoverContent,
  FloatingPopoverTrigger,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectGroup,
  SelectLabel,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
  Switch,
  ScrollArea,
  ScrollBar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "../primitives";
import {
  MediaPreviewFrame,
  MediaPreviewImage,
  type MediaPreviewRatio,
} from "../primitives/MediaPreview";
import { CapabilityStatus } from "./CapabilityStatus";
import type {
  CapabilityTranslate,
  DesignCapabilityRenderer,
  DesignCapabilityRendererContext,
  GalleryLoadMoreIntentPayload,
} from "./contracts";
import { isJsonObject } from "./contracts";
import { DESIGN_CAPABILITY_RENDERER_KEYS } from "./neutralCatalog";
import { DESIGN_CAPABILITY_I18N_KEYS } from "./i18n";
import {
  DataContextBarCapabilityRenderer,
  OntologyTrendMatrixCapabilityRenderer,
} from "./nativeAnalytics";
import { OntologyManagementCapabilityRenderer } from "./ontologyManagement";
import { FeatureColumnManagerCapabilityRenderer } from "./featureColumnManager";

const stringValue = (value: JsonValue | undefined) =>
  typeof value === "string" && value.trim() ? value : undefined;

const copyValue = (
  value: JsonValue | undefined,
  key: JsonValue | undefined,
  translate: CapabilityTranslate,
  fallbackKey: string,
): ReactNode => {
  const semanticKey = stringValue(key);
  if (semanticKey) return translate(semanticKey);
  const dataValue = stringValue(value);
  return dataValue ?? translate(fallbackKey);
};

const copyValueOrFallback = (
  value: JsonValue | undefined,
  key: JsonValue | undefined,
  translate: CapabilityTranslate,
  fallback: ReactNode,
): ReactNode => {
  const semanticKey = stringValue(key);
  if (semanticKey) return translate(semanticKey);
  return stringValue(value) ?? fallback;
};

const objectArray = (value: JsonValue | undefined): JsonObject[] =>
  Array.isArray(value) ? value.filter(isJsonObject) : [];

const readAppearanceEnum = <T extends string>(
  value: unknown,
  allowed: readonly T[],
): T | undefined =>
  typeof value === "string" && allowed.includes(value as T)
    ? (value as T)
    : undefined;

const resolveDataExplorerAppearance = (
  value: JsonValue | undefined,
): DataExplorerAppearance | undefined => {
  if (!isJsonObject(value)) return undefined;
  const appearance: DataExplorerAppearance = {};
  const surface = readAppearanceEnum(
    value.surface,
    DATA_EXPLORER_APPEARANCE_OPTIONS.surface,
  );
  const preset = readAppearanceEnum(
    value.preset,
    DATA_EXPLORER_APPEARANCE_OPTIONS.preset,
  );
  const density = readAppearanceEnum(
    value.density,
    DATA_EXPLORER_APPEARANCE_OPTIONS.density,
  );
  const radius = readAppearanceEnum(
    value.radius,
    DATA_EXPLORER_APPEARANCE_OPTIONS.radius,
  );
  const border = readAppearanceEnum(
    value.border,
    DATA_EXPLORER_APPEARANCE_OPTIONS.border,
  );
  const background = readAppearanceEnum(
    value.background,
    DATA_EXPLORER_APPEARANCE_OPTIONS.background,
  );
  if (surface) appearance.surface = surface;
  if (preset) appearance.preset = preset;
  if (density) appearance.density = density;
  if (radius) appearance.radius = radius;
  if (border) appearance.border = border;
  if (background) appearance.background = background;
  return Object.keys(appearance).length ? appearance : undefined;
};

const SearchIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const RefreshIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M20 11a8 8 0 1 0 2 5.5" />
    <path d="M20 4v7h-7" />
  </svg>
);

const PlusIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const MoreIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="currentColor"
  >
    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />
  </svg>
);

const DeleteIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M4 7h16M9 7V4h6v3m-8 0 1 13h8l1-13M10 11v5m4-5v5" />
  </svg>
);

const EditIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="m4 20 4.5-1 10-10a2.1 2.1 0 0 0-3-3l-10 10Z" />
    <path d="m14 7 3 3" />
  </svg>
);

const ViewIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
    <circle cx="12" cy="12" r="2.5" />
  </svg>
);

const HistoryIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5M12 7v5l3 2" />
  </svg>
);

const DownloadIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 3v12m-4-4 4 4 4-4M4 20h16" />
  </svg>
);

const UploadIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 16V4m-4 4 4-4 4 4M4 20h16" />
  </svg>
);

const SettingsIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="3" />
    <path
      d="M19 13.5v-3l-2-.7-.7-1.7.9-1.9-2.1-2.1-1.9.9-1.7-.7L10.5 2h-3l-.7 2-1.7.7-1.9-.9L1.1 6l.9 1.9-.7 1.7-2 .7v3l2 .7.7 1.7-.9 1.9 2.1 2.1 1.9-.9 1.7.7.7 2h3l.7-2 1.7-.7 1.9.9 2.1-2.1-.9-1.9.7-1.7Z"
      transform="translate(2) scale(.83)"
    />
  </svg>
);

const LinkIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="m10 13 4-4M8 16H6a4 4 0 0 1 0-8h3M16 8h2a4 4 0 1 1 0 8h-3" />
  </svg>
);

const DataKindIcon = ({ kind }: { kind?: string }) => {
  if (kind === "folder" || kind === "group") {
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <path d="M3 6.5h6l2 2h10v9.5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      </svg>
    );
  }
  if (kind === "view" || kind === "dashboard") {
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M8 4v16M8 10h13" />
      </svg>
    );
  }
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v7c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12v7c0 1.7 3.6 3 8 3s8-1.3 8-3v-7" />
    </svg>
  );
};

const actionTone = (
  action: JsonObject,
): "default" | "primary" | "danger" | "muted" =>
  action.destructive === true || action.presentation === "danger"
    ? "danger"
    : action.presentation === "primary"
      ? "primary"
      : action.presentation === "icon"
        ? "muted"
        : "default";

const semanticIcon = (semanticIdValue: string | undefined) => {
  const semanticId = (semanticIdValue ?? "").toLowerCase();
  if (semanticId === "table") return <TableIcon className="h-4 w-4" />;
  if (semanticId === "layout-grid") return <LayoutGrid className="h-4 w-4" />;
  if (semanticId === "images") return <Images className="h-4 w-4" />;
  if (/(database|ontology)/.test(semanticId))
    return <Database className="h-4 w-4" />;
  if (/(grid|all-data|table)/.test(semanticId))
    return <Table2 className="h-4 w-4" />;
  if (/(chart|dashboard)/.test(semanticId))
    return <LayoutDashboard className="h-4 w-4" />;
  if (/(create|add|new|plus)/.test(semanticId)) return <PlusIcon />;
  if (/(edit|update|rename|pencil)/.test(semanticId)) return <EditIcon />;
  if (/(view|open|detail|preview|eye)/.test(semanticId)) return <ViewIcon />;
  if (/(history|revision|rollback|restore|clock)/.test(semanticId))
    return <HistoryIcon />;
  if (/(export|download)/.test(semanticId)) return <DownloadIcon />;
  if (/(import|upload)/.test(semanticId)) return <UploadIcon />;
  if (/(setting|configure|column|layout)/.test(semanticId))
    return <SettingsIcon />;
  if (/(attach|link|relation)/.test(semanticId)) return <LinkIcon />;
  if (/(refresh|reload)/.test(semanticId)) return <RefreshIcon />;
  return <MoreIcon />;
};

const actionIcon = (action: JsonObject) => {
  if (action.destructive === true || action.presentation === "danger") {
    return <DeleteIcon />;
  }
  const governedIcon = isJsonObject(action.icon)
    ? stringValue(action.icon.id)
    : undefined;
  return semanticIcon(governedIcon ?? stringValue(action.id));
};

const legacyRecordActionIcon = (actionId: string) => {
  if (actionId === "record-detail") return <Eye className="mr-2 h-4 w-4" />;
  if (actionId === "record-edit") return <Edit className="mr-2 h-4 w-4" />;
  if (actionId === "record-history")
    return <HistoryLucide className="mr-2 h-4 w-4" />;
  if (actionId === "record-delete") return <Trash2 className="mr-2 h-4 w-4" />;
  return <span className="mr-2 h-4 w-4">{semanticIcon(actionId)}</span>;
};

const actionAriaLabel = (
  action: JsonObject,
  translate: CapabilityTranslate,
) => {
  const ariaLabelKey = stringValue(action.ariaLabelKey);
  return ariaLabelKey ? translate(ariaLabelKey) : undefined;
};

const collectionRowActions = (
  actions: JsonObject[],
  itemId: string,
  translate: CapabilityTranslate,
  emitIntent: DesignCapabilityRendererContext["emitIntent"],
  port: "capability.card-grid.row-action" | "capability.gallery.row-action",
  deletePort?: "capability.card-grid.delete",
): Array<DataExplorerDisplayAction<JsonObject>> =>
  actions.flatMap((action) => {
    const actionId = stringValue(action.id);
    if (!actionId) return [];
    return [
      {
        id: actionId,
        label: copyValueOrFallback(
          action.label,
          action.labelKey,
          translate,
          actionId,
        ),
        icon: actionIcon(action),
        tone: actionTone(action),
        disabled: action.disabled === true,
        separatorBefore:
          action.presentation === "danger" || action.destructive === true,
        onAction: () =>
          actionId === "record-delete" && deletePort
            ? emitIntent(deletePort, { itemId })
            : emitIntent(port, { itemId, actionId }),
      },
    ];
  });

const formatCollectionTimestamp = (value: JsonValue | undefined) => {
  if (value === null || value === undefined || value === "") return "-";
  const date = new Date(
    typeof value === "number" && value < 1_000_000_000_000
      ? value * 1000
      : typeof value === "number" || typeof value === "string"
        ? value
        : Number.NaN,
  );
  if (Number.isNaN(date.getTime())) return governedReadValue(value);
  const locale =
    typeof navigator !== "undefined" ? navigator.language : undefined;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
};

const formatLegacyTableTimestamp = (value: JsonValue | undefined) => {
  if (value === null || value === undefined || value === "") return "-";
  const date = new Date(
    typeof value === "number" && value < 1_000_000_000_000
      ? value * 1000
      : typeof value === "number" || typeof value === "string"
        ? value
        : Number.NaN,
  );
  if (Number.isNaN(date.getTime())) return "-";
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatLegacyFileSize = (value: JsonValue | undefined) => {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0)
    return "-";
  if (value === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const unit = Math.min(
    Math.floor(Math.log(value) / Math.log(1024)),
    units.length - 1,
  );
  return `${Math.round((value / 1024 ** unit) * 100) / 100} ${units[unit]}`;
};

const formatDetailTimestamp = (value: JsonValue | undefined) => {
  if (value === null || value === undefined || value === "") return "-";
  const date = new Date(
    typeof value === "number" && value < 1_000_000_000_000
      ? value * 1000
      : typeof value === "number" || typeof value === "string"
        ? value
        : Number.NaN,
  );
  if (Number.isNaN(date.getTime())) return governedReadValue(value);
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const stringArray = (value: JsonValue | undefined): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

const primitiveValue = (value: JsonValue | undefined): ReactNode => {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return String(value);
  return typeof value === "string" || typeof value === "number" ? value : "";
};

const governedReadValue = (value: JsonValue | undefined): ReactNode => {
  if (!isJsonObject(value)) return primitiveValue(value);
  const kind = stringValue(value.kind);
  if (kind === "json") {
    if (value.dataType === 'img') {
      const safeUrl = (url: unknown) => {
        if (typeof url !== 'string') return undefined;
        try { const parsed = new URL(url); return ['https:', 'http:'].includes(parsed.protocol) && !parsed.username && !parsed.password ? parsed.href : undefined; }
        catch { return undefined; }
      };
      const media = (Array.isArray(value.value) ? value.value : []).flatMap(item => {
        const entry = isJsonObject(item) ? item : {};
        const url = safeUrl(typeof item === 'string' ? item : entry.url);
        return url ? [{ url, thumbnail: safeUrl(entry.thumbnailUrl) || url, image: !entry.mediaType || entry.mediaType === 'image' }] : [];
      });
      return <span data-read-value-kind="feature-media" className="flex flex-wrap gap-2">{media.map((item, index) => <a key={item.url} href={item.url} target="_blank" rel="noopener noreferrer" onClick={event => event.stopPropagation()}>{item.image ? <img src={item.thumbnail} alt={String(index + 1)} loading="lazy" className="h-16 w-16 rounded object-cover" referrerPolicy="no-referrer" /> : String(index + 1)}</a>)}</span>;
    }
    return (
      <BaseCode display="block" data-read-value-kind="json">
        {JSON.stringify(value.value ?? null, null, 2)}
      </BaseCode>
    );
  }
  if (kind !== "tags" && kind !== "files" && kind !== "relations") return "";
  const items = objectArray(value.items);
  return (
    <span data-read-value-kind={kind} role="list">
      {items.map((item) => {
        const id = stringValue(item.id);
        if (!id) return null;
        const label = item.labelI18n === undefined ? stringValue(item.label) ?? id : <LocalizedDisplayText value={item.labelI18n} />;
        return (
          <BaseBadge
            key={`${kind}:${id}`}
            label={label}
            title={kind === "files" ? id : undefined}
            tone={kind === "tags" ? "primary" : "muted"}
            role="listitem"
          />
        );
      })}
    </span>
  );
};

const hasGovernedReadValue = (value: JsonValue | undefined): boolean => {
  if (value === undefined || value === null || value === "") return false;
  if (!isJsonObject(value)) return true;
  const kind = stringValue(value.kind);
  if (kind === "json") return hasGovernedReadValue(value.value);
  if (kind === "tags" || kind === "files" || kind === "relations") {
    return objectArray(value.items).length > 0;
  }
  return false;
};

const scalarValue = (
  value: JsonValue | undefined,
): string | number | boolean | null | undefined =>
  value === null ||
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean"
    ? value
    : undefined;

const governedEditorValue = (
  value: JsonValue | undefined,
): JsonValue | undefined => {
  if (scalarValue(value) !== undefined) return value;
  if (!isJsonObject(value)) return undefined;
  const kind = stringValue(value.kind);
  if (kind === "json") return value;
  if (
    (kind === "tags" || kind === "files" || kind === "relations") &&
    Array.isArray(value.items)
  )
    return value;
  return undefined;
};

type GovernedSelectionKind = "tags" | "files" | "relations";

const governedSelectionKind = (
  fieldKind: JsonValue | undefined,
): GovernedSelectionKind | undefined => {
  if (fieldKind === "tags") return "tags";
  if (fieldKind === "files" || fieldKind === "media") return "files";
  if (fieldKind === "relation") return "relations";
  return undefined;
};

const governedSelectionItems = (
  value: JsonValue | undefined,
  kind: GovernedSelectionKind,
): JsonObject[] =>
  isJsonObject(value) && value.kind === kind ? objectArray(value.items) : [];

const optionIdentity = (item: JsonObject): string | undefined =>
  stringValue(item.id);

const mergeGovernedOptions = (
  current: JsonObject[],
  options: JsonObject[],
): JsonObject[] => {
  const merged = new Map<string, JsonObject>();
  for (const option of [...current, ...options]) {
    const id = optionIdentity(option);
    if (id) merged.set(id, option);
  }
  return [...merged.values()];
};

const editorSelectionValue = (
  kind: GovernedSelectionKind,
  ids: string[],
  options: JsonObject[],
): JsonObject => {
  const optionsById = new Map(
    options.flatMap((option) => {
      const id = optionIdentity(option);
      return id ? [[id, option] as const] : [];
    }),
  );
  return {
    kind,
    items: ids.flatMap((id) => {
      const option = optionsById.get(id);
      if (!option) return [];
      const label = stringValue(option.label) ?? id;
      return [
        {
          id,
          label,
          ...(kind === "tags" && stringValue(option.color)
            ? { color: String(option.color) }
            : {}),
          ...(kind === "files" && stringValue(option.mediaType)
            ? { mediaType: String(option.mediaType) }
            : {}),
          ...(kind === "relations" && stringValue(option.ontologyId)
            ? { ontologyId: String(option.ontologyId) }
            : {}),
        },
      ];
    }),
  };
};

type CollectionPaginationModel = {
  mode: "append" | "window";
  hasMore: boolean;
  nextCursor?: string | null;
  loadingMore: boolean;
  errorCode?: string;
  pageSize?: number;
  returned?: number;
  total?: number;
  loadedCount?: number;
  loadedStart?: number;
  loadedEnd?: number;
  currentPage?: number;
  totalPages?: number;
  hasPrevious?: boolean;
  hasNext?: boolean;
  loading?: boolean;
};

const cursorPagination = (
  value: JsonValue | undefined,
): CollectionPaginationModel | undefined => {
  if (!isJsonObject(value)) return undefined;
  const mode = value.mode === "window" ? "window" : "append";
  const hasMore = value.hasMore === true;
  const nextCursor = stringValue(value.nextCursor);
  const loadingMore = value.loadingMore === true;
  const errorCode = stringValue(value.errorCode);
  const safeCount = (candidate: JsonValue | undefined) =>
    typeof candidate === "number" &&
    Number.isSafeInteger(candidate) &&
    candidate >= 0
      ? candidate
      : undefined;
  return {
    mode,
    hasMore,
    nextCursor,
    loadingMore,
    errorCode,
    pageSize: safeCount(value.pageSize),
    returned: safeCount(value.returned),
    total: safeCount(value.total),
    loadedCount: safeCount(value.loadedCount),
    loadedStart: safeCount(value.loadedStart),
    loadedEnd: safeCount(value.loadedEnd),
    currentPage: safeCount(value.currentPage),
    totalPages: safeCount(value.totalPages),
    hasPrevious: value.hasPrevious === true,
    hasNext: value.hasNext === true,
    loading: value.loading === true,
  };
};

const CursorLoadMore = ({
  pagination,
  label,
  loadingLabel,
  onLoadMore,
}: {
  pagination: ReturnType<typeof cursorPagination>;
  label: ReactNode;
  loadingLabel: ReactNode;
  onLoadMore: (cursor: string) => void;
}) => {
  if (!pagination?.hasMore || !pagination.nextCursor) return null;
  return (
    <div role="status" aria-live="polite">
      <DataExplorerButton
        label={pagination.loadingMore ? loadingLabel : label}
        loading={pagination.loadingMore}
        onClick={() => onLoadMore(pagination.nextCursor!)}
      />
    </div>
  );
};

const CursorInfiniteScroll = ({
  pagination,
  label,
  loadingLabel,
  onLoadMore,
}: {
  pagination: ReturnType<typeof cursorPagination>;
  label: ReactNode;
  loadingLabel: ReactNode;
  onLoadMore: (cursor: string) => void;
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const requestedCursorRef = useRef<string>();
  const cursor = pagination?.nextCursor;

  useEffect(() => {
    if (!pagination?.loadingMore && requestedCursorRef.current !== cursor) {
      requestedCursorRef.current = undefined;
    }
  }, [cursor, pagination?.loadingMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !pagination?.hasMore || !cursor || pagination.loadingMore)
      return;
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        if (requestedCursorRef.current === cursor) return;
        requestedCursorRef.current = cursor;
        onLoadMore(cursor);
      },
      { rootMargin: "240px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [cursor, onLoadMore, pagination?.hasMore, pagination?.loadingMore]);

  if (!pagination?.hasMore || !cursor) return null;
  return (
    <div
      ref={sentinelRef}
      role="status"
      aria-live="polite"
      aria-busy={pagination.loadingMore}
      aria-label={pagination.loadingMore ? String(loadingLabel) : String(label)}
      className="flex min-h-4 items-center justify-center py-1 text-xs text-muted-foreground"
    >
      {pagination.loadingMore ? loadingLabel : null}
    </div>
  );
};

const CollectionPaginationFooter = ({
  pagination,
  selectedCount,
  loadMoreLabel,
  loadingMoreLabel,
  onLoadMore,
  onPageChange,
  onPageSizeChange,
  legacyDataExplorer = false,
  loadMoreInContent = false,
  translate,
}: {
  pagination: ReturnType<typeof cursorPagination>;
  selectedCount: number;
  loadMoreLabel: ReactNode;
  loadingMoreLabel: ReactNode;
  onLoadMore: (cursor: string) => void;
  onPageChange?: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  legacyDataExplorer?: boolean;
  loadMoreInContent?: boolean;
  translate?: CapabilityTranslate;
}) => {
  if (!pagination) return null;
  const loadedEnd =
    pagination.loadedEnd ?? pagination.loadedCount ?? pagination.returned ?? 0;
  const loadedStart = pagination.loadedStart ?? (loadedEnd > 0 ? 1 : 0);
  const total = pagination.total ?? loadedEnd;
  const pageSize = pagination.pageSize ?? Math.max(1, loadedEnd);
  const currentPage = Math.max(1, Math.ceil(loadedEnd / Math.max(1, pageSize)));
  if (pagination.mode === "window") {
    const windowPage = pagination.currentPage ?? 1;
    const loading = pagination.loading === true;
    return (
      <BaseListFooter
        total={total}
        loadedStart={loadedStart}
        loadedEnd={loadedEnd}
        selectedCount={selectedCount}
        page={windowPage}
        pageCount={pagination.totalPages}
        pageSize={pageSize}
        pageSizeOptions={[20, 50, 100, 200, 500, 1000]}
        paginationMode="compact"
        hasPrevious={!loading && pagination.hasPrevious === true}
        hasNext={!loading && pagination.hasNext === true}
        loading={loading}
        onPageChange={(page) => {
          if (!loading) onPageChange?.(page);
        }}
        onPageSizeChange={onPageSizeChange}
      />
    );
  }
  return (
    <>
      {!loadMoreInContent && <CursorInfiniteScroll
        pagination={pagination}
        label={loadMoreLabel}
        loadingLabel={loadingMoreLabel}
        onLoadMore={onLoadMore}
      />}
      <DataExplorerCollectionFooter
        selectedCount={selectedCount}
        selectedLabel={
          legacyDataExplorer && translate
            ? translate("components.data.card-view.selected-count", {
                count: selectedCount,
              })
            : undefined
        }
        loadedStart={loadedStart}
        loadedEnd={loadedEnd}
        total={Math.max(total, loadedEnd)}
        loadedLabel={
          legacyDataExplorer && translate
            ? translate(
                loadedStart > 0
                  ? "components.data.card-view.loaded-window"
                  : "components.data.card-view.loaded-count",
                loadedStart > 0
                  ? {
                      start: loadedStart,
                      end: loadedEnd,
                      total: Math.max(total, loadedEnd),
                    }
                  : { loaded: loadedEnd, total: Math.max(total, loadedEnd) },
              )
            : undefined
        }
        currentPage={currentPage}
        pageSize={pageSize}
        pageSizeOptions={[20, 50, 100, 200, 500, 1000]}
        loading={pagination.loadingMore}
        tone="hard-edge"
        classNames={legacyDataExplorer ? { summary: "min-w-max" } : undefined}
        onJumpToPage={(targetPage) => {
          const cursor = stringValue(pagination.nextCursor);
          if (targetPage === currentPage + 1 && pagination.hasMore && cursor)
            onLoadMore(cursor);
        }}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
};

const galleryPagination = (
  value: JsonValue | undefined,
): CollectionPaginationModel | undefined => {
  if (!isJsonObject(value)) return undefined;
  const hasMore = value.hasMore;
  const nextCursor = value.nextCursor;
  const loadingMore = value.loadingMore;
  const errorCode = stringValue(value.errorCode);
  const safeCount = (candidate: JsonValue | undefined) =>
    typeof candidate === "number" &&
    Number.isSafeInteger(candidate) &&
    candidate >= 0
      ? candidate
      : undefined;
  if (
    typeof hasMore !== "boolean" ||
    typeof loadingMore !== "boolean" ||
    (hasMore && !stringValue(nextCursor)) ||
    (!hasMore && nextCursor !== null && nextCursor !== undefined) ||
    (!hasMore && loadingMore) ||
    (loadingMore && errorCode !== undefined)
  )
    return undefined;
  return {
    mode: "append",
    hasMore,
    nextCursor: hasMore ? String(nextCursor) : null,
    loadingMore,
    ...(errorCode ? { errorCode } : {}),
    ...(safeCount(value.pageSize) !== undefined
      ? { pageSize: safeCount(value.pageSize) }
      : {}),
    ...(safeCount(value.returned) !== undefined
      ? { returned: safeCount(value.returned) }
      : {}),
    ...(safeCount(value.total) !== undefined
      ? { total: safeCount(value.total) }
      : {}),
    ...(safeCount(value.loadedCount) !== undefined
      ? { loadedCount: safeCount(value.loadedCount) }
      : {}),
    ...(safeCount(value.loadedStart) !== undefined
      ? { loadedStart: safeCount(value.loadedStart) }
      : {}),
    ...(safeCount(value.loadedEnd) !== undefined
      ? { loadedEnd: safeCount(value.loadedEnd) }
      : {}),
  };
};

const ShellLayoutCapabilityView = ({
  model,
  children,
  childNodes,
  childrenBySlot,
  translate,
  emitIntent,
}: Parameters<DesignCapabilityRenderer>[0]) => {
  const clampSidebarWidth = (width: number) =>
    Math.min(600, Math.max(200, Math.round(width)));
  const clampAsideWidth = (width: number) =>
    Math.min(560, Math.max(320, Math.round(width)));
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const configuredWidth =
      typeof model.sidebarWidth === "number" ? model.sidebarWidth : 256;
    return clampSidebarWidth(configuredWidth);
  });
  const sidebarResizeRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    currentWidth: number;
  } | null>(null);
  const [sidebarResizing, setSidebarResizing] = useState(false);
  const [asideWidth, setAsideWidth] = useState(() =>
    clampAsideWidth(
      typeof model.asideWidth === "number" ? model.asideWidth : 400,
    ),
  );
  const asideResizeRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    currentWidth: number;
  } | null>(null);
  const sidebarCollapsed = model.sidebarCollapsed === true;
  const sidebarCollapsible = model.sidebarCollapsible !== false;
  const sidebarResizable = model.sidebarResizable !== false;
  const asideResizable = model.asideResizable !== false;
  const [narrow, setNarrow] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  useEffect(() => {
    if (model.mobileSidebar !== 'drawer') return;
    const query = window.matchMedia('(max-width: 767px)');
    const update = () => {
      setNarrow(query.matches);
      if (!query.matches) setMobileSidebarOpen(false);
    };
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, [model.mobileSidebar]);
  useEffect(() => {
    if (typeof model.sidebarWidth === "number") {
      setSidebarWidth(clampSidebarWidth(model.sidebarWidth));
    }
  }, [model.sidebarWidth]);
  useEffect(() => {
    if (typeof model.asideWidth === "number") {
      setAsideWidth(clampAsideWidth(model.asideWidth));
    }
  }, [model.asideWidth]);
  const sidebarLabel = stringValue(model.sidebarLabelKey)
    ? translate(String(model.sidebarLabelKey))
    : undefined;
  const asideLabel = stringValue(model.asideLabelKey)
    ? translate(String(model.asideLabelKey))
    : undefined;
  const main = childrenBySlot.main ?? (childNodes?.length
    ? Object.entries(childrenBySlot).filter(([slot]) => !['header', 'sidebar', 'aside', 'footer', 'notices', 'overlay'].includes(slot)).map(([, content]) => content)
    : children);
  const drawerSidebar = model.mobileSidebar === 'drawer' && narrow && childrenBySlot.sidebar;
  const overlay = childrenBySlot.overlay ? (
    <div
      className="pointer-events-none fixed inset-0 z-50 [&>*]:pointer-events-auto"
      data-monkeys-shell-overlay
    >
      {childrenBySlot.overlay}
    </div>
  ) : null;

  if (model.variant === "region-stack") {
    return <>{children}</>;
  }

  if (model.variant === "data-explorer") {
    const appearance = resolveDataExplorerAppearance(model.appearance);
    const resizeSidebar = (nextWidth: number) => {
      const width = clampSidebarWidth(nextWidth);
      setSidebarWidth(width);
      if (sidebarResizeRef.current)
        sidebarResizeRef.current.currentWidth = width;
      return width;
    };
    const resizeAside = (nextWidth: number) => {
      const width = clampAsideWidth(nextWidth);
      setAsideWidth(width);
      if (asideResizeRef.current) asideResizeRef.current.currentWidth = width;
      return width;
    };
    const content = (
      <DataExplorerViewShell footer={childrenBySlot.footer}>
        {main}
      </DataExplorerViewShell>
    );
    return (
      <>
        <DataExplorerPage
          appearance={appearance}
          tree={
            childrenBySlot.sidebar ? (
              <div
                className="relative h-full min-h-0"
                style={{
                  width: sidebarCollapsed
                    ? 0
                    : `min(${sidebarWidth}px, max(200px, calc(100vw - 480px)))`,
                  transitionProperty: "width",
                  transitionDuration: sidebarResizing ? "0ms" : "280ms",
                  transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
                }}
                data-sidebar-collapsed={sidebarCollapsed ? "true" : "false"}
              >
                {sidebarCollapsed ? null : (
                  <DataExplorerTreeShell title={sidebarLabel}>
                    {childrenBySlot.sidebar}
                  </DataExplorerTreeShell>
                )}
                {!sidebarCollapsed ? (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute bottom-14 right-0 top-14 z-40 w-px bg-border/80"
                  />
                ) : null}
                {sidebarCollapsible ? (
                  <div className="absolute -right-3 top-1/2 z-30 -translate-y-1/2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      title={translate(
                        sidebarCollapsed
                          ? DESIGN_CAPABILITY_I18N_KEYS.shellLayout
                              .expandSidebar
                          : DESIGN_CAPABILITY_I18N_KEYS.shellLayout
                              .collapseSidebar,
                      )}
                      aria-label={translate(
                        sidebarCollapsed
                          ? DESIGN_CAPABILITY_I18N_KEYS.shellLayout
                              .expandSidebar
                          : DESIGN_CAPABILITY_I18N_KEYS.shellLayout
                              .collapseSidebar,
                      )}
                      className="h-10 w-6 rounded-md bg-background shadow-md transition-[box-shadow,background-color] duration-200"
                      onClick={() =>
                        emitIntent("capability.shell-layout.sidebar-collapse", {
                          collapsed: !sidebarCollapsed,
                        })
                      }
                    >
                      {sidebarCollapsed ? (
                        <ChevronRight className="h-4 w-4" />
                      ) : (
                        <ChevronLeft className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ) : null}
                {!sidebarCollapsed && sidebarResizable ? (
                  <button
                    type="button"
                    aria-label={translate(
                      DESIGN_CAPABILITY_I18N_KEYS.shellLayout.resizeSidebar,
                    )}
                    data-testid="data-sidebar-resize-handle"
                    className="group/resize absolute -right-2 top-0 z-40 h-full w-4 cursor-col-resize touch-none select-none bg-transparent outline-none opacity-100 focus-visible:ring-2 focus-visible:ring-primary/35"
                    onPointerDown={(event) => {
                      setSidebarResizing(true);
                      sidebarResizeRef.current = {
                        pointerId: event.pointerId,
                        startX: event.clientX,
                        startWidth: sidebarWidth,
                        currentWidth: sidebarWidth,
                      };
                      event.currentTarget.setPointerCapture(event.pointerId);
                    }}
                    onPointerMove={(event) => {
                      const resize = sidebarResizeRef.current;
                      if (!resize || resize.pointerId !== event.pointerId)
                        return;
                      resizeSidebar(
                        resize.startWidth + event.clientX - resize.startX,
                      );
                    }}
                    onPointerUp={(event) => {
                      if (
                        sidebarResizeRef.current?.pointerId === event.pointerId
                      ) {
                        emitIntent("capability.shell-layout.sidebar-resize", {
                          width: sidebarResizeRef.current.currentWidth,
                        });
                        sidebarResizeRef.current = null;
                        setSidebarResizing(false);
                        event.currentTarget.releasePointerCapture(
                          event.pointerId,
                        );
                      }
                    }}
                    onPointerCancel={() => {
                      sidebarResizeRef.current = null;
                      setSidebarResizing(false);
                    }}
                  >
                    <span className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-primary/0 transition-colors group-hover/resize:bg-primary/30 group-active/resize:bg-primary/50" />
                  </button>
                ) : null}
              </div>
            ) : undefined
          }
          controls={childrenBySlot.header}
          notices={childrenBySlot.notices}
          content={
            childrenBySlot.aside ? (
              <div
                className="flex min-h-0 min-w-0 flex-1 overflow-hidden"
                data-data-explorer-content-with-detail="true"
              >
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  {content}
                </div>
                <div
                  className="monkeys-data-explorer-detail-slot relative min-h-0 shrink-0 border-l border-border/55 dark:border-border/45"
                  style={{ width: `${asideWidth}px` }}
                >
                  {asideResizable ? (
                    <button
                      type="button"
                      aria-label={translate(
                        DESIGN_CAPABILITY_I18N_KEYS.shellLayout.resizeAside,
                      )}
                      className="group/resize absolute inset-y-0 left-0 z-20 w-3 -translate-x-1/2 cursor-col-resize touch-none bg-transparent outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
                      onPointerDown={(event) => {
                        asideResizeRef.current = {
                          pointerId: event.pointerId,
                          startX: event.clientX,
                          startWidth: asideWidth,
                          currentWidth: asideWidth,
                        };
                        event.currentTarget.setPointerCapture(event.pointerId);
                      }}
                      onPointerMove={(event) => {
                        const resize = asideResizeRef.current;
                        if (!resize || resize.pointerId !== event.pointerId)
                          return;
                        resizeAside(
                          resize.startWidth - event.clientX + resize.startX,
                        );
                      }}
                      onPointerUp={(event) => {
                        if (
                          asideResizeRef.current?.pointerId === event.pointerId
                        ) {
                          emitIntent("capability.shell-layout.aside-resize", {
                            width: asideResizeRef.current.currentWidth,
                          });
                          asideResizeRef.current = null;
                          event.currentTarget.releasePointerCapture(
                            event.pointerId,
                          );
                        }
                      }}
                      onPointerCancel={() => {
                        asideResizeRef.current = null;
                      }}
                    >
                      <span className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-primary/0 transition-colors group-hover/resize:bg-primary/45" />
                    </button>
                  ) : null}
                  <aside
                    aria-label={asideLabel}
                    className="h-full min-h-0 w-full overflow-auto"
                  >
                    {childrenBySlot.aside}
                  </aside>
                </div>
              </div>
            ) : (
              content
            )
          }
          className={model.fullHeight === true ? "h-full" : undefined}
          classNames={{
            sidebar: "overflow-visible",
            main: "min-w-0",
            content: "min-w-0",
          }}
        />
        {overlay}
      </>
    );
  }

  return (
    <>
      <BaseLayout
        header={drawerSidebar ? <>
          <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
            <SheetTrigger asChild><BaseButton label={sidebarLabel || translate(DESIGN_CAPABILITY_I18N_KEYS.shellLayout.expandSidebar)} /></SheetTrigger>
            <SheetContent side="left" className="flex w-[min(22rem,calc(100vw-1.5rem))] flex-col p-4" aria-describedby={undefined}>
              <SheetTitle>{sidebarLabel || translate(DESIGN_CAPABILITY_I18N_KEYS.shellLayout.expandSidebar)}</SheetTitle>
              {childrenBySlot.sidebar}
            </SheetContent>
          </Sheet>
          {childrenBySlot.header}
        </> : childrenBySlot.header}
        sidebar={drawerSidebar ? undefined : childrenBySlot.sidebar}
        aside={childrenBySlot.aside}
        footer={childrenBySlot.footer}
        sidebarLabel={sidebarLabel}
        asideLabel={asideLabel}
        sidebarSize={typeof model.sidebarWidth === 'number' ? model.sidebarWidth : undefined}
        asideSize={typeof model.asideWidth === 'number' ? model.asideWidth : undefined}
        gap="var(--global-spacing)"
        fullHeight={model.fullHeight === true}
        classNames={{
          body: 'max-md:!grid-cols-1 max-md:overflow-y-auto',
          sidebar: 'max-md:min-h-40 max-md:max-h-80',
          main: 'max-md:min-h-80',
          aside: 'max-md:min-h-40',
        }}
      >
        {childrenBySlot.notices}
        {main}
      </BaseLayout>
      {overlay}
    </>
  );
};

export const ShellLayoutCapabilityRenderer: DesignCapabilityRenderer = (
  props,
) => <ShellLayoutCapabilityView {...props} />;

export const SidebarFilterCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  childrenBySlot,
  translate,
  emitIntent,
}) => {
  const search = isJsonObject(model.search) ? model.search : undefined;
  const groups = objectArray(model.groups);
  const title = copyValue(
    model.title,
    model.titleKey,
    translate,
    DESIGN_CAPABILITY_I18N_KEYS.sidebarFilter.title,
  );

  return (
    <BaseLayoutPane as="section" scrollable>
      <BaseStack gap="var(--global-spacing)">
        {childrenBySlot.header}
        <BaseHeading level={2} size="sm">
          {title}
        </BaseHeading>
        {search ? (
          <BaseInput
            type="search"
            value={stringValue(search.value) ?? ""}
            aria-label={translate(
              stringValue(search.labelKey) ??
                DESIGN_CAPABILITY_I18N_KEYS.sidebarFilter.searchLabel,
            )}
            placeholder={translate(
              stringValue(search.placeholderKey) ??
                DESIGN_CAPABILITY_I18N_KEYS.sidebarFilter.searchPlaceholder,
            )}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              emitIntent("capability.sidebar-filter.search", {
                parameterName: stringValue(search.parameterName) ?? "",
                value: event.currentTarget.value,
              })
            }
          />
        ) : null}
        {groups.map((group) => {
          const groupId = stringValue(group.id);
          if (!groupId) return null;
          return (
            <BaseFieldset
              key={groupId}
              legend={copyValue(
                group.label,
                group.labelKey,
                translate,
                DESIGN_CAPABILITY_I18N_KEYS.sidebarFilter.groupLabel,
              )}
            >
              {objectArray(group.options).map((option) => {
                const optionId = stringValue(option.id);
                if (!optionId) return null;
                const selected = option.selected === true;
                return (
                  <BaseCheckbox
                    key={optionId}
                    checked={selected}
                    disabled={option.disabled === true}
                    label={copyValue(
                      option.label,
                      option.labelKey,
                      translate,
                      DESIGN_CAPABILITY_I18N_KEYS.sidebarFilter.optionLabel,
                    )}
                    onChange={(event) =>
                      emitIntent("capability.sidebar-filter.toggle", {
                        parameterName: stringValue(group.parameterName) ?? "",
                        groupId,
                        optionId,
                        selected: event.currentTarget.checked,
                      })
                    }
                  />
                );
              })}
            </BaseFieldset>
          );
        })}
        {childrenBySlot.footer}
      </BaseStack>
    </BaseLayoutPane>
  );
};

export const GalleryCapabilityRenderer: DesignCapabilityRenderer = ({
  node,
  model,
  childrenBySlot,
  translate,
  emitIntent,
  resolveMedia = () => undefined,
  loading,
  pendingActionInvocations,
}) => {
  const items = objectArray(model.items);
  const selected = items
    .filter((item) => item.selected === true)
    .flatMap((item) => (stringValue(item.id) ? [String(item.id)] : []));
  const pagination = galleryPagination(model.pagination);
  if (items.length === 0 && pagination?.hasMore !== true) {
    return <CapabilityStatus kind="empty" translate={translate} />;
  }
  const requestNextPage = () => {
    if (!pagination?.hasMore || pagination.loadingMore) return;
    const cursor = stringValue(pagination.nextCursor);
    if (!cursor) return;
    const payload: GalleryLoadMoreIntentPayload = { cursor };
    emitIntent("capability.gallery.load-more", payload);
  };
  const paginationControl = pagination?.errorCode ? (
    <CapabilityStatus
      kind="error"
      code={pagination.errorCode}
      onRetry={requestNextPage}
      translate={translate}
    />
  ) : pagination ? (
    <CollectionPaginationFooter
      pagination={pagination}
      selectedCount={selected.length}
      loadMoreLabel={translate(DESIGN_CAPABILITY_I18N_KEYS.gallery.loadMore)}
      loadingMoreLabel={translate(
        DESIGN_CAPABILITY_I18N_KEYS.gallery.loadingMore,
      )}
      onLoadMore={(cursor) =>
        emitIntent("capability.gallery.load-more", { cursor })
      }
      onPageSizeChange={(pageSize) =>
        emitIntent("capability.gallery.page-size-change", { pageSize })
      }
    />
  ) : null;
  if (model.galleryFields !== undefined || model.gallerySettings !== undefined) return <GalleryFieldPresentation renderActionIcon={actionIcon} kind="gallery" model={model} translate={translate} emitIntent={emitIntent} resolveMedia={resolveMedia} loading={loading} pendingActionInvocations={pendingActionInvocations} header={childrenBySlot.header} footer={<>{paginationControl}{childrenBySlot.footer}</>} />;
  const displayItems = items.flatMap((item) => {
    const itemId = stringValue(item.id);
    if (!itemId) return [];
    const media = isJsonObject(item.media) ? item.media : undefined;
    const mediaReference =
      media && isJsonObject(media.reference) ? media.reference : undefined;
    const mediaId = mediaReference ? stringValue(mediaReference.id) : undefined;
    const mediaAlt = media ? stringValue(media.alt) : undefined;
    const mediaAspectRatio = media ? stringValue(media.aspectRatio) : undefined;
    const resolvedMedia = mediaId ? resolveMedia({ id: mediaId }) : undefined;
    const mediaNode =
      media && mediaId && mediaAlt ? (
        <MediaPreviewFrame
          aspectRatio={
            (mediaAspectRatio as MediaPreviewRatio | undefined) ?? "1:1"
          }
          data-media-placeholder={stringValue(media.placeholder) ?? "neutral"}
        >
          {resolvedMedia ? (
            <MediaPreviewImage
              src={resolvedMedia.src}
              alt={mediaAlt}
              fit="cover"
            />
          ) : (
            <div
              aria-hidden="true"
              className="size-full bg-muted"
              data-media-unresolved={mediaId}
            />
          )}
        </MediaPreviewFrame>
      ) : undefined;
    return [
      {
        id: itemId,
        title: copyValue(
          item.title,
          item.titleKey,
          translate,
          DESIGN_CAPABILITY_I18N_KEYS.gallery.itemTitle,
        ),
        description:
          stringValue(item.description) ??
          (stringValue(item.descriptionKey)
            ? translate(String(item.descriptionKey))
            : undefined),
        selected: item.selected === true,
        disabled: item.disabled === true,
        ...(mediaId && mediaAlt
          ? {
              media: {
                id: mediaId,
                kind: "image" as const,
                alt: mediaAlt,
                node: mediaNode,
              },
            }
          : {}),
        source: item,
      } satisfies DataExplorerDisplayItem<JsonObject>,
    ];
  });

  const presentation = isJsonObject(model.presentation)
    ? model.presentation
    : undefined;
  const legacyDataExplorer = presentation?.kind === "legacy-data-explorer";
  if (legacyDataExplorer) {
    const galleryColumns: WorkbenchGalleryColumn[] = [
      {
        key: "galleryPreview",
        label: translate("components.data-v2.gallery.fields.preview"),
        renderType: "image",
      },
      {
        key: "name",
        label: translate("components.data-v2.gallery.fields.name"),
      },
      {
        key: "description",
        label: translate("components.data-v2.gallery.fields.description"),
        wrap: true,
      },
      {
        key: "keywords",
        label: translate("components.data-v2.gallery.fields.keywords"),
      },
      {
        key: "type",
        label: translate("components.data-v2.gallery.fields.type"),
      },
      {
        key: "updatedAt",
        label: translate("components.data-v2.gallery.fields.updated"),
      },
    ];
    const galleryRows = items.flatMap((item) => {
      const id = stringValue(item.id);
      if (!id) return [];
      const metadata = new Map(
        objectArray(item.metadata).flatMap((entry) => {
          const key = stringValue(entry.id);
          return key ? [[key, entry.value] as const] : [];
        }),
      );
      const media = isJsonObject(item.media) ? item.media : undefined;
      const reference =
        media && isJsonObject(media.reference) ? media.reference : undefined;
      const resolved =
        reference && stringValue(reference.id)
          ? resolveMedia({ id: String(reference.id) })
          : undefined;
      return [
        {
          id,
          name: stringValue(item.title) ?? id,
          description: stringValue(item.description) ?? "",
          galleryPreview: resolved?.src ?? "",
          // The legacy gallery intentionally renders an absent keyword list as
          // blank while retaining the dash placeholder for other absent scalar
          // fields such as type and preview.
          keywords: metadata.get("keywords") ?? [],
          type: metadata.get("type") ?? null,
          updatedAt: metadata.get("updatedAt") ?? null,
          selected: item.selected === true,
        },
      ];
    });
    return (
      <DataExplorerViewShell
        className="col-span-full h-full"
        header={childrenBySlot.header}
        footer={
          <>
            <CollectionPaginationFooter
              pagination={pagination}
              selectedCount={selected.length}
              legacyDataExplorer
              loadMoreInContent
              translate={translate}
              loadMoreLabel={translate(
                DESIGN_CAPABILITY_I18N_KEYS.gallery.loadMore,
              )}
              loadingMoreLabel={translate(
                DESIGN_CAPABILITY_I18N_KEYS.gallery.loadingMore,
              )}
              onLoadMore={(cursor) =>
                emitIntent("capability.gallery.load-more", { cursor })
              }
              onPageSizeChange={(pageSize) =>
                emitIntent("capability.gallery.page-size-change", { pageSize })
              }
            />
            {childrenBySlot.footer}
          </>
        }
      >
        <div className="min-h-0 flex-1 overflow-auto p-4">
          <WorkbenchGalleryView
            rows={galleryRows}
            columns={galleryColumns}
            selectedRowKey={selected.length === 1 ? selected[0] : undefined}
            onRowClick={(item: Record<string, JsonValue>) =>
              emitIntent("capability.gallery.activate", {
                itemId: String(item.id),
              })
            }
            galleryFieldKey="galleryPreview"
            galleryDensity="comfortable"
            galleryBentoColumns={2}
            galleryDetailVisibility="limit"
            galleryDetailLimit={4}
            galleryGridColumns="auto"
            isMasonry={false}
            getFieldValue={(
              column: WorkbenchGalleryColumn,
              row: Record<string, JsonValue>,
            ) => row[column.key]}
            getDisplayText={(value: unknown) => {
              if (value === null || value === undefined || value === "")
                return "-";
              if (Array.isArray(value)) return value.map(String).join(", ");
              return String(value);
            }}
            renderFieldValue={(
              column: WorkbenchGalleryColumn,
              row: Record<string, JsonValue>,
            ) => {
              const raw = row[column.key];
              const value =
                column.key === "updatedAt"
                  ? formatCollectionTimestamp(raw)
                  : raw === null || raw === undefined || raw === ""
                    ? "-"
                    : Array.isArray(raw)
                      ? raw.map(String).join(", ")
                      : String(raw);
              return (
                <span
                  className="block min-w-0 max-w-full truncate"
                  title={String(value)}
                >
                  {value}
                </span>
              );
            }}
            isMediaColumn={(
              column: WorkbenchGalleryColumn,
              row: Record<string, JsonValue>,
            ) =>
              column.key === "galleryPreview" &&
              typeof row.galleryPreview === "string" &&
              row.galleryPreview.length > 0
            }
            isImageField={(column: WorkbenchGalleryColumn, value: unknown) =>
              column.key === "galleryPreview" &&
              typeof value === "string" &&
              value.length > 0
            }
            getImagePreviewSrc={(value: unknown) =>
              typeof value === "string" ? value : ""
            }
            isTitleCandidate={(column: WorkbenchGalleryColumn) =>
              column.key === "name"
            }
            isSummaryCandidate={(column: WorkbenchGalleryColumn) =>
              column.key === "description"
            }
            className="items-start"
          />
          {pagination?.mode !== 'window' && pagination?.hasMore ? <CursorInfiniteScroll pagination={pagination} label={translate(DESIGN_CAPABILITY_I18N_KEYS.gallery.loadMore)} loadingLabel={translate(DESIGN_CAPABILITY_I18N_KEYS.gallery.loadingMore)} onLoadMore={cursor => emitIntent('capability.gallery.load-more', { cursor })} /> : null}
          {pagination?.hasMore === false && galleryRows.length > 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {translate("components.data.card-view.all-loaded", {
                total:
                  pagination.total ??
                  pagination.loadedEnd ??
                  pagination.returned ??
                  galleryRows.length,
              })}
            </div>
          ) : null}
        </div>
      </DataExplorerViewShell>
    );
  }

  return (
    <DataExplorerViewShell
      className="col-span-full"
      footer={
        <>
          {paginationControl}
          {childrenBySlot.footer}
        </>
      }
    >
      {childrenBySlot.header}
      <DataExplorerViewCollection
        items={displayItems}
        activeViewId="gallery"
        layout="grid"
        selectedIds={selected}
        getItemId={(item) => item.id}
        classNames={{ items: "p-4" }}
        itemsStyle={{
          gridTemplateColumns: `repeat(${node.layout.columns ?? 1}, minmax(0, 1fr))`,
        }}
        onOpenItem={(item) =>
          emitIntent("capability.gallery.activate", { itemId: item.id })
        }
        onSelectionChange={(itemIds) =>
          emitIntent("capability.gallery.selection-change", { itemIds })
        }
        renderItem={({
          item,
          selected: itemSelected,
          onOpen,
          onSelectionChange,
        }) => (
          <DataExplorerRecordCard
            item={item}
            selected={itemSelected}
            disabled={item.disabled}
            media={item.media?.node}
            actions={
              <DataExplorerButton
                label={translate(
                  stringValue(item.source.actionLabelKey) ??
                    DESIGN_CAPABILITY_I18N_KEYS.gallery.itemActivate,
                )}
                disabled={item.disabled}
                stopPropagation
                onClick={() =>
                  emitIntent("capability.gallery.activate", { itemId: item.id })
                }
              />
            }
            footer={
              <BaseDescriptionList
                items={objectArray(item.source.metadata).flatMap((entry) => {
                  const entryId = stringValue(entry.id);
                  return entryId
                    ? [
                        {
                          term: copyValueOrFallback(
                            entry.label,
                            entry.labelKey,
                            translate,
                            entryId,
                          ),
                          value: governedReadValue(entry.value),
                        },
                      ]
                    : [];
                })}
              />
            }
            selectLabel={translate(DESIGN_CAPABILITY_I18N_KEYS.cardGrid.select)}
            unselectLabel={translate(
              DESIGN_CAPABILITY_I18N_KEYS.cardGrid.select,
            )}
            onClick={() => onOpen?.(item)}
            onToggleSelection={() =>
              onSelectionChange?.(item, itemSelected !== true)
            }
          />
        )}
      />
    </DataExplorerViewShell>
  );
};

interface OntologyNavigatorTreeNode {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  meta?: ReactNode;
  disabled?: boolean;
  selectable: boolean;
  collapsible: boolean;
  reorderable?: boolean;
  source: JsonObject;
  actions?: Array<{
    id: string;
    label: ReactNode;
    icon?: ReactNode;
    tone?: "default" | "primary" | "danger" | "muted";
    disabled?: boolean;
    loading?: boolean;
  }>;
  children: OntologyNavigatorTreeNode[];
}

const treeNodes = (
  value: JsonValue | undefined,
  translate: CapabilityTranslate,
): OntologyNavigatorTreeNode[] =>
  objectArray(value).flatMap((item) => {
    const id = stringValue(item.id);
    if (!id) return [];
    const children = treeNodes(item.children, translate);
    return [
      {
        id,
        label: copyValue(
          item.label,
          item.labelKey,
          translate,
          DESIGN_CAPABILITY_I18N_KEYS.ontologyNavigator.empty,
        ),
        ...(stringValue(item.description) || stringValue(item.descriptionKey)
          ? {
              description: stringValue(item.descriptionKey)
                ? translate(String(item.descriptionKey))
                : stringValue(item.description),
            }
          : {}),
        ...(stringValue(item.meta) ? { meta: stringValue(item.meta) } : {}),
        icon: isJsonObject(item.icon) ? (
          semanticIcon(stringValue(item.icon.id))
        ) : (
          <DataKindIcon kind={stringValue(item.kind)} />
        ),
        disabled: item.disabled === true,
        selectable: item.disabled !== true,
        collapsible: children.length > 0,
        reorderable: item.reorderable === true,
        source: item,
        actions: objectArray(item.actions).flatMap((action) => {
          const actionId = stringValue(action.id);
          if (!actionId) return [];
          return [
            {
              id: actionId,
              label: copyValueOrFallback(
                action.label,
                action.labelKey,
                translate,
                actionId,
              ),
              icon: actionIcon(action),
              tone: actionTone(action),
              disabled: action.disabled === true,
              loading: action.loading === true,
            },
          ];
        }),
        children,
      },
    ];
  });

const searchableTreeNodeText = (value: ReactNode) =>
  typeof value === "string" || typeof value === "number"
    ? String(value).toLocaleLowerCase()
    : "";

const filterTreeNodes = (
  nodes: OntologyNavigatorTreeNode[],
  searchValue: string,
): OntologyNavigatorTreeNode[] => {
  const query = searchValue.trim().toLocaleLowerCase();
  if (!query) return nodes;
  return nodes.flatMap((node) => {
    const children = filterTreeNodes(node.children, query);
    const matches = [node.label, node.description, node.meta].some((value) =>
      searchableTreeNodeText(value).includes(query),
    );
    return matches || children.length
      ? [{ ...node, children, collapsible: children.length > 0 }]
      : [];
  });
};

const legacyViewTreeClassName = (
  ...parts: Array<string | false | null | undefined>
) => parts.filter(Boolean).join(" ");

interface LegacyDataSidebarViewTreeProps {
  nodes: OntologyNavigatorTreeNode[];
  selectedNodeId?: string;
  empty: ReactNode;
  translate: CapabilityTranslate;
  emitIntent: DesignCapabilityRendererContext["emitIntent"];
}

const flattenLegacyViewTree = (
  nodes: OntologyNavigatorTreeNode[],
  parentId: string | undefined = undefined,
  result = new Map<
    string,
    { node: OntologyNavigatorTreeNode; parentId?: string; index: number }
  >(),
) => {
  nodes.forEach((node, index) => {
    result.set(node.id, { node, parentId, index });
    flattenLegacyViewTree(node.children, node.id, result);
  });
  return result;
};

interface LegacyDataSidebarViewTreeNodeProps {
  node: OntologyNavigatorTreeNode;
  selectedNodeId?: string;
  expandedNodeIds: Set<string>;
  depth: number;
  translate: CapabilityTranslate;
  emitIntent: DesignCapabilityRendererContext["emitIntent"];
  onToggle: (nodeId: string) => void;
}

const LegacyDataSidebarViewTreeNode = ({
  node,
  selectedNodeId,
  expandedNodeIds,
  depth,
  translate,
  emitIntent,
  onToggle,
}: LegacyDataSidebarViewTreeNodeProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id });
  const children = node.children;
  const hasChildren = children.length > 0;
  const isExpanded = node.collapsible === false || expandedNodeIds.has(node.id);
  const isSelected = selectedNodeId === node.id;
  const childIds = children.map((child) => child.id);
  const normalizedTransform = transform
    ? { ...transform, scaleX: 1, scaleY: 1 }
    : null;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(normalizedTransform),
        transition,
        opacity: isDragging ? 0.55 : 1,
      }}
      className="min-w-0 space-y-1"
    >
      <div
        {...attributes}
        {...listeners}
        className={legacyViewTreeClassName(
          "group/tree-node bg-background/72 hover:border-primary/24 relative flex min-h-8 w-full min-w-0 cursor-grab items-center rounded-lg border border-border/55 pr-1 text-left text-[13px] font-medium transition-[background-color,border-color,box-shadow,opacity,transform] duration-200 before:absolute before:-left-2 before:top-1/2 before:h-px before:w-2 before:bg-border/70 hover:bg-background active:cursor-grabbing",
          isSelected &&
            "border-primary/26 bg-primary/[0.08] text-foreground hover:bg-primary/[0.08] dark:border-primary/10 dark:bg-primary/[0.12] dark:hover:bg-primary/[0.12]",
          isDragging && "shadow-md ring-1 ring-primary/25",
        )}
        style={{ paddingLeft: `${depth * 8 + 4}px` }}
        role="treeitem"
        aria-selected={isSelected || undefined}
        aria-expanded={hasChildren ? isExpanded : undefined}
        tabIndex={0}
        onClick={() =>
          emitIntent("capability.ontology-navigator.select", {
            nodeId: node.id,
          })
        }
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          emitIntent("capability.ontology-navigator.select", {
            nodeId: node.id,
          });
        }}
      >
        <span
          className="mr-1 flex h-6 w-3.5 shrink-0 items-center justify-center rounded-sm text-muted-foreground/70 transition-colors group-hover/tree-node:text-foreground"
          aria-label={translate("components.data-v2.sidebar.drag-to-sort")}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </span>
        {hasChildren ? (
          <button
            type="button"
            className="mr-1 inline-flex h-6 w-4 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label={translate(
              isExpanded ? "common.actions.collapse" : "common.actions.expand",
            )}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onToggle(node.id);
            }}
          >
            <ChevronDown
              className={legacyViewTreeClassName(
                "h-4 w-4 transition-transform",
                isExpanded ? "rotate-0" : "-rotate-90",
              )}
            />
          </button>
        ) : null}
        <span className="mr-1 inline-flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground group-hover/tree-node:text-foreground">
          <Database className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1 truncate">{node.label}</span>
        {node.actions?.length ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={legacyViewTreeClassName(
                  "ml-1 h-7 w-7 shrink-0 transition-opacity data-[state=open]:opacity-100",
                  isSelected
                    ? "opacity-100"
                    : "opacity-0 group-hover/tree-node:opacity-100",
                )}
                aria-label={translate("common.actions.more-actions")}
                title={translate("common.actions.more-actions")}
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {node.actions.map((action) => (
                <DropdownMenuItem
                  key={action.id}
                  disabled={action.disabled === true || action.loading === true}
                  className={legacyViewTreeClassName(
                    action.tone === "danger" &&
                      "text-destructive-text focus:text-destructive-text",
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (action.disabled === true || action.loading === true)
                      return;
                    emitIntent("capability.ontology-navigator.action", {
                      actionId: action.id,
                      nodeId: node.id,
                    });
                  }}
                >
                  {action.icon}
                  <span>{action.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      {hasChildren ? (
        <div
          aria-hidden={!isExpanded}
          className={legacyViewTreeClassName(
            "ease-emphasized grid origin-top transition-[grid-template-rows,transform] duration-300",
            isExpanded
              ? "translate-y-0 grid-rows-[1fr]"
              : "pointer-events-none -translate-y-0.5 grid-rows-[0fr]",
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <SortableContext
              items={childIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="ml-2 space-y-1 border-l border-border/70 pl-2 pt-1">
                {children.map((child) => (
                  <LegacyDataSidebarViewTreeNode
                    key={child.id}
                    node={child}
                    selectedNodeId={selectedNodeId}
                    expandedNodeIds={expandedNodeIds}
                    depth={depth + 1}
                    translate={translate}
                    emitIntent={emitIntent}
                    onToggle={onToggle}
                  />
                ))}
              </div>
            </SortableContext>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const LegacyDataSidebarViewTree = ({
  nodes,
  selectedNodeId,
  empty,
  translate,
  emitIntent,
}: LegacyDataSidebarViewTreeProps) => {
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(
    () => new Set(),
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const flattenedNodes = useMemo(() => flattenLegacyViewTree(nodes), [nodes]);
  const rootIds = nodes.map((node) => node.id);

  useEffect(() => {
    if (!selectedNodeId) return;
    const next = new Set<string>();
    let parentId = flattenedNodes.get(selectedNodeId)?.parentId;
    while (parentId) {
      next.add(parentId);
      parentId = flattenedNodes.get(parentId)?.parentId;
    }
    if (!next.size) return;
    setExpandedNodeIds((previous) => {
      const merged = new Set(previous);
      next.forEach((id) => merged.add(id));
      return merged.size === previous.size ? previous : merged;
    });
  }, [flattenedNodes, selectedNodeId]);

  const onToggle = (nodeId: string) => {
    const next = new Set(expandedNodeIds);
    if (next.has(nodeId)) next.delete(nodeId);
    else next.add(nodeId);
    setExpandedNodeIds(next);
    emitIntent("capability.ontology-navigator.toggle", {
      expandedNodeIds: [...next],
    });
  };

  const onDragEnd = (event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id) return;
    const active = flattenedNodes.get(String(event.active.id));
    const over = flattenedNodes.get(String(event.over.id));
    if (!active || !over || active.parentId !== over.parentId) return;
    emitIntent("capability.ontology-navigator.reorder", {
      nodeId: active.node.id,
      parentNodeId: active.parentId ?? null,
      beforeNodeId: over.node.id,
    });
  };

  if (!nodes.length) {
    return (
      <div className="flex min-h-12 items-center justify-center px-3 py-3 text-center text-xs text-muted-foreground">
        {empty}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={rootIds} strategy={verticalListSortingStrategy}>
        <div className="space-y-1">
          {nodes.map((node) => (
            <LegacyDataSidebarViewTreeNode
              key={node.id}
              node={node}
              selectedNodeId={selectedNodeId}
              expandedNodeIds={expandedNodeIds}
              depth={0}
              translate={translate}
              emitIntent={emitIntent}
              onToggle={onToggle}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export const OntologyNavigatorCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  binding,
  childrenBySlot,
  translate,
  emitIntent,
}) => {
  const pagination = cursorPagination(model.pagination);
  const search = isJsonObject(model.search) ? model.search : undefined;
  const searchValue = search ? stringValue(search.value) ?? "" : "";
  const visibleNodes = filterTreeNodes(
    treeNodes(model.nodes, translate),
    searchValue,
  );
  const error = isJsonObject(model.error) ? model.error : undefined;
  const retry = isJsonObject(model.retry) ? model.retry : undefined;
  const presentation = isJsonObject(model.presentation)
    ? model.presentation
    : undefined;
  const exactBinding = isJsonObject(binding as JsonValue)
    ? (binding as JsonObject)
    : undefined;
  const queryDefinitionRef =
    exactBinding && isJsonObject(exactBinding.queryDefinitionRevisionRef)
      ? exactBinding.queryDefinitionRevisionRef
      : undefined;
  const queryDefinitionId = stringValue(queryDefinitionRef?.id);
  const legacyRole =
    queryDefinitionId === "data.ontology.catalog"
      ? "ontology"
      : queryDefinitionId === "data.view.catalog"
        ? "view"
        : stringValue(presentation?.role);
  const legacyDataSidebar =
    Boolean(legacyRole) || presentation?.kind === "legacy-data-sidebar";
  const legacyNodes = (
    nodes: OntologyNavigatorTreeNode[],
  ): OntologyNavigatorTreeNode[] =>
    nodes.map((node) => ({
      ...node,
      description: undefined,
      meta: undefined,
      children: legacyNodes(node.children),
    }));
  const renderedNodes = legacyDataSidebar
    ? legacyNodes(visibleNodes)
    : visibleNodes;
  const footerVisible =
    Boolean(childrenBySlot.footer) ||
    Boolean(pagination?.hasMore && pagination.nextCursor);
  if (legacyDataSidebar && legacyRole === "view") {
    if (presentation?.kind === "domain-record-team-views") {
      return (
        <nav className="ml-8 w-max min-w-full space-y-1 border-l border-border/80 pl-2 pt-1">
          {renderedNodes.map((node) => (
            <Button
              key={node.id}
              size="sm"
              variant="ghost"
              className={`h-8 w-full !justify-start border bg-transparent px-2.5 text-left text-[13px] font-medium ${
                node.id === stringValue(model.selectedNodeId)
                  ? "border-primary/30 bg-primary/[0.08]"
                  : "border-border/70"
              }`}
              title={typeof node.label === "string" ? node.label : undefined}
              onClick={() =>
                emitIntent("capability.ontology-navigator.select", {
                  nodeId: node.id,
                })
              }
            >
              <span className="flex-1 whitespace-nowrap">{node.label}</span>
            </Button>
          ))}
        </nav>
      );
    }
    return (
      <div
        className="ml-8 border-l border-border/80 pl-2 pt-1"
        data-presentation="legacy-data-sidebar-view"
      >
        <LegacyDataSidebarViewTree
          nodes={renderedNodes}
          selectedNodeId={stringValue(model.selectedNodeId)}
          empty={translate("components.data-v2.sidebar.no-views")}
          translate={translate}
          emitIntent={emitIntent}
        />
      </div>
    );
  }
  if (legacyDataSidebar) {
    const selectedNodeId = stringValue(model.selectedNodeId);
    type SidebarNode = (typeof renderedNodes)[number];
    const selectedPath = (nodes: SidebarNode[]): string[] => {
      for (const node of nodes) {
        if (node.id === selectedNodeId) return [node.id];
        const path = selectedPath(node.children || []);
        if (path.length) return [node.id, ...path];
      }
      return [];
    };
    const expandedIds = Array.isArray(model.expandedNodeIds) ? stringArray(model.expandedNodeIds) : selectedPath(renderedNodes);
    const sidebarNodes = (nodes: SidebarNode[]): SidebarNode[] => nodes.map(node => ({ ...node, icon: node.source?.kind === 'folder' ? node.icon : <Database className="h-4 w-4" />, collapsible: true, draggable: node.source?.kind !== 'folder', children: sidebarNodes(node.children || []) }));
    const findSidebarNode = (nodes: SidebarNode[], id: string | undefined): SidebarNode | undefined => {
      for (const node of nodes) { if (node.id === id) return node; const child = findSidebarNode(node.children || [], id); if (child) return child; }
    };
    const firstOntologyId = (nodes: SidebarNode[]): string | undefined => {
      for (const node of nodes) { if (node.source?.kind !== 'folder') return node.id; const id = firstOntologyId(node.children || []); if (id) return id; }
    };
    const activeDestinationId = stringValue(presentation?.activeDestinationId);
    const activeViewId = stringValue(presentation?.activeViewId);
    const allDataActive = !activeDestinationId && !activeViewId;
    return (
      <div
        className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-muted/10"
        data-presentation="legacy-data-sidebar"
      >
        <div className="flex h-14 shrink-0 items-center px-4">
          <div className="flex w-full items-center justify-between gap-2">
            <h2 className="min-w-0 truncate text-sm font-semibold">
              {translate("components.data-v2.sidebar.data-views")}
            </h2>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs hover:bg-accent/80"
                title={translate(
                  "components.data-v2.sidebar.collapse-all-nodes",
                )}
                onClick={() =>
                  emitIntent("capability.ontology-navigator.toggle", {
                    expandedNodeIds: [],
                  })
                }
              >
                {translate("components.data-v2.sidebar.collapse-nodes")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title={translate("common.actions.refresh")}
                aria-label={translate("common.actions.refresh")}
                onClick={() =>
                  emitIntent("capability.ontology-navigator.retry", {})
                }
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title={translate("components.data-v2.sidebar.create-view")}
                aria-label={translate("components.data-v2.sidebar.create-view")}
                onClick={() => {
                  const targetOntologyId =
                    selectedNodeId ?? firstOntologyId(renderedNodes);
                  if (!targetOntologyId) return;
                  emitIntent("capability.ontology-navigator.action", {
                    nodeId: targetOntologyId,
                    actionId: "view-create",
                  });
                }}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
        <ScrollArea className="min-h-0 flex-1 px-3 pb-4 pt-0">
          <div
            className="space-y-1"
            style={{ maxWidth: "100%", overflow: "hidden" }}
          >
            <div className="relative space-y-1">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className={`w-full !justify-start ${
                  allDataActive
                    ? "bg-primary/20 font-medium text-foreground hover:bg-primary/20"
                    : "font-normal"
                }`}
                onClick={() =>
                  emitIntent("capability.ontology-navigator.home", {})
                }
              >
                <LayoutGrid className="mr-2 h-4 w-4" />
                {translate("components.data-v2.sidebar.all-data")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={
                  activeDestinationId === "dashboards" ? "secondary" : "ghost"
                }
                className="w-full !justify-start font-normal"
                onClick={() =>
                  emitIntent("capability.ontology-navigator.destination", {
                    destinationId: "dashboards",
                  })
                }
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                {translate("layout.sidebar.dashboards")}
              </Button>
              <div className="pt-1">
                <DataExplorerDraggableTree
                  nodes={sidebarNodes(renderedNodes)}
                  loading={model.loading === true}
                  selectedNodeId={selectedNodeId}
                  expandedNodeIds={expandedIds}
                  activationMode="handle"
                  indentSize={14}
                  appearance={{ density: "compact", radius: "md" }}
                  labels={{
                    dragHandle: translate(
                      "components.data-v2.sidebar.drag-to-sort",
                    ),
                    expand: translate("common.actions.expand"),
                    collapse: translate("common.actions.collapse"),
                  }}
                  classNames={{
                    root: "space-y-1 overflow-visible",
                    list: "space-y-1",
                    item: "group/tree-node",
                    itemButton:
                      "min-h-8 rounded-md border-border/70 bg-background/70 text-[13px] font-medium leading-none hover:bg-background",
                    itemSelected:
                      "border-primary/30 bg-primary/[0.08] text-foreground hover:bg-primary/[0.08]",
                    itemDragging: "opacity-80 shadow-sm",
                    itemOver: "border-primary/35 bg-primary/[0.08]",
                    dragHandle: "opacity-100",
                    actions: "ml-auto",
                  }}
                  empty={translate(
                    DESIGN_CAPABILITY_I18N_KEYS.ontologyNavigator.empty,
                  )}
                  onSelectNode={node => {
                    if (node.source?.kind === 'folder') { emitIntent('capability.ontology-navigator.toggle', { expandedNodeIds: expandedIds.includes(node.id) ? expandedIds.filter(id => id !== node.id) : [...expandedIds, node.id] }); return; }
                    emitIntent('capability.ontology-navigator.select', { nodeId: node.id });
                  }}
                  onExpandedNodeIdsChange={expandedNodeIds => {
                    emitIntent('capability.ontology-navigator.toggle', { expandedNodeIds });
                    const opened = findSidebarNode(renderedNodes, expandedNodeIds.find(id => !expandedIds.includes(id)));
                    if (opened && opened.source?.kind !== 'folder' && opened.id !== selectedNodeId) emitIntent('capability.ontology-navigator.select', { nodeId: opened.id });
                  }}
                  renderNodeChildren={({ node }) => node.id === selectedNodeId && expandedIds.includes(node.id) ? childrenBySlot.footer : null}
                  renderNodeActions={({ node }) => {
                    const actions = objectArray(node.source?.actions);
                    if (!actions.length) return null;
                    return (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 transition-opacity hover:bg-accent group-hover/tree-node:opacity-100 data-[state=open]:opacity-100"
                            aria-label={translate(
                              "common.actions.more-actions",
                            )}
                            title={translate("common.actions.more-actions")}
                            onClick={(event) => event.stopPropagation()}
                            onPointerDown={(event) => event.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {actions.flatMap((action) => {
                            const actionId = stringValue(action.id);
                            if (!actionId) return [];
                            return [
                              <DropdownMenuItem
                                key={actionId}
                                disabled={
                                  action.disabled === true ||
                                  action.loading === true
                                }
                                className={
                                  actionTone(action) === "danger"
                                    ? "text-destructive-text focus:text-destructive-text"
                                    : undefined
                                }
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (
                                    action.disabled === true ||
                                    action.loading === true
                                  )
                                    return;
                                  emitIntent(
                                    "capability.ontology-navigator.action",
                                    {
                                      nodeId: node.id,
                                      actionId,
                                    },
                                  );
                                }}
                              >
                                {actionIcon(action)}
                                <span>
                                  {copyValueOrFallback(
                                    action.label,
                                    action.labelKey,
                                    translate,
                                    actionId,
                                  )}
                                </span>
                              </DropdownMenuItem>,
                            ];
                          })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                  }}
                  onDragEnd={(payload) =>
                    emitIntent("capability.ontology-navigator.reorder", {
                      nodeId: payload.activeNode.id,
                      parentNodeId: payload.targetParentId ?? null,
                      beforeNodeId: payload.overNode.id,
                    })
                  }
                />
                <CursorInfiniteScroll
                  pagination={pagination}
                  label={translate(
                    DESIGN_CAPABILITY_I18N_KEYS.ontologyNavigator.loadMore,
                  )}
                  loadingLabel={translate(
                    DESIGN_CAPABILITY_I18N_KEYS.ontologyNavigator.loadingMore,
                  )}
                  onLoadMore={(cursor) =>
                    emitIntent("capability.ontology-navigator.load-more", {
                      cursor,
                    })
                  }
                />
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
    );
  }
  return (
    <nav
      aria-label={translate(
        stringValue(model.labelKey) ??
          DESIGN_CAPABILITY_I18N_KEYS.catalog.ontologyNavigator.label,
      )}
      className="flex h-full min-h-0 flex-col overflow-hidden"
    >
      <div className="shrink-0 space-y-2 border-b border-border/50 px-3 py-3">
        {search ? (
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <SearchIcon />
            </span>
            <BaseInput
              type="search"
              value={searchValue}
              aria-label={translate(
                stringValue(search.labelKey) ??
                  DESIGN_CAPABILITY_I18N_KEYS.ontologyNavigator.searchLabel,
              )}
              placeholder={translate(
                stringValue(search.placeholderKey) ??
                  DESIGN_CAPABILITY_I18N_KEYS.ontologyNavigator
                    .searchPlaceholder,
              )}
              disabled={search.disabled === true}
              className="h-9 pl-9"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                emitIntent("capability.ontology-navigator.search", {
                  value: event.currentTarget.value,
                })
              }
            />
          </div>
        ) : null}
        {childrenBySlot.header}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        <DataExplorerViewTree
          nodes={renderedNodes}
          loading={model.loading === true}
          error={
            error
              ? translate(
                  stringValue(error.messageKey) ??
                    DESIGN_CAPABILITY_I18N_KEYS.status.error.description,
                  {
                    code: stringValue(error.code) ?? "UNKNOWN",
                  },
                )
              : undefined
          }
          selectedNodeId={stringValue(model.selectedNodeId)}
          expandedNodeIds={stringArray(model.expandedNodeIds)}
          reorderMode="any"
          empty={translate(DESIGN_CAPABILITY_I18N_KEYS.ontologyNavigator.empty)}
          labels={{
            actions: translate(
              stringValue(model.actionsLabelKey) ??
                DESIGN_CAPABILITY_I18N_KEYS.ontologyNavigator.moreActions,
            ),
          }}
          onSelectNode={(node) =>
            emitIntent("capability.ontology-navigator.select", {
              nodeId: node.id,
            })
          }
          onExpandedNodeIdsChange={(expandedNodeIds) =>
            emitIntent("capability.ontology-navigator.toggle", {
              expandedNodeIds,
            })
          }
          onAction={({ action, node }) => {
            if (!node) return;
            emitIntent("capability.ontology-navigator.action", {
              actionId: action.id,
              nodeId: node.id,
            });
          }}
          onReorderNodes={(payload) =>
            emitIntent("capability.ontology-navigator.reorder", {
              nodeId: payload.activeNode.id,
              parentNodeId: payload.overParentId ?? null,
              beforeNodeId: payload.overNode.id,
            })
          }
        />
        {error && retry && error.retryable !== false ? (
          <div className="mt-3">
            <DataExplorerButton
              label={translate(String(retry.labelKey))}
              disabled={retry.disabled === true}
              loading={retry.loading === true}
              onClick={() =>
                emitIntent("capability.ontology-navigator.retry", {})
              }
            />
          </div>
        ) : null}
      </div>
      {footerVisible ? (
        <div className="shrink-0 border-t border-border/50 p-3">
          <BaseStack gap="var(--global-spacing-sm, 0.5rem)">
            <CursorInfiniteScroll
              pagination={pagination}
              label={translate(
                DESIGN_CAPABILITY_I18N_KEYS.ontologyNavigator.loadMore,
              )}
              loadingLabel={translate(
                DESIGN_CAPABILITY_I18N_KEYS.ontologyNavigator.loadingMore,
              )}
              onLoadMore={(cursor) =>
                emitIntent("capability.ontology-navigator.load-more", {
                  cursor,
                })
              }
            />
            {childrenBySlot.footer}
          </BaseStack>
        </div>
      ) : null}
    </nav>
  );
};

const DomainRecordEquivalentToolbar = ({
  model,
  translate,
  emitIntent,
}: Pick<
  DesignCapabilityRendererContext,
  "model" | "translate" | "emitIntent"
>) => {
  const presentation = isJsonObject(model.presentation)
    ? model.presentation
    : {};
  const search = isJsonObject(model.search) ? model.search : undefined;
  const refresh = isJsonObject(model.refresh) ? model.refresh : undefined;
  const searchValue = search ? stringValue(search.value) ?? "" : "";
  const [draft, setDraft] = useState(searchValue);
  useEffect(() => setDraft(searchValue), [searchValue]);
  const schemaRef = stringValue(presentation.schemaRef) ?? "—";

  return (
    <div
      className="flex shrink-0 flex-wrap items-center gap-3 border-b px-4 py-3"
      data-presentation="domain-record-equivalent"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-muted-foreground" />
          <h2 className="truncate text-sm font-medium">
            {stringValue(presentation.title) ?? "—"}
          </h2>
          {stringValue(presentation.contract) ? (
            <Badge variant="outline">
              {stringValue(presentation.contract)}
            </Badge>
          ) : null}
          {presentation.readOnly === true ? (
            <Badge variant="secondary">
              {translate(
                "components.data-management.ontology-records.read-only",
              )}
            </Badge>
          ) : null}
        </div>
        <p className="mt-1 truncate text-xs text-muted-foreground">
          {translate(
            "components.data-management.ontology-records.description",
            {
              schema: schemaRef,
            },
          )}
        </p>
      </div>
      {search ? (
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            emitIntent("capability.data-toolbar.search", {
              value: draft.trim(),
            });
          }}
        >
          <Input
            value={draft}
            placeholder={translate(
              "components.data-management.ontology-records.search-placeholder",
            )}
            disabled={search.disabled === true}
            className="h-9 w-56"
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          <Button
            type="submit"
            size="sm"
            variant="outline"
            className="h-9 gap-2"
          >
            <Search className="h-4 w-4" />
            {translate("components.data-management.ontology-records.search")}
          </Button>
          {refresh ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-9 gap-2"
              disabled={refresh.disabled === true}
              onClick={() => emitIntent("capability.data-toolbar.refresh", {})}
            >
              <RefreshCw
                className={`h-4 w-4 ${refresh.loading === true ? "animate-spin" : ""}`}
              />
              {translate(
                stringValue(refresh.labelKey) ??
                  "components.data-management.ontology-records.refresh",
              )}
            </Button>
          ) : null}
        </form>
      ) : null}
    </div>
  );
};

const legacyAuxiliaryIcon = (action: JsonObject) => {
  const id =
    `${stringValue(action.id) ?? ""} ${isJsonObject(action.icon) ? stringValue(action.icon.id) ?? "" : ""}`.toLowerCase();
  if (/(plugin|extension)/.test(id)) return <Puzzle className="h-4 w-4" />;
  if (/(row-height|rows)/.test(id)) return <Rows3 className="h-4 w-4" />;
  if (/(feature-column|column|setting)/.test(id))
    return <SlidersHorizontal className="h-4 w-4" />;
  return actionIcon(action);
};

const normalizeRowHeightScale = (value: JsonValue | undefined) => {
  const numeric =
    typeof value === "number" && Number.isFinite(value) ? value : 1;
  return Math.round(Math.min(10, Math.max(0.5, numeric)) * 2) / 2;
};

const LegacyRowHeightMenu = ({
  action,
  translate,
  emitIntent,
  showLabel = false,
}: {
  action: JsonObject;
  translate: DesignCapabilityRendererContext["translate"];
  emitIntent: DesignCapabilityRendererContext["emitIntent"];
  showLabel?: boolean;
}) => {
  const preset = ["compact", "default", "comfortable"].includes(
    String(action.preset),
  )
    ? String(action.preset)
    : "default";
  const mode = action.mode === "custom" ? "custom" : "preset";
  const scale = normalizeRowHeightScale(action.scale);
  const [scaleInput, setScaleInput] = useState(String(scale));
  useEffect(() => setScaleInput(String(scale)), [scale]);
  const label = copyValueOrFallback(
    action.label,
    action.labelKey,
    translate,
    "row-height",
  );
  const emit = (next: {
    mode: "preset" | "custom";
    preset: string;
    scale: number;
  }) => emitIntent("capability.data-toolbar.row-height-change", next);
  const commitScale = (value: string) => {
    const numeric = Number(value);
    const nextScale = normalizeRowHeightScale(
      Number.isFinite(numeric) ? numeric : scale,
    );
    setScaleInput(String(nextScale));
    emit({ mode: "custom", preset, scale: nextScale });
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 shrink-0 gap-2 px-3 text-foreground"
          aria-label={typeof label === "string" ? label : "row-height"}
          title={typeof label === "string" ? label : undefined}
          disabled={action.disabled === true}
        >
          <Rows3 className="h-4 w-4" />
          <span className={showLabel ? "" : "sr-only 2xl:not-sr-only"}>
            {label}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={mode === "preset" ? preset : ""}
          onValueChange={(value) =>
            emit({ mode: "preset", preset: value, scale: 1 })
          }
        >
          {objectArray(action.options).map((option) => {
            const id = stringValue(option.id);
            if (!id) return null;
            return (
              <DropdownMenuRadioItem key={id} value={id}>
                {copyValueOrFallback(
                  option.label,
                  option.labelKey,
                  translate,
                  id,
                )}
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <div
          className="space-y-1.5 px-2 py-1.5"
          onKeyDown={(event) => event.stopPropagation()}
        >
          <div className="text-xs text-muted-foreground">
            {translate(
              stringValue(action.customLabelKey) ??
                "components.data-management.row-height-custom-label",
            )}
          </div>
          <div className="flex items-stretch gap-1.5">
            <Input
              value={scaleInput}
              inputMode="decimal"
              className="h-8 text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              onChange={(event) => {
                const next = event.currentTarget.value;
                if (/^\d*(?:\.[05]?)?$/.test(next)) setScaleInput(next);
              }}
              onBlur={() => commitScale(scaleInput)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  commitScale(scaleInput);
                }
              }}
            />
            <div className="flex flex-col justify-between">
              <Button
                type="button"
                variant="outline"
                className="h-[15px] w-6 p-0"
                disabled={scale >= 10}
                onClick={() => commitScale(String(scale + 0.5))}
              >
                <ChevronUp className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-[15px] w-6 p-0"
                disabled={scale <= 0.5}
                onClick={() => commitScale(String(scale - 0.5))}
              >
                <ChevronDown className="h-3 w-3" />
              </Button>
            </div>
          </div>
          <div className="text-[11px] leading-none text-muted-foreground">
            {translate(
              stringValue(action.customRangeLabelKey) ??
                "components.data-management.row-height-custom-range",
            )}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const columnConfigurationIcon = (option: JsonObject) => {
  const semantic =
    `${stringValue(option.id) ?? ""} ${isJsonObject(option.icon) ? stringValue(option.icon.id) ?? "" : ""}`.toLowerCase();
  if (/(^|\W)(index|hash)(\W|$)/.test(semantic))
    return <Hash className="h-3.5 w-3.5" />;
  if (/(preview|image|thumbnail)/.test(semantic))
    return <ImageIcon className="h-3.5 w-3.5" />;
  if (/(^|\W)(name|text)(\W|$)/.test(semantic))
    return <Type className="h-3.5 w-3.5" />;
  if (/(size|storage)/.test(semantic))
    return <HardDrive className="h-3.5 w-3.5" />;
  if (/(updated|clock)/.test(semantic))
    return <Clock3 className="h-3.5 w-3.5" />;
  if (/(created|calendar)/.test(semantic))
    return <CalendarDays className="h-3.5 w-3.5" />;
  if (/(category|folder)/.test(semantic))
    return <FolderTree className="h-3.5 w-3.5" />;
  if (/(tag)/.test(semantic)) return <Tag className="h-3.5 w-3.5" />;
  if (/(attachment|file|media|img)/.test(semantic))
    return <Paperclip className="h-3.5 w-3.5" />;
  if (/(relation|link)/.test(semantic))
    return <Link2 className="h-3.5 w-3.5" />;
  if (/(json|object)/.test(semantic)) return <Braces className="h-3.5 w-3.5" />;
  return <Rows3 className="h-3.5 w-3.5" />;
};

const LegacyColumnConfiguration = ({
  configuration,
  translate,
  emitIntent,
}: {
  configuration: JsonObject;
  translate: DesignCapabilityRendererContext["translate"];
  emitIntent: DesignCapabilityRendererContext["emitIntent"];
}) => {
  const [open, setOpen] = useState(false);
  const [searchBySection, setSearchBySection] = useState<
    Record<string, string>
  >({});
  const sections = objectArray(configuration.sections);
  const sourceSelections = useMemo(
    () =>
      Object.fromEntries(
        sections.flatMap((section) => {
          const id = stringValue(section.id);
          return id ? [[id, stringArray(section.selectedIds)]] : [];
        }),
      ) as Record<string, string[]>,
    [configuration.sections],
  );
  const [selectedBySection, setSelectedBySection] =
    useState<Record<string, string[]>>(sourceSelections);
  useEffect(() => setSelectedBySection(sourceSelections), [sourceSelections]);
  useEffect(() => {
    if (!open) setSearchBySection({});
  }, [open]);

  const triggerLabel = translate(
    stringValue(configuration.triggerLabelKey) ??
      "components.data-v2.column-config.configure",
  );
  const closeLabel = translate(
    stringValue(configuration.closeLabelKey) ?? "common.actions.close",
  );
  const expectedDefinitionVersion = Number(
    configuration.expectedDefinitionVersion,
  );
  const displayConfig = isJsonObject(configuration.displayConfig)
    ? configuration.displayConfig
    : {};
  const tableColumns = isJsonObject(displayConfig.tableColumns)
    ? displayConfig.tableColumns
    : {};

  const toggle = (section: JsonObject, optionId: string) => {
    const sectionId = stringValue(section.id);
    if (!sectionId || !Number.isSafeInteger(expectedDefinitionVersion)) return;
    const current = selectedBySection[sectionId] ?? [];
    const selected = new Set(current);
    if (selected.has(optionId)) selected.delete(optionId);
    else selected.add(optionId);
    const optionOrder = objectArray(section.options)
      .map((option) => stringValue(option.id))
      .filter((id): id is string => Boolean(id));
    const nextSelection = optionOrder.filter((id) => selected.has(id));
    const nextSelectedBySection = {
      ...selectedBySection,
      [sectionId]: nextSelection,
    };
    const nextDisplayConfig: JsonObject = {
      ...displayConfig,
      tableColumns: {
        ...tableColumns,
        [sectionId]: nextSelection,
      },
    };
    setSelectedBySection(nextSelectedBySection);
    emitIntent("capability.data-toolbar.column-configuration-change", {
      expectedDefinitionVersion,
      changes: { displayConfig: nextDisplayConfig },
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={configuration.disabled === true}
          className="monkeys-data-toolbar-secondary-action h-10 shrink-0 gap-2 px-2 text-foreground 2xl:px-3"
          aria-label={triggerLabel}
          title={triggerLabel}
        >
          <Eye className="h-4 w-4 2xl:mr-1" />
          <span className="monkeys-data-toolbar-secondary-action-label sr-only 2xl:not-sr-only">
            {triggerLabel}
          </span>
          <ChevronRight className="monkeys-data-toolbar-secondary-action-chevron hidden h-3.5 w-3.5 text-muted-foreground/70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
        className="z-[80] w-[min(22.75rem,calc(100vw-1rem))] rounded-xl border border-border bg-background p-0 text-foreground shadow-[0_14px_32px_-20px_rgba(15,23,42,0.24)] outline-none"
        data-settings-nested-popover
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex max-h-[min(70vh,32rem)] flex-col overflow-hidden">
          <div className="shrink-0 border-b border-border/60 px-3.5 py-2.5">
            <div className="flex items-start gap-2.5">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/35 text-muted-foreground">
                <Columns3 className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-sm font-semibold text-foreground">
                  {translate(
                    stringValue(configuration.titleKey) ??
                      "components.data-v2.column-config.dialog-title",
                  )}
                </h2>
                <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">
                  {translate(
                    stringValue(configuration.descriptionKey) ??
                      "components.data-v2.column-config.dialog-desc",
                  )}
                </p>
              </div>
              <button
                type="button"
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                onClick={() => setOpen(false)}
                aria-label={closeLabel}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="flex-1 space-y-2.5 overflow-y-auto px-2.5 pb-2.5 pt-2">
            {sections.map((section) => {
              const sectionId = stringValue(section.id);
              if (!sectionId) return null;
              const selectedIds = new Set(
                selectedBySection[sectionId] ??
                  stringArray(section.selectedIds),
              );
              const keyword = searchBySection[sectionId] ?? "";
              const options = objectArray(section.options);
              const filteredOptions = keyword.trim()
                ? options.filter((option) => {
                    const label = copyValueOrFallback(
                      option.label,
                      option.labelKey,
                      translate,
                      stringValue(option.id) ?? "",
                    );
                    return String(label)
                      .toLocaleLowerCase()
                      .includes(keyword.trim().toLocaleLowerCase());
                  })
                : options;
              return (
                <section key={sectionId} className="space-y-1">
                  <div className="flex items-start justify-between gap-2 px-0.5">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <h3 className="truncate text-[13px] font-medium text-muted-foreground">
                        {translate(stringValue(section.labelKey) ?? sectionId)}
                      </h3>
                      <Badge
                        variant="secondary"
                        className="rounded-full bg-muted/70 px-1.5 py-0 text-[10px] font-medium text-muted-foreground"
                      >
                        {selectedIds.size}/{options.length}
                      </Badge>
                    </div>
                    {section.searchable === true ? (
                      <div className="relative w-32 shrink-0">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          value={keyword}
                          onChange={(event) =>
                            setSearchBySection((current) => ({
                              ...current,
                              [sectionId]: event.currentTarget.value,
                            }))
                          }
                          placeholder={translate(
                            stringValue(section.searchPlaceholderKey) ??
                              "common.actions.search",
                          )}
                          className="h-7 rounded-md border-border/70 bg-background pl-7 text-[11px] shadow-none"
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="space-y-1">
                    {filteredOptions.map((option) => {
                      const optionId = stringValue(option.id);
                      if (!optionId) return null;
                      const active = selectedIds.has(optionId);
                      return (
                        <button
                          key={optionId}
                          type="button"
                          disabled={option.disabled === true}
                          className={`group flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${active ? "border-border/80 bg-muted/35" : "border-border/60 bg-background"} ${option.disabled === true ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:border-border hover:bg-accent/30"}`}
                          onClick={() => toggle(section, optionId)}
                        >
                          <span
                            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/35 text-muted-foreground transition-colors ${active ? "text-foreground" : ""}`}
                          >
                            {columnConfigurationIcon(option)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[13px] font-medium text-foreground">
                              {copyValueOrFallback(
                                option.label,
                                option.labelKey,
                                translate,
                                optionId,
                              )}
                            </span>
                            {option.description || option.descriptionKey ? (
                              <span className="mt-0.5 block text-[11px] leading-4 text-muted-foreground">
                                {copyValueOrFallback(
                                  option.description,
                                  option.descriptionKey,
                                  translate,
                                  "",
                                )}
                              </span>
                            ) : null}
                          </span>
                          <span
                            className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-colors ${active ? "border-foreground/60 bg-foreground/60 text-background" : "border-border/70 bg-background text-transparent group-hover:text-muted-foreground"}`}
                          >
                            <Check className="h-2.5 w-2.5" />
                          </span>
                        </button>
                      );
                    })}
                    {filteredOptions.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-border/70 bg-background px-3 py-4 text-center text-xs text-muted-foreground">
                        {translate(
                          stringValue(section.emptyLabelKey) ??
                            "common.status.no-data",
                        )}
                      </div>
                    ) : null}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const LegacyTrendMatrixConfiguration = ({
  configuration,
  translate,
  emitIntent,
}: {
  configuration: JsonObject;
  translate: CapabilityTranslate;
  emitIntent: (type: string, payload?: JsonObject) => void;
}) => {
  const configuredValue = isJsonObject(configuration.value)
    ? configuration.value
    : {};
  const fieldOptions = objectArray(configuration.fieldOptions).flatMap(
    (option) => {
      const id = stringValue(option.id);
      const kind = stringValue(option.kind);
      return id && (kind === "preview" || kind === "featureImage")
        ? [{ ...option, id, kind }]
        : [];
    },
  );
  const fallbackSourceFieldKey =
    fieldOptions.find((option) => option.kind === "preview")?.id ??
    fieldOptions[0]?.id ??
    "preview";
  const fallbackGeneratedFieldKey =
    fieldOptions.find((option) => option.kind === "featureImage")?.id ??
    fallbackSourceFieldKey;
  const configuredSourceFieldKey =
    stringValue(configuredValue.sourceFieldKey) ?? fallbackSourceFieldKey;
  const configuredGeneratedFieldKey =
    stringValue(configuredValue.generatedFieldKey) ?? fallbackGeneratedFieldKey;
  const configuredHistoryMode =
    configuredValue.historyMode === "all" ? "all" : "current";
  const [value, setValue] = useState({
    sourceFieldKey: configuredSourceFieldKey,
    generatedFieldKey: configuredGeneratedFieldKey,
    historyMode: configuredHistoryMode,
  });
  useEffect(
    () =>
      setValue({
        sourceFieldKey: configuredSourceFieldKey,
        generatedFieldKey: configuredGeneratedFieldKey,
        historyMode: configuredHistoryMode,
      }),
    [
      configuredGeneratedFieldKey,
      configuredHistoryMode,
      configuredSourceFieldKey,
    ],
  );
  const disabled = configuration.disabled === true || fieldOptions.length === 0;
  const update = (patch: Partial<typeof value>) => {
    const next = { ...value, ...patch };
    setValue(next);
    const displayConfig = isJsonObject(configuration.displayConfig)
      ? configuration.displayConfig
      : {};
    emitIntent("capability.data-toolbar.trend-matrix-configuration-change", {
      expectedDefinitionVersion: Number(
        configuration.expectedDefinitionVersion,
      ),
      changes: {
        displayConfig: {
          ...displayConfig,
          trendMatrixSettings: next,
        },
      },
    });
  };
  const optionLabel = (option: JsonObject) =>
    copyValueOrFallback(
      option.label,
      option.labelKey,
      translate,
      stringValue(option.id) ?? "",
    );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-10 shrink-0 rounded-md"
          aria-label={translate(
            stringValue(configuration.triggerLabelKey) ??
              "components.data-v2.trend-matrix.settings.button",
          )}
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-4">
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium">
              {translate(
                stringValue(configuration.titleKey) ??
                  "components.data-v2.trend-matrix.settings.title",
              )}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {translate(
                stringValue(configuration.descriptionKey) ??
                  "components.data-v2.trend-matrix.settings.description",
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium">
              {translate(
                stringValue(configuration.sourceFieldLabelKey) ??
                  "components.data-v2.trend-matrix.settings.source-field",
              )}
            </Label>
            <Select
              value={value.sourceFieldKey}
              disabled={disabled}
              onValueChange={(sourceFieldKey) => update({ sourceFieldKey })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fieldOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    <span className="inline-flex items-center gap-2">
                      <ImageIcon className="h-3.5 w-3.5" />
                      {optionLabel(option)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium">
              {translate(
                stringValue(configuration.generatedFieldLabelKey) ??
                  "components.data-v2.trend-matrix.settings.generated-field",
              )}
            </Label>
            <Select
              value={value.generatedFieldKey}
              disabled={disabled}
              onValueChange={(generatedFieldKey) =>
                update({ generatedFieldKey })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fieldOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    <span className="inline-flex items-center gap-2">
                      <ImageIcon className="h-3.5 w-3.5" />
                      {optionLabel(option)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
            <div className="min-w-0">
              <Label className="text-xs font-medium">
                {translate(
                  stringValue(configuration.showHistoryLabelKey) ??
                    "components.data-v2.trend-matrix.settings.show-history",
                )}
              </Label>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {translate(
                  stringValue(configuration.showHistoryHintKey) ??
                    "components.data-v2.trend-matrix.settings.show-history-hint",
                )}
              </div>
            </div>
            <Switch
              checked={value.historyMode === "all"}
              onCheckedChange={(checked) =>
                update({ historyMode: checked ? "all" : "current" })
              }
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const LegacyDataExplorerToolbar = ({
  model,
  childrenBySlot,
  translate,
  emitIntent,
}: DesignCapabilityRendererContext) => {
  const presentation = isJsonObject(model.presentation)
    ? model.presentation
    : {};
  const appearance = resolveDataExplorerAppearance(model.appearance);
  const search = isJsonObject(model.search) ? model.search : undefined;
  const refresh = isJsonObject(model.refresh) ? model.refresh : undefined;
  const searchValue = search ? stringValue(search.value) ?? "" : "";
  const [draft, setDraft] = useState(searchValue);
  useEffect(() => setDraft(searchValue), [searchValue]);
  const scopeSelected = presentation.scopeSelected === true;
  const views = objectArray(model.views).flatMap((view) => {
    const id = stringValue(view.id);
    return id
      ? [
          {
            value: id,
            label: copyValueOrFallback(
              view.label,
              view.labelKey,
              translate,
              id,
            ),
            icon: isJsonObject(view.icon) ? actionIcon(view) : undefined,
            disabled: view.disabled === true,
          },
        ]
      : [];
  });
  const activeViewId = stringValue(model.activeViewId);
  const recordCreateCommand = objectArray(model.commands).find(
    (command) => stringValue(command.id) === "record-create",
  );
  const auxiliaryActions = objectArray(presentation.auxiliaryActions);
  const displayActions = scopeSelected ? objectArray(model.displayActions) : [];
  const columnConfiguration =
    activeViewId === "table" && isJsonObject(model.columnConfiguration)
      ? model.columnConfiguration
      : undefined;
  const trendMatrixConfiguration =
    activeViewId === "trendMatrix" &&
    isJsonObject(model.trendMatrixConfiguration)
      ? model.trendMatrixConfiguration
      : undefined;
  const toolbarRootRef = useRef<HTMLDivElement | null>(null);
  const [toolbarWidth, setToolbarWidth] = useState<number | null>(null);
  useEffect(() => {
    const toolbar = toolbarRootRef.current;
    if (!toolbar) return;
    const measure = () =>
      setToolbarWidth(toolbar.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(toolbar);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const hasSecondaryActions =
    auxiliaryActions.length > 1 ||
    Children.count(childrenBySlot.secondaryActions) > 0 ||
    Boolean(columnConfiguration);
  const columnConfigurationAction = columnConfiguration ? (
    <LegacyColumnConfiguration
      configuration={columnConfiguration}
      translate={translate}
      emitIntent={emitIntent}
    />
  ) : null;
  const trendMatrixConfigurationAction = trendMatrixConfiguration ? (
    <LegacyTrendMatrixConfiguration
      configuration={trendMatrixConfiguration}
      translate={translate}
      emitIntent={emitIntent}
    />
  ) : null;
  const useCompactSecondaryActions =
    hasSecondaryActions && toolbarWidth !== null && toolbarWidth < 920;
  const button = (action: JsonObject, showLabel = false) => {
    const id = stringValue(action.id);
    if (!id) return null;
    if (action.kind === "row-height") {
      return (
        <LegacyRowHeightMenu
          key={id}
          action={action}
          translate={translate}
          emitIntent={emitIntent}
          showLabel={showLabel}
        />
      );
    }
    const label = copyValueOrFallback(
      action.label,
      action.labelKey,
      translate,
      id,
    );
    return (
      <Button
        key={id}
        type="button"
        variant="outline"
        size="sm"
        className="h-9 shrink-0 gap-2 px-3 text-foreground"
        aria-label={typeof label === "string" ? label : id}
        title={typeof label === "string" ? label : undefined}
        disabled={action.disabled === true}
        onClick={() =>
          emitIntent("capability.data-toolbar.command", { commandId: id })
        }
      >
        {legacyAuxiliaryIcon(action)}
        <span className={showLabel ? "" : "sr-only"}>{label}</span>
      </Button>
    );
  };

  return (
    <DataExplorerToolbarShell
      rootRef={toolbarRootRef}
      appearance={appearance}
      searchVisible={false}
      className="min-h-14 flex-col gap-2 py-2 pl-0 pr-3 md:flex-row md:flex-nowrap md:items-center md:justify-between md:gap-x-3 md:gap-y-2 xl:py-0 xl:pr-4"
      dataAttributes={{
        "data-presentation": "legacy-data-explorer-toolbar",
        "data-scope-selected": scopeSelected ? "true" : "false",
        "data-toolbar-compact": useCompactSecondaryActions ? "true" : "false",
      }}
      classNames={{
        actions: "flex w-full min-w-0 flex-row items-center gap-2 md:w-auto md:flex-1",
      }}
    >
      <div className="flex w-full min-w-0 flex-row items-center gap-2 md:w-auto md:flex-1">
        <form
          className="relative min-w-[8rem] max-w-[14rem] flex-1 basis-[14rem]"
          onSubmit={(event) => {
            event.preventDefault();
            emitIntent("capability.data-toolbar.search", {
              value: draft.trim(),
            });
          }}
        >
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={draft}
            placeholder={
              search
                ? translate(
                    stringValue(search.placeholderKey) ??
                      DESIGN_CAPABILITY_I18N_KEYS.dataToolbar.searchPlaceholder,
                  )
                : undefined
            }
            className="pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
        </form>
        {refresh ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="relative z-10 shrink-0 text-foreground"
            aria-label={translate(
              stringValue(refresh.labelKey) ??
                DESIGN_CAPABILITY_I18N_KEYS.dataToolbar.searchPlaceholder,
            )}
            disabled={refresh.disabled === true}
            onClick={() => emitIntent("capability.data-toolbar.refresh", {})}
          >
            <RefreshCw
              className={`h-4 w-4 ${refresh.loading === true ? "animate-spin" : ""}`}
            />
          </Button>
        ) : null}
        {recordCreateCommand ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="relative z-10 shrink-0 text-foreground"
            aria-label={translate(
              DESIGN_CAPABILITY_I18N_KEYS.dataToolbar.addRow,
            )}
            title={translate(DESIGN_CAPABILITY_I18N_KEYS.dataToolbar.addRow)}
            disabled={recordCreateCommand.disabled === true}
            onClick={() =>
              emitIntent("capability.data-toolbar.add-row", {
                blankRowLabel: translate(
                  DESIGN_CAPABILITY_I18N_KEYS.dataToolbar.blankRowName,
                ),
                requestedAt: Date.now(),
              })
            }
          >
            <Plus className="h-4 w-4 text-foreground" />
          </Button>
        ) : null}
      </div>
      <div className="flex w-full flex-wrap items-center justify-end gap-1.5 sm:gap-2 md:ml-auto md:w-auto md:max-w-full md:shrink-0 md:flex-nowrap">
        {auxiliaryActions.slice(0, 1).map((action) => button(action))}
        {!useCompactSecondaryActions ? (
          <>
            {auxiliaryActions.slice(1).map((action) => button(action))}
            {childrenBySlot.secondaryActions}
            {columnConfigurationAction}
          </>
        ) : (
          <FloatingPopover placement="bottom-end" offset={6}>
            <FloatingPopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0 text-foreground"
                aria-label={translate("common.actions.more-actions")}
                title={translate("common.actions.more-actions")}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </FloatingPopoverTrigger>
            <FloatingPopoverContent
              className="z-50 w-52 rounded-lg border border-border/70 bg-popover p-1.5 text-popover-foreground shadow-lg"
              data-slot="data-toolbar-more-menu"
            >
              <div className="px-2.5 pb-1 pt-1 text-[11px] font-medium text-muted-foreground">
                {translate("components.data-v2.toolbar.display-settings")}
              </div>
              <div className="flex flex-col gap-0.5">
                {auxiliaryActions
                  .slice(1)
                  .map((action) => button(action, true))}
                {childrenBySlot.secondaryActions}
                {columnConfigurationAction}
              </div>
            </FloatingPopoverContent>
          </FloatingPopover>
        )}
        {trendMatrixConfigurationAction}
        {childrenBySlot.actions}
        {displayActions.map((action) => button(action))}
        {views.length ? (
          <WorkbenchContentToolbar
            viewMode={stringValue(model.activeViewId)}
            viewModeOptions={views}
            onViewModeChange={(viewId: string) =>
              emitIntent("capability.data-toolbar.view-change", { viewId })
            }
            gallerySettings={false}
            className="!min-h-0 !border-0 !bg-transparent !p-0"
            classNames={{
              start: "gap-0",
              viewModeRoot: "w-[8.75rem] shrink-0",
              viewModeTrigger:
                "!h-10 w-full !rounded-md !border !border-border !bg-background px-3 !font-sans !text-sm !font-medium !text-foreground !shadow-sm hover:!bg-accent",
              viewModeMenu:
                "right-0 left-auto !w-full !min-w-0 !rounded-md !border-border !bg-popover !p-1 !font-sans !text-sm !font-medium !text-popover-foreground !shadow-md",
              viewModeItem:
                "!rounded-sm !px-2.5 !py-1.5 !font-sans !text-sm !font-medium !text-foreground",
            }}
          />
        ) : null}
      </div>
      {childrenBySlot.content}
    </DataExplorerToolbarShell>
  );
};

export const DataToolbarCapabilityRenderer: DesignCapabilityRenderer = (
  context,
) => {
  const { model, childrenBySlot, translate, emitIntent } = context;
  const appearance = resolveDataExplorerAppearance(model.appearance);
  const { locale } = useMonkeysLocale();
  const presentation = isJsonObject(model.presentation)
    ? model.presentation
    : undefined;
  if (presentation?.kind === "domain-record-equivalent") {
    return (
      <DomainRecordEquivalentToolbar
        model={model}
        translate={translate}
        emitIntent={emitIntent}
      />
    );
  }
  if (presentation?.kind === "legacy-data-explorer") {
    return <LegacyDataExplorerToolbar {...context} />;
  }
  const search = isJsonObject(model.search) ? model.search : undefined;
  const refresh = isJsonObject(model.refresh) ? model.refresh : undefined;
  const commands = objectArray(model.commands);
  const displayActions = objectArray(model.displayActions);
  const filterGroups = objectArray(model.filterGroups);
  const selection = isJsonObject(model.selection) ? model.selection : undefined;
  const gallerySettings = isJsonObject(model.gallerySettings)
    ? model.gallerySettings
    : undefined;
  const galleryFields = model.galleryFields === undefined ? undefined : GalleryPresentationFieldsSchema.parse(model.galleryFields);
  const canonicalGallerySettings = galleryFields ? validateGalleryPresentationSettings(model.gallerySettings, galleryFields.map(field => field.id)) : undefined;
  const gallerySettingLabel = (name: string) => translate(`monkeys.design.capability.dataToolbar.galleryPresentation.${name}`);
  const gallerySettingLabels = {
    settingsButton: translate(DESIGN_CAPABILITY_I18N_KEYS.dataToolbar.gallerySettings),
    title: translate(DESIGN_CAPABILITY_I18N_KEYS.dataToolbar.gallerySettings),
    visibleColumns: gallerySettingLabel('visibleColumns'), coverField: gallerySettingLabel('coverField'),
    autoDetect: gallerySettingLabel('autoDetect'), cardDensity: gallerySettingLabel('cardDensity'),
    cardColumns: gallerySettingLabel('cardColumns'), detailColumns: gallerySettingLabel('detailColumns'),
    fieldVisibility: gallerySettingLabel('fieldVisibility'), fieldLimit: gallerySettingLabel('fieldLimit'),
    fieldLimitHint: gallerySettingLabel('fieldLimitHint'), layout: gallerySettingLabel('layout'),
    compact: gallerySettingLabel('compact'), comfortable: gallerySettingLabel('comfortable'), detailed: gallerySettingLabel('detailed'),
    autoColumns: gallerySettingLabel('autoColumns'), columns: (count: number) => translate('monkeys.design.capability.dataToolbar.galleryPresentation.columns', { count }),
    bentoColumns: (count: number) => translate('monkeys.design.capability.dataToolbar.galleryPresentation.bentoColumns', { count }),
    topLimit: gallerySettingLabel('topLimit'), allFields: gallerySettingLabel('allFields'),
    adaptiveGrid: gallerySettingLabel('adaptiveGrid'), masonry: gallerySettingLabel('masonry'),
  };
  const primaryCommands = commands.filter(
    (command) => command.presentation === "primary",
  );
  const menuCommands = commands.filter(
    (command) => command.presentation !== "primary",
  );
  const primaryToolbarActions = primaryCommands.flatMap((command) => {
    const id = stringValue(command.id);
    if (!id) return [];
    return [
      {
        id,
        kind: "button" as const,
        label: copyValueOrFallback(
          command.label,
          command.labelKey,
          translate,
          id,
        ),
        tone: "primary" as const,
        icon: actionIcon(command),
        ariaLabel: actionAriaLabel(command, translate),
        disabled: command.disabled === true,
        loading: command.loading === true,
        onAction: () =>
          emitIntent("capability.data-toolbar.command", { commandId: id }),
      } as const,
    ];
  });
  const menuToolbarActions: DataExplorerToolbarAction[] = [
    ...filterGroups.flatMap((group) => {
      const groupId = stringValue(group.id);
      if (!groupId) return [];
      const selectedValues = objectArray(group.values)
        .map((value) => String(value.id ?? value.value ?? ""))
        .filter(Boolean);
      return [
        {
          id: `filter:${groupId}`,
          kind: "menu" as const,
          label: copyValueOrFallback(
            group.label,
            group.labelKey,
            translate,
            groupId,
          ),
          triggerLabel: String(
            copyValueOrFallback(
              group.label,
              group.labelKey,
              translate,
              groupId,
            ),
          ),
          items: objectArray(group.options).flatMap((option) => {
            const optionId = stringValue(option.id);
            if (!optionId) return [];
            return [
              {
                id: optionId,
                label: copyValueOrFallback(
                  option.label,
                  option.labelKey,
                  translate,
                  optionId,
                ),
                active: selectedValues.includes(optionId),
                disabled: option.disabled === true,
                onAction: () =>
                  emitIntent("capability.data-toolbar.filter-change", {
                    filterId: groupId,
                    values: selectedValues.includes(optionId)
                      ? selectedValues.filter((value) => value !== optionId)
                      : [...selectedValues, optionId],
                  }),
              },
            ];
          }),
        },
      ];
    }),
    ...displayActions.flatMap((action) => {
      const id = stringValue(action.id);
      if (!id) return [];
      return [
        {
          id,
          kind:
            action.presentation === "icon"
              ? ("icon-button" as const)
              : ("button" as const),
          label: copyValueOrFallback(
            action.label,
            action.labelKey,
            translate,
            id,
          ),
          triggerLabel: actionAriaLabel(action, translate),
          icon: actionIcon(action),
          tone: actionTone(action),
          disabled: action.disabled === true,
          loading: action.loading === true,
          onAction: () =>
            emitIntent("capability.data-toolbar.command", { commandId: id }),
        },
      ];
    }),
    ...(menuCommands.length
      ? [
          {
            id: "commands",
            kind: "menu" as const,
            label: translate(
              'monkeys.design.capability.dataToolbar.moreActions',
            ),
            triggerLabel: translate(
              'monkeys.design.capability.dataToolbar.moreActions',
            ),
            items: menuCommands.flatMap((command) => {
              const id = stringValue(command.id);
              if (!id) return [];
              return [
                {
                  id,
                  label: copyValueOrFallback(
                    command.label,
                    command.labelKey,
                    translate,
                    id,
                  ),
                  title: actionAriaLabel(command, translate),
                  icon: actionIcon(command),
                  tone: actionTone(command),
                  disabled: command.disabled === true,
                  loading: command.loading === true,
                  onAction: () =>
                    emitIntent("capability.data-toolbar.command", {
                      commandId: id,
                    }),
                },
              ];
            }),
          },
        ]
      : []),
  ];
  const viewOptions = objectArray(model.views).flatMap((view) => {
    const id = stringValue(view.id);
    return id
      ? [
          {
            value: id,
            label: copyValueOrFallback(
              view.label,
              view.labelKey,
              translate,
              id,
            ),
            disabled: view.disabled === true,
          },
        ]
      : [];
  });
  const searchValue = search ? stringValue(search.value) ?? "" : "";
  const searchPlaceholder = search
    ? translate(
        stringValue(search.placeholderKey) ??
          DESIGN_CAPABILITY_I18N_KEYS.dataToolbar.searchPlaceholder,
      )
    : undefined;
  const hasLeading = Boolean(childrenBySlot.leading || search || (refresh && stringValue(refresh.labelKey)) || (selection && Number(selection.selectedCount) > 0));
  return (
    <DataExplorerToolbarShell
      appearance={appearance}
      leading={hasLeading ?
        <>
          {childrenBySlot.leading}
          {selection &&
          typeof selection.selectedCount === "number" &&
          selection.selectedCount > 0 ? (
            <BaseBadge tone="primary">
              {translate(
                stringValue(selection.labelKey) ??
                  DESIGN_CAPABILITY_I18N_KEYS.dataToolbar.selection,
                {
                  count: selection.selectedCount,
                },
              )}
            </BaseBadge>
          ) : null}
          {search ? (
            <form
              className="relative min-w-[8rem] max-w-[14rem] flex-1"
              onSubmit={(event) => {
                event.preventDefault();
                emitIntent("capability.data-toolbar.search", {
                  value: searchValue,
                });
              }}
            >
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                <SearchIcon />
              </span>
              <BaseInput
                type="search"
                value={searchValue}
                aria-label={searchPlaceholder}
                placeholder={searchPlaceholder}
                className="h-10 w-full pl-9"
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  emitIntent("capability.data-toolbar.search", {
                    value: event.currentTarget.value,
                  })
                }
              />
            </form>
          ) : null}
          {refresh && stringValue(refresh.labelKey) ? (
            <DataExplorerButton
              iconOnly
              icon={<RefreshIcon />}
              label={translate(String(refresh.labelKey))}
              disabled={refresh.disabled === true}
              loading={refresh.loading === true}
              onClick={() => emitIntent("capability.data-toolbar.refresh", {})}
            />
          ) : null}
        </> : undefined
      }
      searchVisible={false}
      actions={
        <>
          {viewOptions.length || canonicalGallerySettings ? (
            <WorkbenchContentToolbar
              viewMode={stringValue(model.activeViewId)}
              viewModeOptions={viewOptions}
              onViewModeChange={(viewId: string) =>
                emitIntent("capability.data-toolbar.view-change", { viewId })
              }
              gallerySettings={
                canonicalGallerySettings && galleryFields ? {
                  triggerLabel: gallerySettingLabels.settingsButton,
                  labels: gallerySettingLabels,
                  columns: galleryFields.map(field => ({ key: field.id, label: field.labelI18n ? resolveI18nDisplayText(field.labelI18n, locale) : field.labelKey ? translate(field.labelKey) : field.label ?? field.id })),
                  value: canonicalGallerySettings,
                  showColumnVisibility: true, showGalleryControls: true, showLayoutControl: true, showGridColumns: true, showDetailColumns: true,
                  allowEmptyVisibleColumns: false,
                  onValueChange: (_next: WorkbenchGallerySettingsValue, patch: Partial<WorkbenchGallerySettingsValue>) => {
                    const keys = Object.keys(patch);
                    if (keys.length !== 1) return;
                    const settingId = keys[0];
                    if (!Object.prototype.hasOwnProperty.call(canonicalGallerySettings, settingId)) return;
                    let next;
                    try { next = normalizeGalleryPresentationSettings({ ...canonicalGallerySettings, ...patch }, galleryFields.map(field => field.id)); }
                    catch { return; }
                    const value = next[settingId as keyof typeof next];
                    emitIntent('capability.data-toolbar.gallery-settings-change', { settingId, value });
                  },
                } : gallerySettings
                  ? {
                      labels: gallerySettingLabels,
                      triggerLabel: translate(
                        DESIGN_CAPABILITY_I18N_KEYS.dataToolbar.gallerySettings,
                      ),
                      value: {
                        galleryGridColumns:
                          typeof gallerySettings.columns === "number"
                            ? gallerySettings.columns
                            : "auto",
                      },
                      showColumnVisibility: false,
                      showGalleryControls: true,
                      showLayoutControl: false,
                      showGridColumns: true,
                      showDetailColumns: false,
                      onValueChange: (
                        nextValue: WorkbenchGallerySettingsValue,
                      ) =>
                        emitIntent(
                          "capability.data-toolbar.gallery-settings-change",
                          {
                            settingId: "columns",
                            value: nextValue.galleryGridColumns ?? "auto",
                          },
                        ),
                    }
                  : false
              }
              className="!min-h-0 !border-0 !bg-transparent !p-0"
              classNames={{
                start: "gap-0",
                viewModeRoot: "w-[8.75rem] shrink-0",
                viewModeTrigger:
                  "!h-10 w-full !rounded-md !border !border-border !bg-background px-3 !font-sans !text-sm !font-medium !text-foreground !shadow-sm hover:!bg-accent",
                viewModeMenu:
                  "right-0 left-auto !w-full !min-w-0 !rounded-md !border-border !bg-popover !p-1 !font-sans !text-sm !font-medium !text-popover-foreground !shadow-md",
                viewModeItem:
                  "!rounded-sm !px-2.5 !py-1.5 !font-sans !text-sm !font-medium !text-foreground",
              }}
            />
          ) : null}
          {menuToolbarActions.length ? (
            <DataExplorerToolbarActions
              actions={menuToolbarActions}
              layout="toolbar"
            />
          ) : null}
          {childrenBySlot.actions}
          {primaryToolbarActions.map((action) => (
            <DataExplorerButton
              key={action.id}
              icon={action.icon}
              label={action.label}
              ariaLabel={action.ariaLabel}
              tone={action.tone}
              disabled={action.disabled}
              loading={action.loading}
              onClick={action.onAction}
            />
          ))}
        </>
      }
      classNames={{
        root: "min-h-14 flex-col gap-2 border-b bg-background py-2 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:gap-3 2xl:flex-nowrap 2xl:px-4",
        leading: "w-full min-w-0 flex-1 flex-wrap lg:w-auto",
        actions:
          "ml-auto w-full min-w-0 max-w-full flex-nowrap justify-start overflow-x-auto pb-1 overscroll-x-contain [scrollbar-width:thin] lg:w-auto lg:flex-1 lg:justify-end 2xl:overflow-visible 2xl:pb-0",
      }}
    >
      {childrenBySlot.content}
    </DataExplorerToolbarShell>
  );
};

const LegacyDataExplorerRowMenu = ({
  rowId,
  rowDisabled,
  menuActions,
  translate,
  onAction,
}: {
  rowId: string;
  rowDisabled: boolean;
  menuActions: Array<{ action: JsonObject; actionId: string }>;
  translate: DesignCapabilityRendererContext["translate"];
  onAction: (rowId: string, actionId: string) => void;
}) => {
  const suppressFocusRestoreRef = useRef(false);
  return (
    <div className="flex items-center justify-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="h-8 w-8 p-0"
            aria-label={translate("components.data-v1.table.open-menu")}
            onClick={(event) => event.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">
              {translate("components.data-v1.table.open-menu")}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onClick={(event) => event.stopPropagation()}
          onCloseAutoFocus={(event) => {
            if (suppressFocusRestoreRef.current) {
              event.preventDefault();
              suppressFocusRestoreRef.current = false;
            }
          }}
        >
          <DropdownMenuLabel>
            {translate("common.actions.actions")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {menuActions.flatMap(({ action, actionId }, actionIndex) => {
            const destructive =
              action.destructive === true || action.presentation === "danger";
            const previousAction =
              actionIndex > 0
                ? menuActions[actionIndex - 1]?.action
                : undefined;
            const previousDestructive =
              previousAction?.destructive === true ||
              previousAction?.presentation === "danger";
            return [
              destructive && !previousDestructive ? (
                <DropdownMenuSeparator key={`${actionId}:separator`} />
              ) : null,
              <DropdownMenuItem
                key={actionId}
                title={typeof action.titleKey === 'string' ? translate(action.titleKey) : undefined}
                disabled={action.disabled === true || rowDisabled}
                className={destructive ? "text-destructive-text" : undefined}
                onSelect={() => {
                  suppressFocusRestoreRef.current =
                    actionId === "record-history";
                  onAction(rowId, actionId);
                }}
              >
                {legacyRecordActionIcon(actionId)}
                {copyValueOrFallback(
                  action.label,
                  action.labelKey,
                  translate,
                  actionId,
                )}
              </DropdownMenuItem>,
            ];
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const restrictTableRowActions = (actions: JsonObject[], row: JsonObject): JsonObject[] => {
  const restrictions = isJsonObject(row.actionAvailability) ? row.actionAvailability : {};
  return actions.flatMap((action) => {
    const id = stringValue(action.id);
    const restriction = id && isJsonObject(restrictions[id]) ? restrictions[id] : undefined;
    if (restriction?.hidden === true) return [];
    const merged: JsonObject = { ...action, disabled: action.disabled === true || restriction?.disabled === true };
    if (typeof restriction?.labelKey === 'string') { delete merged.label; merged.labelKey = restriction.labelKey; }
    if (typeof restriction?.titleKey === 'string') merged.titleKey = restriction.titleKey;
    return [merged];
  });
};

const declaredTableDimension = (value: unknown): number | undefined => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 10000 ? value : undefined;

export const OntologyTableCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  pendingActionInvocations = [],
  collectionScrollRestoration,
  childrenBySlot,
  translate,
  emitIntent,
  resolveMedia = () => undefined,
}) => {
  const modelColumns = objectArray(model.columns);
  const rows = objectArray(model.rows);
  const selected = rows
    .filter((row) => row.selected === true)
    .flatMap((row) => (stringValue(row.id) ? [String(row.id)] : []));
  const pagination = cursorPagination(model.pagination);
  const selectAll = isJsonObject(model.selectAll) ? model.selectAll : undefined;
  const rowActions = objectArray(model.rowActions);
  const emitRowAction = (rowId: string, actionId: string) =>
    actionId === "record-delete"
      ? emitIntent("capability.ontology-table.delete", { rowId })
      : emitIntent("capability.ontology-table.row-action", { rowId, actionId, ...(isJsonObject(rows.find(row => row.id === rowId)?.context) ? { context: rows.find(row => row.id === rowId)!.context } : {}) });
  const columnActions = objectArray(model.columnActions);
  const presentation = isJsonObject(model.presentation)
    ? model.presentation
    : undefined;
  const domainRecordEquivalent =
    presentation?.kind === "domain-record-equivalent";
  const legacyDataExplorer = presentation?.kind === "legacy-data-explorer";
  const renderedModelColumns = legacyDataExplorer
    ? modelColumns.filter((column) => column.hidden !== true)
    : modelColumns;
  const legacyHiddenModelColumns = legacyDataExplorer
    ? modelColumns.filter((column) => column.hidden === true)
    : [];
  const rowHeight = isJsonObject(model.rowHeight) ? model.rowHeight : undefined;
  const rowHeightPreset =
    rowHeight?.preset === "compact" || rowHeight?.preset === "comfortable"
      ? rowHeight.preset
      : "default";
  const rowHeightScale =
    rowHeight?.mode === "custom" ? normalizeRowHeightScale(rowHeight.scale) : 1;
  const rowPaddingY =
    (rowHeightPreset === "compact"
      ? 4
      : rowHeightPreset === "comfortable"
        ? 16
        : 8) * rowHeightScale;
  const legacyRowHeight =
    (rowHeightPreset === "compact"
      ? 57
      : rowHeightPreset === "comfortable"
        ? 121
        : 81) * rowHeightScale;
  const selectionEnabled = model.selectionEnabled !== false;
  const activationEnabled = model.activationEnabled !== false;
  if (!rows.length && !pagination?.hasMore && !domainRecordEquivalent) {
    if (legacyDataExplorer) {
      return (
        <div className="flex h-64 items-center justify-center">
          <p className="text-muted-foreground">
            {translate(
              stringValue(model.emptyLabelKey) ?? "common.status.no-data",
            )}
          </p>
        </div>
      );
    }
    if (childrenBySlot.header || childrenBySlot.footer) {
      return (
        <DataExplorerViewShell
          className="col-span-full"
          header={childrenBySlot.header}
          footer={childrenBySlot.footer}
        >
          <div className="flex min-h-48 flex-1 items-center justify-center">
            <CapabilityStatus
              kind="empty"
              translate={translate}
              emptyLabelKey={stringValue(model.emptyLabelKey)}
            />
          </div>
        </DataExplorerViewShell>
      );
    }
    return <CapabilityStatus kind="empty" translate={translate} emptyLabelKey={stringValue(model.emptyLabelKey)} />;
  }

  const cellValue = (columnId: string, row: JsonObject) => {
    const cells = isJsonObject(row.cells) ? row.cells : {};
    const cell = cells[columnId];
    const typedCell =
      isJsonObject(cell) && stringValue(cell.valueType) ? cell : undefined;
    const rawValue = typedCell ? typedCell.value : cell;
    const valueType = stringValue(typedCell?.valueType);
    if (valueType === 'copy' || valueType === 'localized-text' || valueType === 'identity' || valueType === 'badge' || valueType === 'stack' || valueType === 'metrics') return <TypedDisplayCell valueType={valueType} value={rawValue} copy={typedCell?.copy} valueFormat={typedCell?.valueFormat} translate={translate} />;
    if (valueType === "message") return renderTableMessageValue(rawValue, translate);
    if (valueType === 'timestamp') return renderDateTimeValue(rawValue, typeof rawValue === 'number' && rawValue < 1_000_000_000_000 ? 'seconds' : 'milliseconds', stringValue(typedCell?.locale));
    return governedReadValue(rawValue);
  };

  if (domainRecordEquivalent) {
    return (
      <div
        className="col-span-full flex min-h-0 flex-1 flex-col overflow-hidden bg-background"
        data-presentation="domain-record-equivalent"
      >
        {childrenBySlot.header}
        <div className="min-h-0 flex-1 overflow-auto">
          <Table style={typeof model.tableMinWidth === 'number' ? { minWidth: model.tableMinWidth } : undefined}>
            <TableHeader className="sticky top-0 z-10 bg-background">
              <TableRow>
                {modelColumns.map((column) => {
                  const id = stringValue(column.id);
                  if (!id) return null;
                  return (
                    <TableHead
                      key={id}
                      style={{ width: declaredTableDimension(column.width), minWidth: declaredTableDimension(column.minWidth), whiteSpace: column.wrap === true ? 'normal' : column.wrap === false ? 'nowrap' : undefined }}
                      className={id === "recordVersion" ? "w-24" : undefined}
                    >
                      {copyValueOrFallback(
                        column.label,
                        column.labelKey,
                        translate,
                        id,
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {model.loading === true ? (
                <TableRow>
                  <TableCell
                    colSpan={Math.max(modelColumns.length, 1)}
                    className="h-40 text-center text-muted-foreground"
                  >
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                    {translate(
                      "components.data-management.ontology-records.loading",
                    )}
                  </TableCell>
                </TableRow>
              ) : rows.length ? (
                rows.map((row) => {
                  const rowId = stringValue(row.id);
                  if (!rowId) return null;
                  return (
                    <TableRow key={rowId}>
                      {modelColumns.map((column) => {
                        const id = stringValue(column.id);
                        if (!id) return null;
                        const value = cellValue(id, row);
                        return (
                          <TableCell
                            key={id}
                            style={{ width: declaredTableDimension(column.width), minWidth: declaredTableDimension(column.minWidth), whiteSpace: column.wrap === true ? 'normal' : column.wrap === false ? 'nowrap' : undefined, overflowWrap: column.wrap === true ? 'anywhere' : undefined }}
                            className={
                              column.wrap === true ? "align-top" :
                              id === "aggregateId"
                                ? "max-w-72 font-mono text-xs"
                                : id === "createdTimestamp"
                                  ? "whitespace-nowrap text-xs text-muted-foreground"
                                  : "max-w-72 truncate"
                            }
                            title={
                              typeof value === "string" ||
                              typeof value === "number"
                                ? String(value)
                                : undefined
                            }
                          >
                            {id === "recordVersion" ? (
                              <Badge variant="secondary">{value}</Badge>
                            ) : (
                              value
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={Math.max(modelColumns.length, 1)}
                    className="h-40 text-center text-muted-foreground"
                  >
                    {translate(
                      "components.data-management.ontology-records.empty",
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {pagination?.hasMore && pagination.nextCursor ? (
          <div className="flex justify-center border-t border-border p-3">
            <BaseButton
              label={translate(
                DESIGN_CAPABILITY_I18N_KEYS.ontologyTable.loadMore,
              )}
              loading={pagination.loadingMore}
              disabled={pagination.loadingMore}
              onClick={() =>
                emitIntent("capability.ontology-table.load-more", {
                  cursor: String(pagination.nextCursor),
                })
              }
            />
          </div>
        ) : null}
        {childrenBySlot.footer}
      </div>
    );
  }
  const selectionColumnId = "__selection";
  const legacyCellDivider = legacyDataExplorer
    ? "relative after:pointer-events-none after:absolute after:bottom-2 after:right-0 after:top-2 after:w-px after:bg-border/20 after:content-[''] group-hover:after:bg-border/35"
    : undefined;
  const columns: WorkbenchTableViewColumn[] = [
    ...(selectionEnabled
      ? [
          {
            key: selectionColumnId,
            label: "",
            align: "center" as const,
            width: 56,
            minWidth: 56,
            maxWidth: 56,
            draggable: false,
            resizable: false,
            headerClassName: legacyDataExplorer
              ? "!sticky !bg-background !shadow-none"
              : undefined,
            cellClassName: legacyDataExplorer
              ? "!sticky !z-20 !bg-background !shadow-none group-hover:!bg-muted group-data-[state=selected]:!bg-muted after:pointer-events-none after:absolute after:bottom-2 after:right-0 after:top-2 after:w-px after:bg-border/20 after:content-[''] group-hover:after:bg-border/35"
              : undefined,
          },
        ]
      : []),
    ...renderedModelColumns.flatMap((column) => {
      const id = stringValue(column.id);
      if (!id) return [];
      const actions = legacyDataExplorer
        ? []
        : [...objectArray(column.actions), ...columnActions];
      const legacySize =
        id === "index"
          ? { width: 72, minWidth: 56 }
          : id === "preview"
            ? { width: 96, minWidth: 72 }
            : id === "name"
              ? { width: 240, minWidth: 180 }
              : id === "type"
                ? { width: 120, minWidth: 80 }
                : undefined;
      return [
        {
          key: id,
          label: copyValueOrFallback(
            column.label,
            column.labelKey,
            translate,
            id,
          ),
          align:
            legacyDataExplorer && id === "preview"
              ? "center"
              : (stringValue(column.align) as
                  | "left"
                  | "center"
                  | "right"
                  | undefined) ?? "left",
          sortable:
            column.sortable === true && Boolean(stringValue(column.sortKey)),
          sortDirection:
            (stringValue(column.sortDirection) as
              | "asc"
              | "desc"
              | "none"
              | undefined) ?? "none",
          sortLabel: translate(DESIGN_CAPABILITY_I18N_KEYS.ontologyTable.sort),
          sortKey: stringValue(column.sortKey),
          resizable: true,
          valueType: stringValue(column.valueType),
          ...legacySize,
          width: declaredTableDimension(column.width) ?? legacySize?.width,
          minWidth: declaredTableDimension(column.minWidth) ?? legacySize?.minWidth,
          wrap: column.wrap === true,
          headerStyle: { minWidth: declaredTableDimension(column.minWidth), whiteSpace: column.wrap === true ? 'normal' as const : undefined },
          cellClassName: legacyCellDivider,
          actions: actions.length ? (
            <DataExplorerToolbarActions
              layout="inline"
              actions={actions.flatMap((action) => {
                const actionId = stringValue(action.id);
                if (!actionId) return [];
                return [
                  {
                    id: `${id}:${actionId}`,
                    kind: "icon-button" as const,
                    label: copyValueOrFallback(
                      action.label,
                      action.labelKey,
                      translate,
                      actionId,
                    ),
                    icon: actionIcon(action),
                    tone: actionTone(action),
                    disabled: action.disabled === true,
                    onAction: () =>
                      emitIntent("capability.ontology-table.column-action", {
                        columnId: id,
                        actionId,
                      }),
                  },
                ];
              })}
            />
          ) : undefined,
        },
      ];
    }),
    ...(rowActions.length
      ? [
          {
            key: "__actions",
            label: legacyDataExplorer
              ? translate("common.actions.actions")
              : "",
            align: legacyDataExplorer ? ("center" as const) : undefined,
            width: typeof model.actionWidth === 'number' ? model.actionWidth : 72,
            minWidth: typeof model.actionWidth === 'number' ? model.actionWidth : 72,
            maxWidth: typeof model.actionWidth === 'number' ? model.actionWidth : 72,
            draggable: false,
            resizable: false,
            cellClassName: legacyCellDivider,
          },
        ]
      : []),
    ...legacyHiddenModelColumns.flatMap((column) => {
      const id = stringValue(column.id);
      if (!id) return [];
      return [
        {
          key: `__legacy-hidden:${id}`,
          label: "",
          width: 180,
          minWidth: 120,
          draggable: false,
          resizable: false,
          headerClassName: "!bg-transparent !p-0",
          cellClassName: "!bg-transparent !p-0",
        },
      ];
    }),
    ...(legacyDataExplorer
      ? [
          {
            key: "__legacy-filler",
            label: (
              <div
                className="pointer-events-none absolute inset-y-2 left-0 right-0"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(to right, transparent 0, transparent 179px, hsl(var(--border) / 0.5) 179px, hsl(var(--border) / 0.5) 180px)",
                }}
              />
            ),
            width: "auto",
            minWidth: 1,
            draggable: false,
            resizable: false,
            headerClassName: "!bg-transparent !p-0 after:!hidden",
            cellClassName: "!bg-transparent !p-0",
            headerStyle: {
              backgroundImage:
                "repeating-linear-gradient(to right, hsl(var(--muted) / 0.08) 0, hsl(var(--muted) / 0.08) 180px, hsl(var(--muted) / 0.04) 180px, hsl(var(--muted) / 0.04) 360px)",
            },
            cellStyle: {
              backgroundImage:
                "repeating-linear-gradient(to right, hsl(var(--muted) / 0.05) 0, hsl(var(--muted) / 0.05) 180px, hsl(var(--muted) / 0.025) 180px, hsl(var(--muted) / 0.025) 360px)",
            },
          },
        ]
      : []),
  ];
  const legacyTableMinimumWidth = legacyDataExplorer
    ? columns.reduce((total, column) => {
        if (column.key === "__legacy-filler") return total;
        if (typeof column.width === "number") return total + column.width;
        if (typeof column.minWidth === "number") return total + column.minWidth;
        return total + 160;
      }, 0)
    : undefined;
  return (
    <DataExplorerViewShell
      className="col-span-full"
      header={childrenBySlot.header}
      footer={
        model.showFooter === false && pagination?.hasMore !== true ? childrenBySlot.footer : domainRecordEquivalent ? (
          <CollectionPaginationFooter
            pagination={pagination}
            selectedCount={selected.length}
            loadMoreLabel={translate(
              DESIGN_CAPABILITY_I18N_KEYS.ontologyTable.loadMore,
            )}
            loadingMoreLabel={translate(
              DESIGN_CAPABILITY_I18N_KEYS.ontologyTable.loadingMore,
            )}
            onLoadMore={(cursor) =>
              emitIntent("capability.ontology-table.load-more", { cursor })
            }
            onPageChange={(page) =>
              emitIntent("capability.ontology-table.page-change", { page })
            }
            onPageSizeChange={(pageSize) =>
              emitIntent("capability.ontology-table.page-size-change", {
                pageSize,
              })
            }
          />
        ) : (
          <BaseStack
            gap={legacyDataExplorer ? "0" : "var(--global-spacing-sm, 0.5rem)"}
          >
            <CollectionPaginationFooter
              pagination={pagination}
              selectedCount={selected.length}
              legacyDataExplorer={legacyDataExplorer}
              loadMoreInContent
              translate={translate}
              loadMoreLabel={translate(
                DESIGN_CAPABILITY_I18N_KEYS.ontologyTable.loadMore,
              )}
              loadingMoreLabel={translate(
                DESIGN_CAPABILITY_I18N_KEYS.ontologyTable.loadingMore,
              )}
              onLoadMore={(cursor) =>
                emitIntent("capability.ontology-table.load-more", { cursor })
              }
              onPageChange={(page) =>
                emitIntent("capability.ontology-table.page-change", { page })
              }
              onPageSizeChange={(pageSize) =>
                emitIntent("capability.ontology-table.page-size-change", {
                  pageSize,
                })
              }
            />
            {childrenBySlot.footer}
          </BaseStack>
        )
      }
    >
      <WorkbenchTableView<JsonObject>
        scrollRestoration={collectionScrollRestoration}
        rows={rows}
        columns={columns}
        tableMinWidth={typeof model.tableMinWidth === 'number' ? model.tableMinWidth : legacyTableMinimumWidth}
        scrollFooter={
          pagination?.mode !== 'window' && pagination?.hasMore ? <CursorInfiniteScroll pagination={pagination} label={translate(DESIGN_CAPABILITY_I18N_KEYS.ontologyTable.loadMore)} loadingLabel={translate(DESIGN_CAPABILITY_I18N_KEYS.ontologyTable.loadingMore)} onLoadMore={cursor => emitIntent('capability.ontology-table.load-more', { cursor })} /> : legacyDataExplorer &&
          rows.length > 0 &&
          pagination?.hasMore === false ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {translate("components.data.card-view.all-loaded", {
                total:
                  pagination.total ??
                  pagination.loadedEnd ??
                  pagination.returned ??
                  rows.length,
              })}
            </div>
          ) : undefined
        }
        bodyMaxHeight="100%"
        columnResizeEnabled={model.columnResizeEnabled !== false}
        columnDragEnabled={legacyDataExplorer}
        headerPresentation={
          legacyDataExplorer ? "classic-data-grid" : undefined
        }
        fillRemainingWidthColumnKey={
          legacyDataExplorer ? "__legacy-filler" : undefined
        }
        style={
          {
            "--monkeys-data-row-padding-y": `${rowPaddingY}px`,
            "--monkeys-data-row-height": `${legacyRowHeight}px`,
            scrollbarGutter: legacyDataExplorer ? "auto" : undefined,
          } as CSSProperties
        }
        classNames={{
          root: "!rounded-none !border-0 !bg-transparent",
          scrollArea: "min-h-0 flex-1",
          table: legacyDataExplorer
            ? "!table-fixed !border-collapse !text-sm [&_th:first-child]:!bg-background [&_th:last-child]:!bg-transparent [&_th:last-child]:!p-0 [&_th:last-child]:!font-bold [&_th:last-child]:!text-foreground [&_td:last-child]:!p-0"
            : "!text-sm",
          header: legacyDataExplorer
            ? "!border-b !border-gray-200 !bg-background !font-sans !text-sm !font-normal !normal-case !tracking-normal !text-foreground"
            : "!font-sans !text-xs !normal-case !tracking-normal",
          headerRow: legacyDataExplorer ? "[&>*]:!border-b-0" : undefined,
          body: legacyDataExplorer
            ? "!divide-y-0 [&_tr:last-child>*]:!border-b [&_tr:last-child>*]:!border-gray-200"
            : undefined,
          row: legacyDataExplorer
            ? "group !h-[var(--monkeys-data-row-height)] !border-b !border-gray-200 !transition-colors [&>*]:!border-b-0 hover:!bg-muted/50"
            : undefined,
          headerCell: domainRecordEquivalent
            ? "!bg-background !px-4 !py-2"
            : legacyDataExplorer
              ? "!h-9 !bg-transparent !px-3 !py-px !text-sm !shadow-none"
              : "!bg-background !px-3 !py-2.5",
          cell: domainRecordEquivalent
            ? "!px-4 !py-2"
            : legacyDataExplorer
              ? "!px-3 !py-[var(--monkeys-data-row-padding-y)] !text-foreground"
              : "!px-3 !py-[var(--monkeys-data-row-padding-y)]",
        }}
        getRowKey={(row) => String(row.id)}
        isRowDisabled={(row) => row.disabled === true}
        onRowClick={(row) => {
          const rowId = stringValue(row.id);
          if (rowId && activationEnabled) {
            emitIntent("capability.ontology-table.activate", { rowId });
          }
        }}
        onSort={(_columnKey, column) => {
          const sortBy = stringValue(column.sortKey);
          if (!sortBy || column.key === selectionColumnId) return;
          emitIntent("capability.ontology-table.sort", {
            columnId: column.key,
            sortBy,
            direction: column.sortDirection === "asc" ? "desc" : "asc",
          });
        }}
        renderColumnHeader={(column) => {
          if (column.key === selectionColumnId) {
            const control = (
              <Checkbox
                checked={selectAll?.selected === true}
                aria-label={translate(
                  stringValue(selectAll?.labelKey) ??
                    DESIGN_CAPABILITY_I18N_KEYS.ontologyTable.select,
                )}
                disabled={selectAll?.disabled === true}
                onCheckedChange={(checked) =>
                  emitIntent("capability.ontology-table.selection-change", {
                    rowIds: checked
                      ? rows.flatMap((row) =>
                          stringValue(row.id) && row.disabled !== true
                            ? [String(row.id)]
                            : [],
                        )
                      : [],
                  })
                }
              />
            );
            return legacyDataExplorer ? (
              <div className="flex w-full items-center justify-center">
                {control}
              </div>
            ) : (
              control
            );
          }
          if (column.key.startsWith("__legacy-hidden:")) return null;
          if (column.key === "__legacy-filler") return column.label;
          if (column.key === "__actions")
            return legacyDataExplorer ? column.label : null;
          return column.label;
        }}
        renderCell={(column, row, { rowIndex }) => {
          const rowId = stringValue(row.id);
          if (!rowId) return null;
          if (column.key === selectionColumnId) {
            const control = (
              <Checkbox
                checked={row.selected === true}
                aria-label={translate(
                  DESIGN_CAPABILITY_I18N_KEYS.ontologyTable.select,
                )}
                disabled={row.disabled === true}
                onCheckedChange={(checked) =>
                  emitIntent("capability.ontology-table.selection-change", {
                    rowIds: checked
                      ? Array.from(new Set([...selected, rowId]))
                      : selected.filter((value) => value !== rowId),
                  })
                }
              />
            );
            return legacyDataExplorer ? (
              <div className="flex w-full items-center justify-center">
                {control}
              </div>
            ) : (
              control
            );
          }
          if (column.key.startsWith("__legacy-hidden:")) return null;
          if (column.key === "__legacy-filler") {
            return (
              <div
                className="pointer-events-none absolute inset-y-2 left-0 right-0"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(to right, transparent 0, transparent 179px, hsl(var(--border) / 0.28) 179px, hsl(var(--border) / 0.28) 180px)",
                }}
              />
            );
          }
          if (column.key === "__actions") {
            if (legacyDataExplorer) {
              const menuActions = restrictTableRowActions(rowActions, row).flatMap((action) => {
                const actionId = stringValue(action.id);
                if (!actionId) return [];
                return [{ action, actionId }];
              });
              return (
                <LegacyDataExplorerRowMenu
                  rowId={rowId}
                  rowDisabled={row.disabled === true}
                  menuActions={menuActions}
                  translate={translate}
                  onAction={emitRowAction}
                />
              );
            }
            return (
              <DataExplorerToolbarActions
                layout="inline"
                actions={collectionToolbarActions(restrictTableRowActions(rowActions, row).flatMap((action) => {
                  const actionId = stringValue(action.id);
                  if (!actionId) return [];
                  const pending = action.loading === true || pendingActionInvocations.some(invocation =>
                    invocation.source.port === (actionId === 'record-delete' ? 'capability.ontology-table.delete' : 'capability.ontology-table.row-action') &&
                    invocation.intent.rowId === rowId && (actionId === 'record-delete' || invocation.intent.actionId === actionId));
                  return [
                    {
                      id: `${rowId}:${actionId}`,
                      presentation: action.presentation,
                      destructive: action.destructive === true,
                      kind:
                        action.presentation === "icon"
                          ? ("icon-button" as const)
                          : ("button" as const),
                      label: copyValueOrFallback(
                        action.label,
                        action.labelKey,
                        translate,
                        actionId,
                      ),
                      title: typeof action.titleKey === 'string' ? translate(action.titleKey) : undefined,
                      icon: actionIcon(action),
                      tone: actionTone(action),
                      disabled:
                        action.disabled === true || row.disabled === true || pending,
                      loading: pending,
                      onAction: () => emitRowAction(rowId, actionId),
                    },
                  ];
                }), translate('monkeys.design.capability.dataToolbar.moreActions'))}
              />
            );
          }
          const cells = isJsonObject(row.cells) ? row.cells : {};
          const cell = cells[column.key];
          const typedCell =
            isJsonObject(cell) && stringValue(cell.valueType)
              ? cell
              : undefined;
          const rawValue = typedCell ? typedCell.value : cell;
          if (legacyDataExplorer && column.key === "index") {
            const indexValue =
              typeof rawValue === "number" && Number.isFinite(rawValue)
                ? rawValue
                : rowIndex + 1;
            return (
              <div className="w-full text-center tabular-nums text-muted-foreground">
                {indexValue}
              </div>
            );
          }
          if (legacyDataExplorer && column.key === "preview") {
            if (isJsonObject(rawValue) && rawValue.kind === 'json' && rawValue.dataType === 'img') return governedReadValue(rawValue);
            const files =
              isJsonObject(rawValue) && rawValue.kind === "files"
                ? objectArray(rawValue.items)
                : [];
            const firstFile = files[0];
            const mediaId = firstFile ? stringValue(firstFile.id) : undefined;
            const mediaType = firstFile
              ? stringValue(firstFile.mediaType)
              : undefined;
            const resolvedMedia =
              mediaId && mediaType?.startsWith("image/")
                ? resolveMedia({ id: mediaId })
                : undefined;
            return resolvedMedia ? (
              <MediaPreviewFrame
                aspectRatio="4:3"
                className="h-10 w-14 overflow-hidden rounded"
              >
                <MediaPreviewImage
                  src={resolvedMedia.src}
                  alt={stringValue(firstFile?.label) ?? rowId}
                  fit="cover"
                />
              </MediaPreviewFrame>
            ) : (
              <span className="text-sm text-muted-foreground">-</span>
            );
          }
          if (legacyDataExplorer && column.key === "size") {
            const formatted = formatLegacyFileSize(rawValue);
            return (
              <div
                className="w-full min-w-0 truncate whitespace-nowrap"
                title={formatted}
              >
                {formatted}
              </div>
            );
          }
          if (legacyDataExplorer && column.key === "status") {
            const status = stringValue(rawValue);
            if (!status) return "-";
            const statusLabel = (
              {
                published: "common.status.published",
                draft: "common.status.draft",
                archived: "common.status.archived",
              } as const
            )[status as "published" | "draft" | "archived"];
            const label = statusLabel ? translate(statusLabel) : status;
            return (
              <Badge
                variant={
                  status === "published"
                    ? "default"
                    : status === "archived"
                      ? "outline"
                      : "secondary"
                }
                className="min-w-0 max-w-full truncate"
                title={label}
              >
                {label}
              </Badge>
            );
          }
          if (
            legacyDataExplorer &&
            (column.key === "updatedTimestamp" ||
              column.key === "createdTimestamp")
          ) {
            const formatted = formatLegacyTableTimestamp(rawValue);
            return (
              <div
                className="w-full min-w-0 truncate whitespace-nowrap"
                title={formatted}
              >
                {formatted}
              </div>
            );
          }
          const content =
            typedCell?.valueType === 'copy' || typedCell?.valueType === 'localized-text' || typedCell?.valueType === 'identity' || typedCell?.valueType === 'badge' || typedCell?.valueType === 'stack' || typedCell?.valueType === 'metrics'
              ? <TypedDisplayCell valueType={typedCell.valueType} value={rawValue} copy={typedCell.copy} valueFormat={typedCell.valueFormat} translate={translate} /> :
            typedCell?.valueType === "message" ? renderTableMessageValue(rawValue, translate) :
            typedCell?.valueType === 'timestamp'
              ? renderDateTimeValue(rawValue, typeof rawValue === 'number' && rawValue < 1_000_000_000_000 ? 'seconds' : 'milliseconds', stringValue(typedCell?.locale))
              : governedReadValue(rawValue);
          if (
            typedCell &&
            typedCell.editable === true &&
            stringValue(typedCell.intentId)
          ) {
            return (
              <button
                type="button"
                className="min-w-0 rounded px-1 py-0.5 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                disabled={typedCell.disabled === true || row.disabled === true}
                onClick={(event) => {
                  event.stopPropagation();
                  emitIntent("capability.ontology-table.cell-intent", {
                    rowId,
                    columnId: column.key,
                    intentId: String(typedCell.intentId),
                  });
                }}
              >
                {content}
              </button>
            );
          }
          if (column.wrap === true) return <div className="w-full min-w-0 whitespace-normal break-words">{content}</div>;
          if (legacyDataExplorer && column.key === "name") {
            return (
              <div
                className="w-full min-w-0 truncate font-medium"
                title={typeof content === "string" ? content : undefined}
              >
                {content}
              </div>
            );
          }
          if (legacyDataExplorer && column.key === "type") {
            const typeText =
              typeof content === "string" || typeof content === "number"
                ? String(content)
                : "";
            return typeText ? (
              <Badge
                variant="secondary"
                className="min-w-0 max-w-full truncate"
                title={typeText}
              >
                {typeText}
              </Badge>
            ) : (
              "-"
            );
          }
          return content;
        }}
      />
    </DataExplorerViewShell>
  );
};

const LEGACY_CARD_GRID_ROW_HEIGHT = 4;
const LEGACY_CARD_GRID_GAP = 8;
const LEGACY_CARD_GRID_INITIAL_HEADER_HEIGHT = 140;

const legacyCardGridSpan = (height: number) =>
  Math.max(
    1,
    Math.ceil(
      (height + LEGACY_CARD_GRID_GAP) /
        (LEGACY_CARD_GRID_ROW_HEIGHT + LEGACY_CARD_GRID_GAP),
    ),
  );

const LegacyCardGridItem = ({ children }: { children: ReactNode }) => {
  const measuredRef = useRef<HTMLDivElement>(null);
  const [span, setSpan] = useState(() =>
    legacyCardGridSpan(LEGACY_CARD_GRID_INITIAL_HEADER_HEIGHT),
  );

  useEffect(() => {
    const element = measuredRef.current;
    if (!element) return;
    const updateSpan = () =>
      setSpan(legacyCardGridSpan(element.getBoundingClientRect().height));
    updateSpan();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateSpan);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div style={{ gridRowEnd: `span ${span}` }}>
      <div ref={measuredRef} className="w-full">
        {children}
      </div>
    </div>
  );
};

export const CardGridCapabilityRenderer: DesignCapabilityRenderer = ({
  node,
  model,
  childrenBySlot,
  translate,
  emitIntent,
  resolveMedia,
  loading,
  pendingActionInvocations,
}) => {
  const items = objectArray(model.items);
  const selected = items
    .filter((item) => item.selected === true)
    .flatMap((item) => (stringValue(item.id) ? [String(item.id)] : []));
  const pagination = cursorPagination(model.pagination);
  if (!items.length && !pagination?.hasMore)
    return <CapabilityStatus kind="empty" translate={translate} />;
  if (model.galleryFields !== undefined || model.gallerySettings !== undefined) return <GalleryFieldPresentation renderActionIcon={actionIcon} kind="card-grid" model={model} translate={translate} emitIntent={emitIntent} resolveMedia={resolveMedia} loading={loading} pendingActionInvocations={pendingActionInvocations} header={childrenBySlot.header} footer={<><CollectionPaginationFooter pagination={pagination} selectedCount={selected.length} loadMoreLabel={translate(DESIGN_CAPABILITY_I18N_KEYS.cardGrid.loadMore)} loadingMoreLabel={translate(DESIGN_CAPABILITY_I18N_KEYS.cardGrid.loadingMore)} onLoadMore={cursor => emitIntent("capability.card-grid.load-more", { cursor })} onPageSizeChange={pageSize => emitIntent("capability.card-grid.page-size-change", { pageSize })} />{childrenBySlot.footer}</>} />;
  const displayItems: Array<DataExplorerDisplayItem<JsonObject>> =
    items.flatMap((item) => {
      const id = stringValue(item.id);
      if (!id) return [];
      return [
        {
          id,
          title: copyValueOrFallback(item.title, item.titleKey, translate, id),
          description: stringValue(item.descriptionKey)
            ? translate(String(item.descriptionKey))
            : stringValue(item.description),
          selected: item.selected === true,
          disabled: item.disabled === true,
          metadata: objectArray(item.metadata).flatMap((entry) => {
            const entryId = stringValue(entry.id);
            return entryId
              ? [
                  {
                    id: entryId,
                    label: copyValueOrFallback(
                      entry.label,
                      entry.labelKey,
                      translate,
                      entryId,
                    ),
                    value: governedReadValue(entry.value),
                  },
                ]
              : [];
          }),
          source: item,
        },
      ];
    });
  const presentation = isJsonObject(model.presentation)
    ? model.presentation
    : undefined;
  const legacyDataExplorer = presentation?.kind === "legacy-data-explorer";
  const rowActions = objectArray(model.rowActions);
  if (legacyDataExplorer) {
    return (
      <DataExplorerViewShell
        className="col-span-full h-full"
        header={childrenBySlot.header}
        footer={
          <>
            <CollectionPaginationFooter
              pagination={pagination}
              selectedCount={selected.length}
              legacyDataExplorer
              loadMoreInContent
              translate={translate}
              loadMoreLabel={translate(
                DESIGN_CAPABILITY_I18N_KEYS.cardGrid.loadMore,
              )}
              loadingMoreLabel={translate(
                DESIGN_CAPABILITY_I18N_KEYS.cardGrid.loadingMore,
              )}
              onLoadMore={(cursor) =>
                emitIntent("capability.card-grid.load-more", { cursor })
              }
              onPageSizeChange={(pageSize) =>
                emitIntent("capability.card-grid.page-size-change", {
                  pageSize,
                })
              }
            />
            {childrenBySlot.footer}
          </>
        }
      >
        <div className="min-h-0 flex-1 overflow-auto p-4">
          <DataExplorerViewCollection<DataExplorerDisplayItem<JsonObject>>
            activeViewId="card-grid"
            items={displayItems}
            layout="grid"
            scrollable={false}
            wrapItems={false}
            selectedIds={selected}
            getItemId={(item) => item.id}
            classNames={{
              root: "min-h-0 flex-none overflow-visible",
              items: "grid overflow-visible",
            }}
            itemsStyle={{
              gap: 8,
              gridAutoFlow: "dense",
              gridAutoRows: 4,
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            }}
            renderItem={({ item, selected: isSelected }) => {
              const actions = collectionRowActions(
                rowActions.filter(
                  (action) => stringValue(action.id) !== "record-history",
                ),
                item.id,
                translate,
                emitIntent,
                "capability.card-grid.row-action",
                "capability.card-grid.delete",
              );
              const preview = isJsonObject(item.source?.fields) && isJsonObject(item.source.fields.preview) ? item.source.fields.preview : undefined;
              const reference = preview && isJsonObject(preview.reference) ? preview.reference : undefined;
              const cover = reference && stringValue(reference.id) ? resolveMedia?.({ id: String(reference.id) }) : undefined;
              return (
                <LegacyCardGridItem>
                  <DataExplorerDisplayCard
                    item={item}
                    children={null}
                    media={cover?.src ? <img src={cover.src} alt={String(item.title ?? '')} loading="lazy" className="h-auto w-full object-cover" referrerPolicy="no-referrer" /> : undefined}
                    appearance={{ preset: "soft" }}
                    className={`border-border/70 transition-shadow hover:shadow-sm${isSelected ? " ring-2 ring-primary" : ""}`}
                    classNames={{ header: "p-3 pb-2" }}
                    header={
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() =>
                              emitIntent(
                                "capability.card-grid.selection-change",
                                {
                                  itemIds: isSelected
                                    ? selected.filter(
                                        (value) => value !== item.id,
                                      )
                                    : Array.from(
                                        new Set([...selected, item.id]),
                                      ),
                                },
                              )
                            }
                            onClick={(event) => event.stopPropagation()}
                            aria-label={translate(
                              DESIGN_CAPABILITY_I18N_KEYS.cardGrid.select,
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <span
                              className="inline-flex min-w-0 max-w-full items-baseline text-sm font-semibold leading-none"
                              title={String(item.title ?? "")}
                              aria-label={String(item.title ?? "")}
                              data-content-safe-text="filename"
                            >
                              <span className="min-w-0 truncate">
                                {item.title}
                              </span>
                            </span>
                          </div>
                        </div>
                        <DataExplorerDisplayActionMenu
                          item={item}
                          label={translate("common.actions.actions")}
                          triggerLabel={translate("common.actions.actions")}
                          actions={actions}
                          appearance={{ preset: "soft" }}
                          className="h-7 w-7 rounded-full text-foreground shadow-sm backdrop-blur-sm"
                        />
                      </div>
                    }
                  />
                </LegacyCardGridItem>
              );
            }}
          />
          {pagination?.mode !== 'window' && pagination?.hasMore ? <CursorInfiniteScroll pagination={pagination} label={translate(DESIGN_CAPABILITY_I18N_KEYS.cardGrid.loadMore)} loadingLabel={translate(DESIGN_CAPABILITY_I18N_KEYS.cardGrid.loadingMore)} onLoadMore={cursor => emitIntent('capability.card-grid.load-more', { cursor })} /> : null}
          {pagination?.hasMore === false && displayItems.length > 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {translate("components.data.card-view.all-loaded", {
                total:
                  pagination.total ??
                  pagination.loadedEnd ??
                  pagination.returned ??
                  displayItems.length,
              })}
            </div>
          ) : null}
        </div>
      </DataExplorerViewShell>
    );
  }
  return (
    <DataExplorerViewShell
      className="col-span-full"
      header={childrenBySlot.header}
      footer={
        <BaseStack gap="var(--global-spacing-sm, 0.5rem)">
          <CollectionPaginationFooter
            pagination={pagination}
            selectedCount={selected.length}
            loadMoreLabel={translate(
              DESIGN_CAPABILITY_I18N_KEYS.cardGrid.loadMore,
            )}
            loadingMoreLabel={translate(
              DESIGN_CAPABILITY_I18N_KEYS.cardGrid.loadingMore,
            )}
            onLoadMore={(cursor) =>
              emitIntent("capability.card-grid.load-more", { cursor })
            }
            onPageSizeChange={(pageSize) =>
              emitIntent("capability.card-grid.page-size-change", { pageSize })
            }
          />
          {childrenBySlot.footer}
        </BaseStack>
      }
    >
      <DataExplorerViewCollection<DataExplorerDisplayItem<JsonObject>>
        activeViewId="card-grid"
        items={displayItems}
        layout="grid"
        selectedIds={selected}
        getItemId={(item) => item.id}
        classNames={{ items: "p-4" }}
        itemsStyle={{
          gridTemplateColumns: `repeat(${node.layout.columns ?? 3}, minmax(0, 1fr))`,
        }}
        renderItem={({ item, selected: isSelected }) => (
          <DataExplorerRecordCard
            item={item}
            selected={isSelected}
            disabled={item.disabled}
            selectLabel={translate(DESIGN_CAPABILITY_I18N_KEYS.cardGrid.select)}
            unselectLabel={translate(
              DESIGN_CAPABILITY_I18N_KEYS.cardGrid.select,
            )}
            footer={
              item.description || item.metadata?.length ? (
                <BaseStack gap="var(--global-spacing-sm, 0.5rem)">
                  {item.description}
                  <BaseDescriptionList
                    items={(item.metadata ?? []).map((entry) => ({
                      term: entry.label,
                      value: entry.value,
                    }))}
                  />
                </BaseStack>
              ) : undefined
            }
            onClick={() =>
              emitIntent("capability.card-grid.activate", {
                itemId: item.id,
              })
            }
            onToggleSelection={() =>
              emitIntent("capability.card-grid.selection-change", {
                itemIds: isSelected
                  ? selected.filter((value) => value !== item.id)
                  : Array.from(new Set([...selected, item.id])),
              })
            }
          />
        )}
      />
    </DataExplorerViewShell>
  );
};

export const PaginationCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  translate,
  emitIntent,
}) => {
  if (model.mode === "cursor") {
    return (
      <CursorLoadMore
        pagination={{
          mode: "append",
          hasMore: model.hasNext === true,
          nextCursor: stringValue(model.nextCursor),
          loadingMore: model.loading === true,
          errorCode: stringValue(model.errorCode),
        }}
        label={translate(DESIGN_CAPABILITY_I18N_KEYS.pagination.loadMore)}
        loadingLabel={translate(
          DESIGN_CAPABILITY_I18N_KEYS.pagination.loadingMore,
        )}
        onLoadMore={(cursor) =>
          emitIntent("capability.pagination.load-more", { cursor })
        }
      />
    );
  }
  return (
    <BasePagination
      page={typeof model.page === "number" ? model.page : 1}
      pageSize={typeof model.pageSize === "number" ? model.pageSize : 20}
      total={typeof model.total === "number" ? model.total : 0}
      pageCount={
        typeof model.pageCount === "number" && model.pageCount > 0
          ? model.pageCount
          : undefined
      }
      hasNext={model.hasNext === true}
      hasPrevious={model.hasPrevious === true}
      mode="pages"
      label={translate(
        stringValue(model.labelKey) ??
          DESIGN_CAPABILITY_I18N_KEYS.pagination.label,
      )}
      onPageChange={(page) =>
        emitIntent("capability.pagination.page-change", { page })
      }
    />
  );
};

const RecordDetailViewedIntent = ({
  itemId,
  emitIntent,
}: {
  itemId: string;
  emitIntent: DesignCapabilityRendererContext["emitIntent"];
}) => {
  const trackedItemRef = useRef<string>();
  useEffect(() => {
    if (trackedItemRef.current === itemId) return;
    trackedItemRef.current = itemId;
    emitIntent("capability.record-detail.viewed", { itemId });
  }, [emitIntent, itemId]);
  return null;
};

export const RecordDetailCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  childrenBySlot,
  translate,
  emitIntent,
}) => {
  const presentation = isJsonObject(model.presentation)
    ? model.presentation
    : undefined;
  const impressionRecord = isJsonObject(model.dataExplorerRecord)
    ? model.dataExplorerRecord
    : {};
  // Dismissal is available for a standalone detail surface unless the host
  // explicitly owns closing it.
  const closeEnabled = model.closeEnabled !== false;
  if (presentation?.kind === "hidden") return null;
  if (presentation?.kind === "data-explorer-sidebar") {
    const record = impressionRecord;
    const id = stringValue(record.id) ?? "-";
    const recordTitle =
      stringValue(record.title) ?? stringValue(model.title) ?? id;
    const description =
      stringValue(record.description) ?? stringValue(model.description);
    const recordType = stringValue(record.type);
    const status = stringValue(record.status);
    const previewSource = stringValue(record.previewSource);
    const previewAlt = stringValue(record.previewAlt) ?? recordTitle;
    const previewKind = stringValue(record.previewKind);
    const tagLabels = stringArray(record.tagLabels);
    const properties = isJsonObject(record.properties) ? record.properties : {};
    const metadata = isJsonObject(record.metadata) ? record.metadata : {};
    const navigation = isJsonObject(model.navigation)
      ? model.navigation
      : undefined;
    const previewDialogId = `monkeys-record-preview-${id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
    const fieldMap = new Map(
      objectArray(model.fields).flatMap((field) => {
        const fieldId = stringValue(field.id);
        return fieldId ? [[fieldId, field] as const] : [];
      }),
    );
    const metric = (value: JsonValue | undefined) =>
      typeof value === "number" && Number.isSafeInteger(value) && value >= 0
        ? value
        : 0;
    const timestamp = (value: JsonValue | undefined) =>
      value === undefined || value === null || value === ""
        ? undefined
        : formatDetailTimestamp(value);
    const detailValue = (fieldId: string) =>
      governedReadValue(fieldMap.get(fieldId)?.value);
    const featureFields = [...fieldMap.entries()].filter(
      ([fieldId, field]) =>
        fieldId.startsWith("feature:") && hasGovernedReadValue(field.value),
    );
    const statusTone =
      status === "published"
        ? "border-success/25 bg-success/10 text-success dark:border-success/25 dark:bg-success/12 dark:text-success"
        : status === "draft"
          ? "border-warning/30 bg-warning/10 text-warning dark:border-warning/30 dark:bg-warning/12 dark:text-warning"
          : "border-border/70 bg-muted/70 text-muted-foreground";
    const closeLabel = translate(
      stringValue(model.closeAriaLabelKey) ??
        stringValue(model.closeLabelKey) ??
        DESIGN_CAPABILITY_I18N_KEYS.recordDetail.close,
    );
    const previousLabel = translate(
      stringValue(navigation?.previousLabelKey) ??
        DESIGN_CAPABILITY_I18N_KEYS.recordDetail.previous,
    );
    const nextLabel = translate(
      stringValue(navigation?.nextLabelKey) ??
        DESIGN_CAPABILITY_I18N_KEYS.recordDetail.next,
    );
    const previousId = stringValue(navigation?.previousId);
    const nextId = stringValue(navigation?.nextId);
    const downloadPreview = async () => {
      if (!previewSource) return;
      try {
        const response = await fetch(previewSource);
        if (!response.ok) throw new Error("preview download failed");
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        try {
          const anchor = document.createElement("a");
          anchor.href = objectUrl;
          anchor.download = recordTitle;
          anchor.target = "_self";
          anchor.rel = "noopener noreferrer";
          anchor.style.display = "none";
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
        } finally {
          window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
        }
        emitIntent("capability.record-detail.preview-download", {
          itemId: id,
          source: previewSource,
        });
      } catch {
        const anchor = document.createElement("a");
        anchor.href = previewSource;
        anchor.download = recordTitle;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        anchor.click();
        emitIntent("capability.record-detail.preview-download", {
          itemId: id,
          source: previewSource,
        });
      }
    };
    const compactField = (
      label: ReactNode,
      icon: ReactNode,
      value: ReactNode,
      key?: string,
    ) => (
      <div key={key} className="min-w-0 space-y-1.5">
        <div className="inline-flex min-w-0 items-center gap-1.5 text-[11px] font-semibold text-foreground">
          {icon}
          <span className="min-w-0 truncate">{label}</span>
        </div>
        <div className="flex min-h-10 min-w-0 items-center rounded-md border border-border/55 bg-muted/25 px-3 py-2 text-xs leading-5 text-foreground shadow-[inset_0_1px_0_hsl(var(--background)/0.8)] dark:border-border/45 dark:bg-white/[0.035]">
          {value}
        </div>
      </div>
    );
    const sectionTitle = (label: ReactNode) => (
      <div className="inline-flex max-w-full items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-2.5 py-1.5 text-xs font-medium text-foreground dark:border-border/45 dark:bg-muted/10">
        {label}
      </div>
    );
    return (
      <div
        className="relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-background shadow-none"
        data-data-explorer-record-detail
      >
        <RecordDetailViewedIntent itemId={id} emitIntent={emitIntent} />
        <div className="shrink-0 border-b border-border/40 px-3 py-3 dark:border-border/30">
          <div className="min-w-0 space-y-3">
            <div className="flex h-8 items-center justify-between gap-3">
              <div className="inline-flex h-7 items-center overflow-hidden rounded-md border border-border/60 bg-muted/15 text-muted-foreground shadow-[inset_0_1px_0_hsl(var(--background)/0.75)]">
                <button
                  type="button"
                  disabled={!previousId}
                  aria-label={previousLabel}
                  onClick={() =>
                    previousId &&
                    emitIntent("capability.record-detail.navigate", {
                      direction: "previous",
                      targetId: previousId,
                    })
                  }
                  className="flex h-7 w-7 items-center justify-center transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <span className="h-4 w-px bg-border/60" aria-hidden="true" />
                <button
                  type="button"
                  disabled={!nextId}
                  aria-label={nextLabel}
                  onClick={() =>
                    nextId &&
                    emitIntent("capability.record-detail.navigate", {
                      direction: "next",
                      targetId: nextId,
                    })
                  }
                  className="flex h-7 w-7 items-center justify-center transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </div>
              {closeEnabled ? (
                <button
                  type="button"
                  aria-label={closeLabel}
                  onClick={() => emitIntent("capability.record-detail.close", {})}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <PanelRightClose className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            <div className="space-y-2">
              <div className="break-words text-sm font-semibold leading-5 text-foreground">
                {recordTitle}
              </div>
              <div className="break-all font-mono text-[11px] leading-4 text-muted-foreground">
                {translate("common.table.id")}: {id}
              </div>
            </div>
          </div>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3 py-4">
          <div className="rounded-md border border-border/55 bg-muted/20 p-2 dark:border-border/45 dark:bg-muted/10">
            <div className="group relative flex aspect-[4/3] max-h-[300px] items-center justify-center overflow-hidden rounded-md bg-background">
              {previewSource ? (
                previewKind === "text" ? (
                  <pre className="h-full w-full overflow-auto whitespace-pre-wrap break-all p-4 text-xs leading-5 text-foreground">
                    {previewSource}
                  </pre>
                ) : (
                  <img
                    src={previewSource}
                    alt={previewAlt}
                    className="h-full w-full object-contain"
                  />
                )
              ) : (
                <div className="text-center text-muted-foreground">
                  <FileText className="mx-auto mb-2 h-8 w-8 opacity-60" />
                  <span className="text-xs">
                    {translate("common.status.no-preview")}
                  </span>
                </div>
              )}
              {previewSource ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 transition duration-150 group-focus-within:bg-black/25 group-focus-within:opacity-100 group-hover:bg-black/25 group-hover:opacity-100">
                  <button
                    type="button"
                    aria-label={translate(
                      "components.data-v1.detail.enlarge-preview",
                    )}
                    title={translate(
                      "components.data-v1.detail.enlarge-preview",
                    )}
                    onClick={() =>
                      (
                        document.getElementById(
                          previewDialogId,
                        ) as HTMLDialogElement | null
                      )?.showModal()
                    }
                    className="pointer-events-auto inline-flex h-9 items-center gap-2 rounded-md border border-white/25 bg-background/95 px-3 text-xs font-medium text-foreground shadow-lg backdrop-blur transition-colors hover:bg-background"
                  >
                    <Maximize2 className="h-3.5 w-3.5" />
                    <span>
                      {translate("components.data-v1.detail.enlarge-preview")}
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label={translate("common.actions.download")}
                    title={translate("common.actions.download")}
                    onClick={() => void downloadPreview()}
                    className="pointer-events-auto inline-flex h-9 items-center gap-2 rounded-md border border-white/25 bg-background/95 px-3 text-xs font-medium text-foreground shadow-lg backdrop-blur transition-colors hover:bg-background disabled:pointer-events-none disabled:opacity-60"
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span>{translate("common.actions.download")}</span>
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          <section className="grid min-w-0 grid-cols-2 gap-3">
            {recordType
              ? compactField(
                  translate("common.table.type"),
                  <Tag className="h-4 w-4" />,
                  <Badge
                    variant="secondary"
                    className="rounded-full px-3 py-1 text-sm font-medium"
                  >
                    {recordType}
                  </Badge>,
                )
              : null}
            {status
              ? compactField(
                  translate("common.table.status"),
                  <span className="relative inline-flex h-3.5 w-3.5 items-center justify-center">
                    <span className="absolute inset-0 rounded-full border border-primary/25 bg-primary/10" />
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  </span>,
                  <Badge
                    className={`cursor-default select-none rounded-full border px-3 py-1 text-sm font-medium ${statusTone}`}
                  >
                    {status}
                  </Badge>,
                )
              : null}
            {compactField(
              translate("components.data-v1.detail.view-count"),
              <Eye className="h-4 w-4" />,
              metric(record.viewCount),
            )}
            {compactField(
              translate("components.data-v1.detail.download-count"),
              <Download className="h-4 w-4" />,
              metric(record.downloadCount),
            )}
          </section>
          <section className="min-w-0 space-y-3">
            {sectionTitle(translate("components.data.detail-dialog.labels"))}
            {compactField(
              translate("components.data.detail-dialog.tags"),
              <Tag className="h-4 w-4" />,
              tagLabels.length ? (
                <div className="flex flex-wrap gap-2">
                  {tagLabels.map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="select-none rounded-full px-3 py-1 text-sm font-medium"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">
                  {translate("components.data.detail-dialog.no-labels")}
                </span>
              ),
            )}
          </section>
          {timestamp(record.createdAt) || timestamp(record.updatedAt) ? (
            <section className="min-w-0 space-y-3">
              {sectionTitle(translate("components.data-v1.detail.time-info"))}
              <div className="grid gap-3">
                {timestamp(record.createdAt)
                  ? compactField(
                      translate("common.table.created-at"),
                      <Calendar className="h-4 w-4" />,
                      timestamp(record.createdAt),
                    )
                  : null}
                {timestamp(record.updatedAt)
                  ? compactField(
                      translate("common.table.updated-at"),
                      <Calendar className="h-4 w-4" />,
                      timestamp(record.updatedAt),
                    )
                  : null}
                {stringValue(record.creatorId)
                  ? compactField(
                      translate("components.data-v1.detail.creator"),
                      <span className="relative inline-flex h-3.5 w-3.5 items-center justify-center">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                      </span>,
                      <span className="break-all font-mono text-[13px]">
                        {String(record.creatorId)}
                      </span>,
                    )
                  : null}
              </div>
            </section>
          ) : null}
          {description ? (
            <section className="min-w-0 space-y-3">
              {sectionTitle(translate("common.table.description"))}
              <div className="grid gap-3">
                {compactField(
                  translate("common.table.description"),
                  <FileText className="h-4 w-4" />,
                  <span className="whitespace-pre-wrap break-words">
                    {description}
                  </span>,
                )}
              </div>
            </section>
          ) : null}
          {featureFields.length ? (
            <section className="min-w-0 space-y-3">
              {sectionTitle(
                translate(
                  "components.dataBrowser.detailPanel.sections.customProperties",
                ),
              )}
              <div className="grid gap-3">
                {featureFields.map(([fieldId, field]) =>
                  compactField(
                    copyValueOrFallback(
                      field.label,
                      field.labelKey,
                      translate,
                      fieldId.slice(8),
                    ),
                    <span className="relative inline-flex h-3.5 w-3.5 items-center justify-center">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    </span>,
                    detailValue(fieldId),
                    fieldId,
                  ),
                )}
              </div>
            </section>
          ) : null}
          {Object.keys(properties).length ? (
            <section className="min-w-0 space-y-3">
              {sectionTitle(
                translate(
                  "components.dataBrowser.detailPanel.sections.customProperties",
                ),
              )}
              <div className="grid gap-3">
                {Object.entries(properties)
                  .filter(
                    ([propertyId]) => !fieldMap.has(`feature:${propertyId}`),
                  )
                  .map(([propertyId, value]) =>
                    compactField(
                      propertyId,
                      <span className="relative inline-flex h-3.5 w-3.5 items-center justify-center">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                      </span>,
                      governedReadValue(value as JsonValue),
                      propertyId,
                    ),
                  )}
              </div>
            </section>
          ) : null}
          {Object.keys(metadata).length ? (
            <section className="min-w-0 space-y-3">
              {sectionTitle(
                translate("components.data-v1.detail.full-metadata"),
              )}
              <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-md border border-border/55 bg-muted/25 p-3 font-mono text-xs leading-5 text-foreground">
                {JSON.stringify(metadata, null, 2)}
              </pre>
            </section>
          ) : null}
        </div>
        <dialog
          id={previewDialogId}
          aria-label={recordTitle}
          className="fixed left-1/2 top-1/2 m-0 h-[min(900px,88vh)] w-[min(1180px,92vw)] max-w-[min(1180px,92vw)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-border bg-background p-3 text-foreground shadow-[0_24px_70px_rgba(15,23,42,0.26)] backdrop:bg-black/55 dark:shadow-[0_24px_70px_rgba(0,0,0,0.55)] dark:backdrop:bg-black/75"
        >
          <div className="sr-only">
            {translate("components.data-v1.detail.preview-dialog-description")}
          </div>
          <form method="dialog" className="absolute right-4 top-4 z-10">
            <button
              type="submit"
              aria-label={translate("common.actions.close")}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-background/90 text-muted-foreground shadow hover:bg-accent hover:text-foreground"
            >
              <PanelRightClose className="h-4 w-4" />
            </button>
          </form>
          <div className="h-full min-h-0 overflow-auto rounded-xl bg-background">
            {previewKind === "text" ? (
              <pre className="whitespace-pre-wrap break-all p-5 text-sm leading-6 text-foreground">
                {previewSource}
              </pre>
            ) : (
              <img
                src={previewSource}
                alt={previewAlt}
                className="h-full w-full object-contain"
              />
            )}
          </div>
        </dialog>
      </div>
    );
  }
  const title = copyValueOrFallback(
    model.title,
    model.titleKey,
    translate,
    undefined,
  );
  const detailField = (field: JsonObject) => {
    const id = stringValue(field.id);
    if (!id) return undefined;
    const value = stringValue(field.valueLabelKey)
      ? translate(String(field.valueLabelKey))
      : field.valueType === 'timestamp' ? renderDateTimeValue(field.value, 'milliseconds', stringValue(field.locale)) : governedReadValue(field.value);
    const actionId = stringValue(field.actionId);
    return {
      id,
      label: <RecordEditorReadOnlyFieldLabel field={field} label={copyValueOrFallback(field.label, field.labelKey, translate, id)} translate={translate} />,
      value: isJsonObject(field.copy) && typeof field.value === "string" ? (
        <RecordDetailCopyValue key={`${id}:${field.value}`} value={field.value} copy={field.copy} translate={translate} />
      ) : actionId ? (
        <button
          type="button"
          className="rounded px-1 py-0.5 text-left text-primary hover:bg-accent"
          onClick={() =>
            emitIntent("capability.record-detail.action", {
              actionId,
              itemId: id,
            })
          }
        >
          {value}
        </button>
      ) : (
        value
      ),
    };
  };
  const declaredSections = objectArray(model.sections);
  const attachments = objectArray(model.attachments);
  const navigation = isJsonObject(model.navigation)
    ? model.navigation
    : undefined;
  const actions = objectArray(model.actions);
  const preview = isJsonObject(model.preview) ? model.preview : undefined;
  const sections = [
    ...(declaredSections.length
      ? declaredSections.flatMap((section) => {
          const id = stringValue(section.id);
          if (!id) return [];
          return [
            {
              id,
              title: copyValueOrFallback(
                section.label,
                section.labelKey,
                translate,
                undefined,
              ),
              fields: objectArray(section.fields).flatMap((field) => {
                const mapped = detailField(field);
                return mapped ? [mapped] : [];
              }),
            },
          ];
        })
      : [
          {
            id: "record-fields",
            fields: objectArray(model.fields).flatMap((field) => {
              const mapped = detailField(field);
              return mapped ? [mapped] : [];
            }),
          },
        ]),
    ...(attachments.length
      ? [
          {
            id: "attachments",
            title: translate(
              DESIGN_CAPABILITY_I18N_KEYS.recordDetail.attachments,
            ),
            children: (
              <div className="space-y-2">
                {attachments.map((attachment) => {
                  const id = stringValue(attachment.id);
                  if (!id) return null;
                  const actionId = stringValue(attachment.actionId);
                  return (
                    <DataExplorerButton
                      key={id}
                      label={copyValueOrFallback(
                        attachment.label,
                        attachment.labelKey,
                        translate,
                        id,
                      )}
                      disabled={attachment.disabled === true || !actionId}
                      onClick={() =>
                        actionId &&
                        emitIntent("capability.record-detail.action", {
                          actionId,
                          itemId: id,
                        })
                      }
                    />
                  );
                })}
              </div>
            ),
          },
        ]
      : []),
  ];
  return (
    <DataExplorerDetailShell
      title={title}
      subtitle={
        stringValue(model.descriptionKey)
          ? translate(String(model.descriptionKey))
          : stringValue(model.description)
      }
      preview={
        childrenBySlot.header ??
        (preview ? (
          <div className="flex min-h-40 flex-col items-center justify-center gap-2 rounded-lg bg-muted/40 p-6 text-center">
            <DataKindIcon kind={stringValue(preview.kind)} />
            <span className="text-sm font-medium">
              {copyValueOrFallback(
                isJsonObject(preview.reference)
                  ? preview.reference.label
                  : undefined,
                preview.altKey,
                translate,
                isJsonObject(preview.reference)
                  ? stringValue(preview.reference.id)
                  : undefined,
              )}
            </span>
          </div>
        ) : null)
      }
      previewVisible={Boolean(childrenBySlot.header || preview)}
      closeLabel={translate(
        stringValue(model.closeAriaLabelKey) ??
          stringValue(model.closeLabelKey) ??
          DESIGN_CAPABILITY_I18N_KEYS.recordDetail.close,
      )}
      closeVisible={closeEnabled}
      onClose={closeEnabled ? () => emitIntent("capability.record-detail.close", {}) : undefined}
      actions={
        <div className="flex flex-nowrap items-center gap-2">
          {model.editable !== false || model.editDisabled === true ? (
            <DataExplorerButton
              label={translate(
                stringValue(model.editLabelKey) ??
                  DESIGN_CAPABILITY_I18N_KEYS.recordDetail.edit,
              )}
              ariaLabel={
                stringValue(model.editAriaLabelKey)
                  ? translate(String(model.editAriaLabelKey))
                  : undefined
              }
              disabled={model.editable === false || model.editDisabled === true}
              onClick={() => emitIntent("capability.record-detail.edit", {})}
            />
          ) : null}
          {actions.map((action) => {
            const actionId = stringValue(action.id);
            if (!actionId) return null;
            return (
              <DataExplorerButton
                key={actionId}
                icon={actionIcon(action)}
                label={copyValueOrFallback(
                  action.label,
                  action.labelKey,
                  translate,
                  actionId,
                )}
                ariaLabel={actionAriaLabel(action, translate)}
                tone={actionTone(action)}
                disabled={action.disabled === true}
                loading={action.loading === true}
                onClick={() =>
                  emitIntent("capability.record-detail.action", { actionId })
                }
              />
            );
          })}
          {childrenBySlot.footer}
        </div>
      }
      navigation={
        navigation &&
        (stringValue(navigation.previousId) || stringValue(navigation.nextId))
          ? {
              visible: true,
              index: 1,
              count: 2,
              previousLabel: translate(
                stringValue(navigation.previousLabelKey) ??
                  DESIGN_CAPABILITY_I18N_KEYS.recordDetail.previous,
              ),
              nextLabel: translate(
                stringValue(navigation.nextLabelKey) ??
                  DESIGN_CAPABILITY_I18N_KEYS.recordDetail.next,
              ),
              onPrevious: stringValue(navigation.previousId)
                ? () =>
                    emitIntent("capability.record-detail.navigate", {
                      direction: "previous",
                      targetId: navigation.previousId,
                    })
                : undefined,
              onNext: stringValue(navigation.nextId)
                ? () =>
                    emitIntent("capability.record-detail.navigate", {
                      direction: "next",
                      targetId: navigation.nextId,
                    })
                : undefined,
            }
          : undefined
      }
      sections={sections}
      classNames={{
        header: "min-w-0",
        headerMain: "min-w-0 flex-1",
        headerActions: "shrink-0 flex-nowrap",
      }}
    />
  );
};

function resolveRecordEditorFieldDescription(field: JsonObject, translate: CapabilityTranslate, locale: string): string | undefined {
  const keys = ['description', 'descriptionKey', 'descriptionI18n'].filter(key => field[key] !== undefined);
  if (keys.length > 1) throw new TypeError('Editor field descriptions are mutually exclusive.');
  if (field.descriptionI18n !== undefined) return resolveI18nDisplayText(field.descriptionI18n, locale);
  if (field.descriptionKey !== undefined) { if (typeof field.descriptionKey !== 'string' || !field.descriptionKey) throw new TypeError('Invalid editor field description key.'); return translate(field.descriptionKey); }
  if (field.description !== undefined && typeof field.description !== 'string') throw new TypeError('Invalid editor field description.');
  return field.description;
}
const RecordEditorReadOnlyFieldLabel = ({ field, label, translate }: { field: JsonObject; label: ReactNode; translate: CapabilityTranslate }) => {
  const { locale } = useMonkeysLocale();
  const description = resolveRecordEditorFieldDescription(field, translate, locale);
  return description === undefined ? <>{label}</> : <span className="flex flex-col gap-1"><span>{label}</span><span className="text-sm font-normal text-muted-foreground" data-editor-field-description>{description}</span></span>;
};

const RecordEditorSelectionField = ({
  field,
  fieldId,
  domId,
  label,
  description,
  disabled,
  translate,
  emitIntent,
}: {
  field: JsonObject;
  fieldId: string;
  domId: string;
  label: ReactNode;
  description?: string;
  disabled: boolean;
  translate: CapabilityTranslate;
  emitIntent: DesignCapabilityRendererContext["emitIntent"];
}) => {
  const kind = governedSelectionKind(field.kind);
  if (!kind) return null;
  const selected = governedSelectionItems(field.value, kind);
  const options = mergeGovernedOptions(selected, objectArray(field.options));
  const selectedIds = selected.flatMap((item) => {
    const id = optionIdentity(item);
    return id ? [id] : [];
  });
  const pagination = cursorPagination(field.optionsPagination);
  const externalError = stringValue(field.errorCode);
  return (
    <BaseField
      label={label}
      description={description}
      required={field.required === true}
      error={externalError ? translate(externalError) : undefined}
      disabled={disabled}
      data-editor-field-kind={String(field.kind)}
    >
      <BaseStack gap="var(--global-spacing-sm, 0.5rem)">
        <BaseMultiSelect
          id={domId}
          aria-label={typeof label === "string" ? label : fieldId}
          options={options.flatMap((option) => {
            const id = optionIdentity(option);
            if (!id) return [];
            const optionLabel = stringValue(option.label) ?? id;
            return [
              {
                value: id,
                label: optionLabel,
                textValue: optionLabel,
                disabled: option.disabled === true,
              },
            ];
          })}
          value={selectedIds}
          required={field.required === true}
          disabled={disabled}
          emptyLabel={translate(
            DESIGN_CAPABILITY_I18N_KEYS.recordEditor.optionsEmpty,
          )}
          onValueChange={(ids) =>
            emitIntent("capability.record-editor.change", {
              fieldId,
              value: editorSelectionValue(kind, ids, options),
            })
          }
        />
        <div data-options-cursor={pagination?.nextCursor}>
          <CursorLoadMore
            pagination={pagination}
            label={translate(
              DESIGN_CAPABILITY_I18N_KEYS.recordEditor.optionsLoadMore,
            )}
            loadingLabel={translate(
              DESIGN_CAPABILITY_I18N_KEYS.recordEditor.optionsLoadingMore,
            )}
            onLoadMore={(cursor) =>
              emitIntent("capability.record-editor.options-load-more", {
                fieldId,
                cursor,
              })
            }
          />
        </div>
        {pagination?.errorCode ? (
          <CapabilityStatus
            kind="error"
            code={pagination.errorCode}
            translate={translate}
          />
        ) : null}
      </BaseStack>
    </BaseField>
  );
};

const jsonEditorValue = (value: JsonValue | undefined) =>
  isJsonObject(value) && value.kind === "json"
    ? JSON.stringify(value.value ?? null, null, 2)
    : "null";

const RecordEditorJsonField = ({
  field,
  fieldId,
  domId,
  label,
  description,
  disabled,
  translate,
  emitIntent,
  onValidityChange,
}: {
  field: JsonObject;
  fieldId: string;
  domId: string;
  label: ReactNode;
  description?: string;
  disabled: boolean;
  translate: CapabilityTranslate;
  emitIntent: DesignCapabilityRendererContext["emitIntent"];
  onValidityChange: (fieldId: string, valid: boolean) => void;
}) => {
  const modelText = jsonEditorValue(field.value);
  const [text, setText] = useState(modelText);
  const [invalid, setInvalid] = useState(false);
  useEffect(() => {
    setText(modelText);
    setInvalid(false);
    onValidityChange(fieldId, true);
  }, [modelText, fieldId, onValidityChange]);
  useEffect(
    () => () => onValidityChange(fieldId, true),
    [fieldId, onValidityChange],
  );
  const externalError = stringValue(field.errorCode);
  return (
    <BaseField
      label={label}
      description={description}
      htmlFor={domId}
      required={field.required === true}
      error={
        invalid
          ? translate(DESIGN_CAPABILITY_I18N_KEYS.recordEditor.jsonInvalid)
          : externalError
            ? translate(externalError)
            : undefined
      }
      disabled={disabled}
      data-editor-field-kind="json"
    >
      <BaseTextarea
        id={domId}
        aria-label={typeof label === "string" ? label : fieldId}
        value={text}
        disabled={disabled}
        required={field.required === true}
        invalid={invalid}
        onChange={(event) => {
          const nextText = event.currentTarget.value;
          setText(nextText);
          try {
            const value = JSON.parse(nextText) as JsonValue;
            setInvalid(false);
            onValidityChange(fieldId, true);
            emitIntent("capability.record-editor.change", {
              fieldId,
              value: {
                kind: "json",
                dataType:
                  isJsonObject(field.value) && stringValue(field.value.dataType)
                    ? String(field.value.dataType)
                    : "json",
                value,
              },
            });
          } catch {
            setInvalid(true);
            onValidityChange(fieldId, false);
          }
        }}
      />
    </BaseField>
  );
};

const RecordEditorDateControl = ({ field, fieldId, domId, label, description, disabled, translate, emitIntent, onValidityChange }: {
  field: JsonObject; fieldId: string; domId: string; label: string; description?: string; disabled: boolean;
  translate: DesignCapabilityRendererContext['translate']; emitIntent: DesignCapabilityRendererContext['emitIntent'];
  onValidityChange: (id: string, valid: boolean) => void;
}) => {
  const definition: RecordEditorDateField = { kind: field.kind === 'date' ? 'date' : 'datetime-local', value: field.value,
    ...(typeof field.valueFormat === 'string' ? { valueFormat: field.valueFormat as RecordEditorDateField['valueFormat'] } : {}) };
  const read = () => {
    let text = '';
    try {
      text = toControlValue(definition);
      const comparable = (value: string) => definition.kind === 'date' ? value : (value.length === 16 ? value + ':00.000' : value.length === 19 ? value + '.000' : value.padEnd(23, '0'));
      for (const limit of ['min', 'max'] as const) {
        const bound = field[limit];
        if (!text || typeof bound !== 'string' || !bound) continue;
        fromControlValue(definition, bound);
        if (limit === 'min' ? comparable(text) < comparable(bound) : comparable(text) > comparable(bound)) throw new RangeError();
      }
      return { text, invalid: false };
    } catch { return { text, invalid: true }; }
  };
  const [control, setControl] = useState(read);
  useEffect(() => { const next = read(); setControl(next); onValidityChange(fieldId, !next.invalid); }, [field.value, field.kind, field.valueFormat, field.min, field.max, fieldId, onValidityChange]);
  useEffect(() => () => onValidityChange(fieldId, true), [fieldId, onValidityChange]);
  return <BaseField htmlFor={domId} label={label} description={description} required={field.required === true}
    error={control.invalid || field.errorCode ? translate(typeof field.errorCode === 'string' ? field.errorCode : 'monkeys.design.capability.recordEditor.date.invalid') : undefined}>
    <BaseInput id={domId} type={definition.kind} value={control.text}
    min={typeof field.min === 'string' ? field.min : undefined} max={typeof field.max === 'string' ? field.max : undefined}
    step={definition.kind === 'datetime-local' ? 'any' : undefined} disabled={disabled} required={field.required === true} aria-invalid={control.invalid || Boolean(field.errorCode)}
    onChange={(event) => {
      const text = event.currentTarget.value;
      try {
        if (event.currentTarget.validity.badInput || event.currentTarget.validity.rangeUnderflow || event.currentTarget.validity.rangeOverflow) throw new RangeError();
        const value = fromControlValue(definition, text);
        setControl({ text, invalid: false }); onValidityChange(fieldId, true);
        emitIntent('capability.record-editor.change', { fieldId, value });
      } catch { setControl({ text, invalid: true }); onValidityChange(fieldId, false); }
    }} />
  </BaseField>;
};

function recordEditorArrayBounds(field: JsonObject) {
  if (field.reorderable !== undefined && typeof field.reorderable !== 'boolean') throw new TypeError('Invalid array reorderable flag.');
  const min = field.minItems === undefined ? 0 : field.minItems;
  const max = field.maxItems === undefined ? 1000 : field.maxItems;
  if (typeof min !== 'number' || typeof max !== 'number' || !Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max > 1000 || min > max) throw new TypeError('Invalid record editor array bounds.');
  return { min, max };
}
function recordEditorArraysInvalid(fields: JsonObject[]): boolean {
  return fields.some(field => {
    if (field.hidden === true || field.disabled === true) return false;
    if (field.kind === 'object') return recordEditorArraysInvalid(editorObjects(field.children));
    if (field.kind !== 'array') {
      if (field.minItems !== undefined || field.maxItems !== undefined || field.reorderable !== undefined) throw new TypeError('Array bounds require an array field.');
      return false;
    }
    const { min, max } = recordEditorArrayBounds(field);
    const value = field.value;
    if (value === null && isJsonObject(field.nullToggle)) return false;
    if (!Array.isArray(value)) return value !== null && value !== undefined || min > 0;
    return value.length < min || value.length > max || editorObjects(field.items).some(item => recordEditorArraysInvalid(editorObjects(item.fields)));
  });
}

const renderRecordEditorDescription = (model: JsonObject, translate: CapabilityTranslate): ReactNode => {
  if (model.description !== undefined && model.descriptionKey !== undefined) throw new TypeError('Ambiguous editor description.');
  if (model.description !== undefined) {
    if (typeof model.description !== 'string') throw new TypeError('Invalid editor description.');
    return model.description || null;
  }
  return stringValue(model.descriptionKey) ? <LocalizedMessageText messageKey={String(model.descriptionKey)} values={model.descriptionParameters} translate={translate} /> : null;
};

const RecordEditor = ({
  model,
  childrenBySlot,
  translate,
  emitIntent: hostEmitIntent,
}: DesignCapabilityRendererContext) => {
  const { locale } = useMonkeysLocale();
  const editorLabel = (field: JsonObject, fallback: string) => field.labelI18n !== undefined
    ? resolveI18nDisplayText(field.labelI18n, locale)
    : typeof field.labelKey === 'string' && field.labelKey
      ? translate(field.labelKey, resolveLocalizedDisplayParameters(field.labelParameters, locale))
      : stringValue(field.label) ?? fallback;
  const groupedOptions = (options: JsonObject[], render: (option: JsonObject) => ReactNode, multiple: boolean) => {
    const runs: { group?: JsonObject; options: JsonObject[] }[] = [];
    for (const option of options) {
      const group = isJsonObject(option.group) ? option.group : undefined;
      const previous = runs[runs.length - 1];
      if (previous && previous.group?.id === group?.id) previous.options.push(option);
      else runs.push({ group, options: [option] });
    }
    return runs.map((run, index) => {
      const children = run.options.map(render);
      if (!run.group) return <Fragment key={index}>{children}</Fragment>;
      const label = editorLabel(run.group, '');
      return multiple
        ? <fieldset key={index} data-editor-option-group={String(run.group.id)}><legend>{label}</legend>{children}</fieldset>
        : <SelectGroup key={index} data-editor-option-group={String(run.group.id)}><SelectLabel>{label}</SelectLabel>{children}</SelectGroup>;
    });
  };
  const editorId = useId();
  const draftConfig = isJsonObject(model.draft) ? model.draft : undefined;
  const draftRevision = draftConfig && stringValue(draftConfig.revision);
  const draftScope = stringValue(model.secretInputScopeKey) ?? '';
  const draftIdentity = JSON.stringify([draftRevision]);
  const makeDraft = () => draftRevision && draftConfig && isJsonObject(draftConfig.value) ? createRecordEditorDraft(draftRevision, draftConfig.value) : undefined;
  const [draftState, setDraftState] = useState(() => ({ identity: draftIdentity, draft: makeDraft(), resetGeneration: 0 }));
  const draft = draftState.identity === draftIdentity ? draftState.draft : makeDraft();
  useEffect(() => { setDraftState({ identity: draftIdentity, draft: makeDraft(), resetGeneration: 0 }); }, [draftIdentity]);
  const changeDraft = (operation: RecordEditorDraftOperation) => {
    if (!draft) return;
    const changedField = operation.kind === 'set' ? leaves.find((field) => field.id === recordEditorPathKey(operation.path)) : undefined;
    const next = applyEditorFieldChange(draft, operation, changedField, model);
    if (operation.kind === 'set' && changedField?.changeEffects !== undefined) projectEditorFields(presentedFields, next, [], model);
    setDraftState({ identity: draftIdentity, draft: next, resetGeneration: draftState.resetGeneration + (operation.kind === 'reset' ? 1 : 0) });
    if (operation.kind === 'touch') return;
    hostEmitIntent('capability.record-editor.change', { fieldId: 'path' in operation ? recordEditorPathKey(operation.path) : '', value: next.value, draft: { revision: next.revision, value: next.value, dirty: recordEditorDraftIsDirty(next), touched: next.touched } });
  };
  const emitIntent: DesignCapabilityRendererContext['emitIntent'] = (type, payload = {}, options) => {
    if (draft && type === 'capability.record-editor.change' && typeof payload.fieldId === 'string') {
      changeDraft({ kind: 'set', path: RecordEditorPathSchema.parse(JSON.parse(payload.fieldId)), value: payload.value ?? null });
      return;
    }
    return hostEmitIntent(type, payload, options);
  };

  const rawSecretScope = stringValue(model.secretInputScopeKey);
  const secretScope = rawSecretScope ? JSON.stringify([rawSecretScope, draftRevision ?? null]) : undefined;
  const [secretDraft, setSecretDraft] = useState<{ scope: string | undefined; values: Record<string, string>; modes: Record<string, "keep" | "replace" | "clear"> }>({ scope: secretScope, values: {}, modes: {} });
  const secretValues = secretDraft.scope === secretScope ? secretDraft.values : {};
  useEffect(() => {
    setSecretDraft({ scope: secretScope, values: {}, modes: {} });
  }, [secretScope]);
  const auxiliaryIdentity = JSON.stringify([secretScope, draftIdentity]);
  const [auxiliaryPending, setAuxiliaryPending] = useState<{ identity: string; id: string }>();
  const auxiliaryTicket = useRef<object>();
  const currentAuxiliaryIdentity = useRef(auxiliaryIdentity); currentAuxiliaryIdentity.current = auxiliaryIdentity;
  useEffect(() => { auxiliaryTicket.current = undefined; return () => { auxiliaryTicket.current = undefined; }; }, [auxiliaryIdentity]);
  const auxPending = auxiliaryPending?.identity === auxiliaryIdentity ? auxiliaryPending.id : undefined;
  const editorLocked = model.submitting === true || Boolean(auxPending);
  const clearSecrets = () => setSecretDraft({ scope: secretScope, values: {}, modes: {} });
  const cancel = () => {
    if (editorLocked) return;
    if (model.resetOnCancel === true && draft) {
      setDraftState({ identity: draftIdentity, draft: createRecordEditorDraft(draft.revision, draft.original), resetGeneration: draftState.resetGeneration + 1 });
    }
    clearSecrets();
    emitIntent("capability.record-editor.cancel", {});
  };

  const [invalidJsonFieldIds, setInvalidJsonFieldIds] = useState<Set<string>>(
    () => new Set(),
  );
  const onValidityChange = useCallback((fieldId: string, valid: boolean) => {
    setInvalidJsonFieldIds((current) => {
      if (current.has(fieldId) === !valid) return current;
      const next = new Set(current);
      if (valid) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  }, []);
  const baseCannotSubmit = editorLocked || model.submitDisabled === true || invalidJsonFieldIds.size > 0;
  const sourceFields = objectArray(model.fields);
  const presentationById = new Map(
    objectArray(model.fieldPresentation).flatMap((field) => {
      const id = stringValue(field.id);
      return id ? [[id, field] as const] : [];
    }),
  );
  const mergeFieldPresentation = (field: JsonObject): JsonObject => {
    const id = stringValue(field.id);
    if (!id) return field;
    const presentation = presentationById.get(id);
    if (!presentation) return field;
    const merged: JsonObject = {
      ...field,
      ...presentation,
      id,
      value: field.value ?? null,
    };
    const fallbackValue = stringValue(merged.fallbackValue);
    const optionIds = objectArray(merged.options).flatMap((option) =>
      typeof option.id === 'string' ? [option.id] : [],
    );
    return merged.kind === "select" &&
      fallbackValue &&
      !optionIds.includes(String(merged.value ?? ""))
      ? { ...merged, value: fallbackValue }
      : merged;
  };
  const visibleFieldIds = Array.isArray(model.visibleFieldIds)
    ? model.visibleFieldIds.flatMap((id) =>
        typeof id === "string" ? [id] : [],
      )
    : undefined;
  const sourceFieldsById = new Map(
    sourceFields.flatMap((field) => {
      const id = stringValue(field.id);
      return id ? [[id, field] as const] : [];
    }),
  );
  const presentedFields = visibleFieldIds
    ? visibleFieldIds.flatMap((id) => {
        const field = sourceFieldsById.get(id);
        return field ? [mergeFieldPresentation(field)] : [];
      })
    : sourceFields.map(mergeFieldPresentation);
  const fields = draft ? projectEditorFields(presentedFields, draft, [], model) : presentedFields;
  const patchKeys = useRef<{ identity: string; keys: Set<string> }>({ identity: draftIdentity, keys: new Set() });
  useEffect(() => {
    if (patchKeys.current.identity !== draftIdentity) patchKeys.current = { identity: draftIdentity, keys: new Set() };
    if (!draft || model.readOnly === true || model.submitting === true || !isJsonObject(model.draftPatch)) return;
    const patch = model.draftPatch;
    const key = typeof patch.key === 'string' ? patch.key : '';
    if (patchKeys.current.keys.has(key)) return;
    patchKeys.current.keys.add(key);
    const result = applyEditorDraftPatch(draft, patch);
    let reason = result.success ? undefined : result.reason;
    if (result.success) {
      try { projectEditorFields(sourceFields, result.draft, [], model); }
      catch { reason = 'invalid-operation'; }
    }
    if (reason) { hostEmitIntent('capability.record-editor.draftPatchRejected', { key, reason }); return; }
    if (!result.success) return;
    const next = result.draft;
    setDraftState({ identity: draftIdentity, draft: next, resetGeneration: draftState.resetGeneration });
    hostEmitIntent('capability.record-editor.change', { fieldId: '', value: next.value, draft: { revision: next.revision, value: next.value, dirty: recordEditorDraftIsDirty(next), touched: next.touched } });
  }, [model.draftPatch, model.readOnly, model.submitting, draftIdentity, draft]);
  const leaves = editorLeafFields(fields);
  const cannotSubmit = baseCannotSubmit || recordEditorArraysInvalid(fields) || Boolean(draft && leaves.some(field => {
    if (field.kind !== 'secret' || field.hidden === true || field.disabled === true) return false;
    const mode = secretDraft.scope === secretScope ? secretDraft.modes[String(field.id)] ?? (field.hasValue === true ? 'keep' : 'replace') : (field.hasValue === true ? 'keep' : 'replace');
    if (mode !== 'replace') return field.required === true && (mode === 'clear' || field.hasValue !== true);
    const parsed = parseTypedSecretInput(field.inputKind, secretValues[String(field.id)]);
    return parsed.status === 'invalid' || (field.required === true && (parsed.status === 'empty' || parsed.value === '' || parsed.value === null));
  }));
  const fieldErrors = draft ? editorDraftErrors(model, draft) : [];
  const draftInvalid = draft && leaves.some((field) => field.hidden !== true && field.disabled !== true && field.kind !== 'secret' && field.required === true && ((field.value === null && !isJsonObject(field.nullToggle)) || field.value === '' || (Array.isArray(field.value) && !field.value.length)));
  const values = Object.fromEntries(
    leaves.flatMap((field) =>
      stringValue(field.id) ? [[String(field.id), field.value ?? null]] : [],
    ),
  );
  const featureValues = Object.fromEntries(
    leaves.flatMap((field) => {
      const fieldId = stringValue(field.id);
      const value = governedEditorValue(field.value);
      if (
        field.kind === "secret" ||
        !fieldId?.startsWith("feature:") ||
        fieldId.length === "feature:".length ||
        value === undefined
      )
        return [];
      return [[fieldId.slice("feature:".length), value]];
    }),
  );
  const auxiliarySubmissions = objectArray(model.auxiliarySubmissions);
  const auxiliaryInvalid = (action: JsonObject) => {
    const ids = action.validateFieldIds;
    if (!Array.isArray(ids) || !ids.length || new Set(ids).size !== ids.length || ids.some(id => typeof id !== 'string' || !sourceFields.some(field => field.id === id))) return true;
    const targets = leaves.filter(field => ids.some(id => field.id === id || (Array.isArray(field.path) && field.path[0] === id)));
    return editorLocked || model.readOnly === true || action.disabled === true || targets.some(field => invalidJsonFieldIds.has(String(field.id)) || (field.required === true && (field.value === undefined || (field.value === null && !isJsonObject(field.nullToggle)) || field.value === '' || (Array.isArray(field.value) && !field.value.length)))) || recordEditorArraysInvalid(targets) || fieldErrors.some(error => Array.isArray(error.path) && ids.includes(error.path[0]));
  };
  const submit = (auxiliary?: JsonObject) => {
    if (auxiliary ? auxiliaryInvalid(auxiliary) : cannotSubmit || draftInvalid || fieldErrors.length) return;
    const submittedValues = draft ? { ...draft.value } : { ...values };
    const secretChanges: JsonObject[] = [];
    for (const field of leaves) {
      if (field.kind !== "secret" || field.hidden === true || field.disabled === true) continue;
      const id = String(field.id);
      if (draft) {
        const mode = secretDraft.scope === secretScope ? secretDraft.modes[id] ?? (field.hasValue === true ? 'keep' : 'replace') : (field.hasValue === true ? 'keep' : 'replace');
        const parsed = parseTypedSecretInput(field.inputKind, secretValues[id]);
        if (!secretScope || (field.required === true && (mode === 'clear' || (mode === 'keep' && field.hasValue !== true)))) return;
        if (mode === 'replace') {
          if (parsed.status === 'invalid') return;
          if (parsed.status === 'empty') { if (field.required === true) return; continue; }
          if (field.required === true && (parsed.value === '' || parsed.value === null)) return;
          secretChanges.push({ path: field.path!, mode, value: parsed.value });
        } else secretChanges.push({ path: field.path!, mode });
        continue;
      }
      if (field.inputKind !== undefined && field.inputKind !== 'password') return;
      if (!secretScope || (field.required === true && !secretValues[id])) return;
      if (Object.prototype.hasOwnProperty.call(secretValues, id)) submittedValues[id] = secretValues[id];
      else delete submittedValues[id];
    }
    if (auxiliary) {
      const id = String(auxiliary.id), identity = auxiliaryIdentity, ticket = {};
      if (auxiliaryTicket.current) return;
      auxiliaryTicket.current = ticket; setAuxiliaryPending({ identity, id });
      const finish = () => { if (auxiliaryTicket.current !== ticket || currentAuxiliaryIdentity.current !== identity) return; auxiliaryTicket.current = undefined; setAuxiliaryPending(undefined); };
      try {
        const completion = emitIntent('capability.record-editor.auxiliary-submit', { actionId: id, values: submittedValues, featureValues, ...(draft ? { secretChanges, draftRevision: draft.revision } : {}) }, { awaitCompletion: true });
        void Promise.resolve(completion).then(finish, finish);
      } catch { finish(); }
      return;
    }
    const submittedDraft = secretDraft;
    const clearSubmittedSecrets = () => setSecretDraft((current) => current === submittedDraft
      ? { scope: secretScope, values: {}, modes: {} } : current);
    const completion = emitIntent("capability.record-editor.submit", { values: submittedValues, featureValues, ...(draft ? { secretChanges, draftRevision: draft.revision } : {}) }, { awaitCompletion: true });
    if (completion && typeof completion.then === "function") {
      void completion.then(clearSubmittedSecrets, () => undefined);
    } else clearSubmittedSecrets();
  };
  const renderField = (
    field: JsonObject,
    dialog: boolean,
    availableColumns: number,
    inheritedDisabled = false,
  ) => {
    const id = stringValue(field.id);
    if (!id || field.hidden === true) return null;
    const domId = `${editorId}-${id}`;
    const label = editorLabel(field, id);
    const description = resolveRecordEditorFieldDescription(field, translate, locale);
    const descriptionNode = description === undefined ? null : <span className="block text-sm text-muted-foreground" data-editor-field-description>{description}</span>;
    const error = stringValue(field.errorCode) ?? stringValue(fieldErrors.find((entry) => recordEditorPathKey(RecordEditorPathSchema.parse(entry.path)) === id)?.code);
    if (error) field = { ...field, errorCode: error };
    const disabled = inheritedDisabled || field.disabled === true || editorLocked;
    const span = Math.min(availableColumns, typeof field.columnSpan === 'number' ? field.columnSpan : 1);
    const fieldClassName = model.presentation === 'toolbar' ? 'min-w-0 basis-40 grow' : span === 3 ? 'min-w-0 sm:col-span-3' : span === 2 ? 'min-w-0 sm:col-span-2' : 'min-w-0';
    const fieldIdentity = JSON.stringify([draftIdentity, draftScope, draftState.resetGeneration, id]);
    const wrapNullable = (children: ReactNode) => {
      if (field.nullToggle === undefined) return children;
      if (!isJsonObject(field.nullToggle) || typeof field.nullToggle.labelKey !== 'string' || !field.nullToggle.labelKey || !['multi-select', 'array'].includes(String(field.kind))) throw new TypeError('Invalid nullable editor field.');
      const toggle = field.nullToggle;
      if (field.kind === 'array' && toggle.retainSelectionOnNull === true) throw new TypeError('Array fields cannot retain nullable selection.');
      return <RecordEditorNullToggle value={field.value ?? null} label={translate(toggle.labelKey as string)}
        checkedWhen={toggle.checkedWhen === 'non-null' ? 'non-null' : 'null'} retainSelectionOnNull={toggle.retainSelectionOnNull === true}
        disabled={disabled} resetKey={fieldIdentity} onChange={value => emitIntent('capability.record-editor.change', { fieldId: id, value })}>{children}</RecordEditorNullToggle>;
    };
    if (draft && (field.kind === 'object' || field.kind === 'array')) {
      const path = RecordEditorPathSchema.parse(field.path);
      const idKey = stringValue(field.itemIdKey) ?? 'id';
      const items = editorObjects(field.items);
      const bounds = field.kind === 'array' ? recordEditorArrayBounds(field) : { min: 0, max: 1000 };
      return <fieldset key={id} disabled={disabled} className={fieldClassName}><legend>{label}</legend>{descriptionNode}
        {error ? <div role="alert">{translate(error)}</div> : null}
        {wrapNullable(field.kind === 'object' ? editorObjects(field.children).map((child) => renderField(child, dialog, 1, disabled)) : <>
          <RecordEditorArraySorting ids={items.map(item => String(item.id))} enabled={field.reorderable === true} disabled={disabled} onMove={(itemId, beforeId) => changeDraft({ kind: 'move', path, idKey, itemId, ...(beforeId === undefined ? {} : { beforeId }) })}>
          {items.map((item, index) => { const content = <>
            {editorObjects(item.fields).map((child) => renderField(child, dialog, 1, disabled))}
            <BaseButton label={translate(stringValue(field.removeLabelKey) ?? 'monkeys.design.capability.recordEditor.remove')} disabled={disabled || items.length <= bounds.min} onClick={() => changeDraft({ kind: 'remove', path, idKey, itemId: String(item.id) })} />
            <BaseButton label={translate('monkeys.design.capability.recordEditor.moveUp')} disabled={disabled || index === 0} onClick={() => changeDraft({ kind: 'move', path, idKey, itemId: String(item.id), beforeId: String(items[index - 1]?.id) })} />
            <BaseButton label={translate('monkeys.design.capability.recordEditor.moveDown')} disabled={disabled || index === items.length - 1} onClick={() => changeDraft({ kind: 'move', path, idKey, itemId: String(item.id), ...(items[index + 2] ? { beforeId: String(items[index + 2]!.id) } : {}) })} />
          </>; return field.reorderable === true ? <RecordEditorSortableArrayItem key={String(item.id)} id={String(item.id)} disabled={disabled} label={`${translate('monkeys.design.capability.recordEditor.moveUp')} / ${translate('monkeys.design.capability.recordEditor.moveDown')} ${index + 1}`}>{content}</RecordEditorSortableArrayItem> : <fieldset key={String(item.id)}>{content}</fieldset>; })}
          </RecordEditorArraySorting>
          <BaseButton label={translate(stringValue(field.addLabelKey) ?? 'monkeys.design.capability.recordEditor.add')} disabled={disabled || field.addDisabled === true || items.length >= bounds.max} onClick={() => changeDraft({ kind: 'insert', path, idKey, value: { ...(isJsonObject(field.itemDefault) ? field.itemDefault : {}), [idKey]: globalThis.crypto.randomUUID() } })} />
        </>)}
      </fieldset>;
    }
    if (field.kind === "secret") {
      const secret = secretValues[id] ?? "";
      const inputKind = field.inputKind ?? 'password';
      if (!['password', 'text', 'textarea', 'boolean', 'number', 'json'].includes(String(inputKind)) || (!draft && inputKind !== 'password')) throw new TypeError('Typed secret controls require a draft and a declared input kind.');
      const update = (value: string) => setSecretDraft((current) => ({ scope: secretScope, values: { ...(current.scope === secretScope ? current.values : {}), [id]: value }, modes: { ...(current.scope === secretScope ? current.modes : {}), [id]: "replace" } }));
      const secretMode = secretDraft.scope === secretScope ? secretDraft.modes[id] ?? (field.hasValue === true ? 'keep' : 'replace') : (field.hasValue === true ? 'keep' : 'replace');
      const secretDisabled = disabled || !secretScope || Boolean(draft && secretMode !== 'replace');
      return <BaseField key={id} className={fieldClassName} label={label} htmlFor={domId} description={description} required={field.required === true} error={error ? translate(error) : undefined}><BaseStack gap="var(--global-spacing)">
        {draft ? <BaseSelect id={`${domId}-mode`} aria-label={translate('monkeys.design.capability.recordEditor.secretMode', { field: typeof label === 'string' ? label : id })} value={secretMode} disabled={disabled || !secretScope} onChange={(event) => {
          const mode = event.currentTarget.value as 'keep' | 'replace' | 'clear';
          setSecretDraft((current) => ({ scope: secretScope, values: { ...(current.scope === secretScope ? current.values : {}), [id]: '' }, modes: { ...(current.scope === secretScope ? current.modes : {}), [id]: mode } }));
        }}>
          <option value="keep" disabled={field.hasValue !== true}>{translate('monkeys.design.capability.recordEditor.secretKeep')}</option>
          <option value="replace">{translate('monkeys.design.capability.recordEditor.secretReplace')}</option>
          <option value="clear" disabled={field.required === true}>{translate('monkeys.design.capability.recordEditor.secretClear')}</option>
        </BaseSelect> : null}
        {inputKind === 'boolean' ? <BaseCheckbox id={domId} aria-label={typeof label === 'string' ? label : id} checked={secret === 'true'} disabled={secretDisabled} onChange={event => update(String(event.currentTarget.checked))} /> : inputKind === 'textarea' || inputKind === 'json' ? <BaseTextarea id={domId} aria-label={typeof label === 'string' ? label : id} autoComplete="off" spellCheck={false} value={secret} disabled={secretDisabled} rows={typeof field.rows === 'number' ? field.rows : 4} onChange={event => update(event.currentTarget.value)} /> : inputKind === 'text' || inputKind === 'number' ? <BaseInput id={domId} type={inputKind} aria-label={typeof label === 'string' ? label : id} autoComplete="off" value={secret} disabled={secretDisabled} onChange={event => update(event.currentTarget.value)} /> : field.multiline === true ? <div className="relative">
          <textarea aria-label={typeof label === "string" ? label : id} autoComplete="off" spellCheck={false} value={secret}
            rows={typeof field.rows === "number" ? field.rows : 4}
            disabled={secretDisabled} required={field.required === true}
            className="relative z-10 w-full bg-transparent font-mono text-transparent caret-current"
            style={{ color: "transparent", caretColor: "var(--foreground)", WebkitTextFillColor: "transparent" }}
            onChange={(event) => update(event.target.value)} />
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 whitespace-pre-wrap break-all font-mono">
            {secret.replace(/[^\r\n]/g, "•")}
          </div>
        </div> : <BaseInput id={domId} type="password" aria-label={typeof label === "string" ? label : id} autoComplete="new-password" spellCheck={false}
          value={secret} disabled={secretDisabled} required={field.required === true}
          onChange={(event) => update(event.target.value)} />}
      </BaseStack></BaseField>;
    }
    if (field.kind === 'multi-select') {
      const selected = Array.isArray(field.value) ? field.value.filter((value): value is string => typeof value === 'string') : [];
      const enhanced = field.searchable === true || field.allowCustomValues === true || field.showSelectedIds === true || field.optionsSource !== undefined;
      const choices = wrapNullable(enhanced ? <RecordEditorStringSelection key={fieldIdentity} field={field} value={selected} disabled={disabled} id={id} domId={domId} translate={translate} resolveLabel={editorLabel}
          onChange={value => emitIntent('capability.record-editor.change', { fieldId: id, value })} /> : groupedOptions(objectArray(field.options), (option) => {
          const optionId = typeof option.id === 'string' ? option.id : undefined;
          if (optionId === undefined) return null;
          const optionLabel = editorLabel(option, optionId);
          return <BaseCheckbox key={optionId} id={`${domId}-${encodeURIComponent(optionId)}`}
            aria-label={typeof optionLabel === 'string' ? optionLabel : optionId}
            label={optionLabel} disabled={disabled || option.disabled === true} checked={selected.includes(optionId)}
            onChange={(event) => emitIntent('capability.record-editor.change', { fieldId: id, value: event.currentTarget.checked ? [...new Set([...selected, optionId])] : selected.filter((value) => value !== optionId) })} />;
        }, true));
      return <fieldset key={id} disabled={disabled} className={fieldClassName} data-editor-field-path={draft ? id : undefined} aria-required={field.required === true} aria-invalid={Boolean(error)}>
        <legend>{label}</legend>{descriptionNode}
        {model.presentation === 'toolbar' ? <Popover><PopoverTrigger asChild><BaseButton disabled={disabled} ariaLabel={typeof label === 'string' ? label : id} label={selected.length ? translate('monkeys.design.capability.recordEditor.selectionSummary', { count: selected.length }) : translate('monkeys.design.capability.recordEditor.pick')} /></PopoverTrigger><PopoverContent align="start" className="max-h-80 overflow-auto">{choices}</PopoverContent></Popover> : choices}
        {error ? <div role="alert">{translate(error)}</div> : null}
      </fieldset>;
    }
    if (field.kind === 'relation' && field.presentation === 'picker') {
      return <fieldset key={id} className={fieldClassName} disabled={disabled || !draft} data-editor-field-path={draft ? id : undefined}>
        <legend>{label}</legend>{descriptionNode}<div>{governedReadValue(field.value)}</div>
        <BaseButton label={translate(typeof field.pickLabelKey === 'string' ? field.pickLabelKey : 'monkeys.design.capability.recordEditor.pick')} disabled={disabled || !draft}
          onClick={() => { if (draft) hostEmitIntent('capability.record-editor.pick', { fieldId: id, draftValues: draft.value, draftRevision: draft.revision }); }} />
        {error ? <div role="alert">{translate(error)}</div> : null}
      </fieldset>;
    }
    if (governedSelectionKind(field.kind)) {
      return (
        <div key={id} className={fieldClassName} data-editor-field-path={draft ? id : undefined}>
          <RecordEditorSelectionField
            field={field}
            fieldId={id}
            domId={domId}
            label={label}
            description={description}
            disabled={disabled}
            translate={translate}
            emitIntent={emitIntent}
          />
        </div>
      );
    }
    if (field.kind === 'date' || field.kind === 'datetime-local') {
      return <div key={id} className={fieldClassName} data-editor-field-path={draft ? id : undefined}>
        <RecordEditorDateControl field={field} fieldId={id} domId={domId} label={typeof label === 'string' ? label : id} description={description} disabled={disabled} translate={translate} emitIntent={emitIntent} onValidityChange={onValidityChange} />
      </div>;
    }
    if (field.kind === "json") {
      return (
        <div key={id} className={fieldClassName} data-editor-field-path={draft ? id : undefined}>
          <RecordEditorJsonField
            field={field}
            fieldId={id}
            domId={domId}
            label={label}
            description={description}
            disabled={disabled}
            translate={translate}
            emitIntent={emitIntent}
            onValidityChange={onValidityChange}
          />
        </div>
      );
    }
    const common = {
      id: domId,
      disabled,
      required: field.required === true,
      value:
        typeof field.value === "string" || typeof field.value === "number"
          ? field.value
          : "",
      onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const rawValue = event.currentTarget.value;
        const value =
          field.kind === "number"
            ? rawValue.trim() === ""
              ? null
              : event.currentTarget instanceof HTMLInputElement && Number.isFinite(event.currentTarget.valueAsNumber)
                ? event.currentTarget.valueAsNumber
                : rawValue
            : rawValue;
        emitIntent("capability.record-editor.change", { fieldId: id, value });
      },
    };
    const placeholder = copyValueOrFallback(
      field.placeholder,
      field.placeholderKey,
      translate,
      undefined,
    );
    const control =
      field.kind === 'text' && Array.isArray(field.options) ? <>
        {dialog ? <Input {...common} type="text" list={`${domId}-options`} placeholder={typeof placeholder === 'string' ? placeholder : undefined} /> : <BaseInput {...common} type="text" list={`${domId}-options`} placeholder={typeof placeholder === 'string' ? placeholder : undefined} />}
        <datalist id={`${domId}-options`}>{objectArray(field.options).filter(option => typeof option.id === 'string' && option.disabled !== true).map(option => <option key={String(option.id)} value={String(option.id)}>{editorLabel(option, String(option.id))}</option>)}</datalist>
      </> : field.kind === "select" ? (
        <Select
          value={typeof field.value === "string" ? JSON.stringify([field.value]) : ""}
          disabled={disabled}
          onOpenChange={open => { if (open && !disabled && model.readOnly !== true) emitIntent('capability.record-editor.options-request', { fieldId: id }); }}
          onValueChange={(value) =>
            emitIntent("capability.record-editor.change", {
              fieldId: id,
              value: JSON.parse(value)[0],
            })
          }
        >
          <SelectTrigger
            id={domId}
            aria-label={typeof label === "string" ? label : id}
          >
            <SelectValue
              placeholder={
                stringValue(field.placeholderKey)
                  ? translate(String(field.placeholderKey))
                  : undefined
              }
            />
          </SelectTrigger>
          <SelectContent>
            {groupedOptions(objectArray(field.options), (option) => {
              const optionId = typeof option.id === 'string' ? option.id : undefined;
              if (optionId === undefined) return null;
              return (
                <SelectItem
                  key={optionId}
                  value={JSON.stringify([optionId])}
                  disabled={option.disabled === true}
                >
                  {editorLabel(option, optionId)}
                </SelectItem>
              );
            }, false)}
          </SelectContent>
        </Select>
      ) : field.kind === "textarea" || (field.kind === "text" && field.multiline === true) ? (
        dialog ? (
          <Textarea
            {...common}
            placeholder={
              typeof placeholder === "string" ? placeholder : undefined
            }
            rows={Number(field.rows) || undefined}
          />
        ) : (
          <BaseTextarea
            {...common}
            className="w-full"
            placeholder={
              typeof placeholder === "string" ? placeholder : undefined
            }
            rows={Number(field.rows) || undefined}
          />
        )
      ) : field.kind === "boolean" ? (
        <BaseCheckbox
          id={domId}
          aria-label={typeof label === "string" ? label : undefined}
          checked={field.value === true}
          disabled={common.disabled}
          onChange={(event) =>
            emitIntent("capability.record-editor.change", {
              fieldId: id,
              value: event.currentTarget.checked,
            })
          }
        />
      ) : dialog ? (
        <Input
          {...common}
          placeholder={
            typeof placeholder === "string" ? placeholder : undefined
          }
          type={field.kind === "number" ? "number" : "text"}
        />
      ) : (
        <BaseInput
          {...common}
          placeholder={
            typeof placeholder === "string" ? placeholder : undefined
          }
          type={field.kind === "number" ? "number" : "text"}
        />
      );
    return (
      <BaseField
        key={id}
        label={label}
        description={description}
        htmlFor={domId}
        required={field.required === true}
        error={error ? translate(error) : undefined}
        data-editor-field-kind={String(field.kind)}
        data-editor-field-path={draft ? id : undefined}
        className={fieldClassName}
      >
        {control}
      </BaseField>
    );
  };
  const configuredFieldRows = Array.isArray(model.fieldRows)
    ? model.fieldRows.flatMap((row) =>
        Array.isArray(row)
          ? [
              row.flatMap((fieldId) =>
                typeof fieldId === "string" && fieldId.trim() ? [fieldId] : [],
              ),
            ]
          : [],
      )
    : [];
  const fieldsById = new Map(
    fields.flatMap((field, index) => {
      const id = stringValue(field.id);
      const originalId = stringValue(presentedFields[index]?.id);
      if (!id) return [];
      // Map both the transformed id and the original id to the field
      const entries: Array<[string, JsonObject]> = [[id, field]];
      if (originalId && originalId !== id) {
        entries.push([originalId, field]);
      }
      return entries;
    }),
  );
  const groupedFieldIds = new Set<string>();
  const fieldRows = configuredFieldRows.flatMap((row) => {
    const groupedFields = row.flatMap((fieldId) => {
      const field = fieldsById.get(fieldId);
      if (!field || field.hidden === true || groupedFieldIds.has(String(field.id)))
        return [];
      groupedFieldIds.add(String(field.id));
      return [field];
    });
    return groupedFields.length > 0 ? [groupedFields] : [];
  });
  const editorGridColumns = (columns: unknown) => columns === 3 ? 'grid-cols-1 sm:grid-cols-3' : columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1';
  const renderFieldsContent = (dialog: boolean) => {
    if (model.presentation === 'toolbar') return fields.map(field => renderField(field, false, 1));
    const groups = objectArray(model.fieldGroups);
    if (groups.length) {
      const renderedIds = new Set<string>();
      return <div className="grid gap-4">
        {groups.map((group) => {
          const groupFields = (Array.isArray(group.fieldIds) ? group.fieldIds : []).flatMap((fieldId) => {
            const field = typeof fieldId === 'string' ? fieldsById.get(fieldId) : undefined;
            if (!field || field.hidden === true || renderedIds.has(String(field.id))) return [];
            renderedIds.add(String(field.id));
            return [field];
          });
          if (!groupFields.length) return null;
          const groupContent = <fieldset key={String(group.id)} data-editor-group={String(group.id)}>
            <legend>{translate(String(group.titleKey))}</legend>
            {stringValue(group.descriptionKey) ? <p>{translate(String(group.descriptionKey))}</p> : null}
            <div className={`grid gap-4 ${editorGridColumns(group.columns)}`}>
              {groupFields.map((field) => renderField(field, dialog, group.columns === 3 ? 3 : group.columns === 2 ? 2 : 1))}
            </div>
          </fieldset>;
          return group.surface === 'panel' ? <BasePanel key={String(group.id)}>{groupContent}</BasePanel> : groupContent;
        })}
        {fields.filter((field) => !renderedIds.has(String(field.id))).map((field) => renderField(field, dialog, 1))}
      </div>;
    }

    if (fieldRows.length > 0) {
      return (
        <div className="grid gap-4">
          {fieldRows.map((row, rowIndex) => (
            <div
              key={`field-row-${rowIndex}`}
              className={`grid ${editorGridColumns(row.length)} gap-4`}
            >
              {row.map((field) => renderField(field, dialog, 1))}
            </div>
          ))}
          {fields
            .filter((field) => {
              const id = stringValue(field.id);
              return id && !groupedFieldIds.has(id);
            })
            .map((field) => renderField(field, dialog, 1))}
        </div>
      );
    }
    return (
      <div
        className={
          dialog || model.layoutColumns !== undefined
            ? `grid gap-4 ${editorGridColumns(model.layoutColumns)}`
            : undefined
        }
      >
        {fields.map((field) => renderField(field, dialog, model.layoutColumns === 3 ? 3 : model.layoutColumns === 2 ? 2 : 1))}
      </div>
    );
  };
  const renderFields = (dialog: boolean) => <div className={model.presentation === 'toolbar' ? 'contents' : undefined} key={`${draftIdentity}:${draftState.resetGeneration}`} data-editor-dirty={draft ? recordEditorDraftIsDirty(draft) : undefined} data-editor-touched={draft?.touched.length} onBlur={(event) => {
    if (!draft) return;
    const target = event.target as HTMLElement;
    const id = target.closest('[data-editor-field-path]')?.getAttribute('data-editor-field-path') ?? target.id;
    const field = leaves.find((field) => field.id === id);
    if (field && field.kind !== 'secret') changeDraft({ kind: 'touch', path: RecordEditorPathSchema.parse(field.path) });
  }}>{renderFieldsContent(dialog)}</div>;
  const resetButton = draft && model.showReset !== false ? <BaseButton label={translate('monkeys.design.capability.recordEditor.reset')} disabled={editorLocked} onClick={() => { clearSecrets(); changeDraft({ kind: 'reset', revision: draft.revision, value: draft.original }); }} /> : null;
  const visibleLeafIds = new Set(leaves.filter((field) => field.hidden !== true).map((field) => String(field.id)));
  const summaryErrors = fieldErrors.filter((error) => !visibleLeafIds.has(recordEditorPathKey(RecordEditorPathSchema.parse(error.path))));
  const errorSummary = summaryErrors.length ? <ul role="alert">{summaryErrors.map((error, index) => <li key={index}>{translate(String(error.code))}</li>)}</ul> : null;
  const actions = (
    <>
      {resetButton}
      {errorSummary}
      {stringValue(model.errorCode) ? (
        <CapabilityStatus
          kind="error"
          code={String(model.errorCode)}
          translate={translate}
        />
      ) : null}
      {model.showCancel !== false ? <BaseButton
        label={translate(
          stringValue(model.cancelLabelKey) ??
            DESIGN_CAPABILITY_I18N_KEYS.recordEditor.cancel,
        )}
        disabled={editorLocked}
        onClick={cancel}
      /> : null}
      {model.showSubmit !== false ? <BaseButton
        label={translate(
          stringValue(model.submitLabelKey) ??
            DESIGN_CAPABILITY_I18N_KEYS.recordEditor.submit,
        )}
        tone={model.submitDestructive === true ? "danger" : "default"}
        loading={model.submitting === true && !auxPending}
        disabled={Boolean(cannotSubmit || draftInvalid || fieldErrors.length)}
        onClick={() => submit()}
      /> : null}
    </>
  );
  const auxiliaryButtons = auxiliarySubmissions.map(action => <BaseButton key={String(action.id)} label={translate(String(action.labelKey))} loading={auxPending === action.id} disabled={auxiliaryInvalid(action)} onClick={() => submit(action)} />);
  const cancelLabel = translate(
    stringValue(model.cancelLabelKey) ??
      DESIGN_CAPABILITY_I18N_KEYS.recordEditor.cancel,
  );
  const submitLabel = translate(
    stringValue(model.submitLabelKey) ??
      DESIGN_CAPABILITY_I18N_KEYS.recordEditor.submit,
  );
  const submittingLabel = translate(
    stringValue(model.submittingLabelKey) ??
      stringValue(model.submitLabelKey) ??
      DESIGN_CAPABILITY_I18N_KEYS.recordEditor.submit,
  );
  const recordTitle =
    stringValue(model.recordTitle) ?? stringValue(model.title);
  const title = stringValue(model.titleKey)
    ? translate(
        String(model.titleKey),
        recordTitle ? { title: recordTitle } : undefined,
      )
    : copyValueOrFallback(model.title, undefined, translate, undefined);
  const description = renderRecordEditorDescription(model, translate);
  if (model.presentation === 'toolbar') return (
    <div data-record-editor-toolbar className="flex min-w-0 flex-wrap items-end" style={{ gap: 'var(--global-spacing)' }}>
      {childrenBySlot.header}
      {renderFields(false)}
      <BaseInline gap="var(--global-spacing)">{auxiliaryButtons}{actions}{childrenBySlot.footer}</BaseInline>
    </div>
  );
  if (model.presentation === 'sections') return (
    <BaseStack gap="var(--global-spacing)">
      {childrenBySlot.header}
      {description !== null ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      {renderFields(false)}
      <BaseToolbar actions={childrenBySlot.footer}>{auxiliaryButtons}{actions}</BaseToolbar>
    </BaseStack>
  );
  if (model.presentation === "data-explorer-dialog" || model.presentation === "dialog") {
    return (
      <Dialog
        open
        onOpenChange={(open) =>
          !open && cancel()
        }
      >
        <DialogContent
          className="flex max-h-[90dvh] max-w-2xl flex-col overflow-hidden"
          onOpenAutoFocus={(event) => {
            if (model.initialFocus !== "dialog") return;
            event.preventDefault();
            (event.currentTarget as HTMLElement).focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description !== null ? (
              <DialogDescription>
                {description}
              </DialogDescription>
            ) : null}
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {renderFields(true)}
            {childrenBySlot.footer}
          </div>
          <DialogFooter className="shrink-0">
            {auxiliaryButtons}
            {resetButton}
            {errorSummary}
            {stringValue(model.errorCode) ? (
              <CapabilityStatus
                kind="error"
                code={String(model.errorCode)}
                translate={translate}
              />
            ) : null}
            {model.showCancel !== false ? <Button
              type="button"
              variant="outline"
              disabled={editorLocked}
              onClick={cancel}
            >
              {cancelLabel}
            </Button> : null}
            {model.showSubmit !== false ? <Button
              type="button"
              variant={model.submitDestructive === true ? "destructive" : "default"}
              disabled={Boolean(cannotSubmit || draftInvalid || fieldErrors.length)}
              onClick={() => submit()}
            >
              {model.submitting === true && !auxPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {submittingLabel}
                </>
              ) : (
                submitLabel
              )}
            </Button> : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <BasePanel title={title} media={childrenBySlot.header}>
      <BaseStack gap="var(--global-spacing)">
        {description !== null ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        {renderFields(false)}
        {resetButton || errorSummary || model.errorCode || model.showCancel !== false || model.showSubmit !== false || auxiliaryButtons.length > 0 || childrenBySlot.footer
          ? <BaseToolbar actions={childrenBySlot.footer}>{auxiliaryButtons}{actions}</BaseToolbar>
          : null}
      </BaseStack>
    </BasePanel>
  );
};

const ReadOnlyRecordEditor = (context: DesignCapabilityRendererContext) => {
  const { model, translate, emitIntent } = context;
  const dialog = model.presentation === 'dialog' || model.presentation === 'data-explorer-dialog';
  const { locale } = useMonkeysLocale();
  const fields = projectReadOnlyEditorFields(model, translate, locale);
  const body = <RecordDetailCapabilityRenderer {...context} model={{ title: dialog ? '' : model.title ?? model.recordTitle ?? '', titleKey: dialog ? '' : model.titleKey ?? '', editable: false, closeEnabled: false, fields }} />;
  const description = renderRecordEditorDescription(model, translate);
  if (!dialog) return <>{description !== null ? <p className="text-sm text-muted-foreground">{description}</p> : null}{body}</>;
  const cancel = () => { if (model.submitting !== true) emitIntent('capability.record-editor.cancel', {}); };
  const title = typeof model.titleKey === 'string' && model.titleKey ? translate(model.titleKey, typeof model.recordTitle === 'string' ? { title: model.recordTitle } : undefined) : String(model.title ?? model.recordTitle ?? '');
  return <Dialog open onOpenChange={open => { if (!open) cancel(); }}>
    <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>{title}</DialogTitle>
      {description !== null ? <DialogDescription>{description}</DialogDescription> : null}
    </DialogHeader>{body}<DialogFooter><Button type="button" variant="outline" disabled={model.submitting === true} onClick={cancel}>{translate(typeof model.cancelLabelKey === 'string' ? model.cancelLabelKey : DESIGN_CAPABILITY_I18N_KEYS.recordEditor.cancel)}</Button></DialogFooter></DialogContent>
  </Dialog>;
};
export const RecordEditorCapabilityRenderer: DesignCapabilityRenderer = (context) => context.model.readOnly === true
  ? <ReadOnlyRecordEditor {...context} /> : <RecordEditor {...context} />;

const DataExplorerRecordHistoryDialog = ({
  model,
  translate,
  emitIntent,
}: Pick<
  DesignCapabilityRendererContext,
  "model" | "translate" | "emitIntent"
>) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const aggregateId = stringValue(model.aggregateId) ?? "";
  const headCommitId = stringValue(model.headCommitId) ?? "";
  const commits = objectArray(model.commits);
  const historyDag = useMemo(
    () => computeRecordHistoryDag(commits, headCommitId),
    [commits, headCommitId],
  );
  const selectedTargetCommitId = stringValue(model.selectedTargetCommitId);
  const selectedCommit =
    commits.find(
      (commit) => stringValue(commit.commitId) === selectedTargetCommitId,
    ) ?? commits[0];
  const reason = typeof model.reason === "string" ? model.reason : "";
  const rollback = isJsonObject(model.rollback) ? model.rollback : {};
  const messageEditor = isJsonObject(model.messageEditor)
    ? model.messageEditor
    : {};
  const editingCommitId = stringValue(messageEditor.commitId) ?? "";
  const editingMessage =
    typeof messageEditor.message === "string" ? messageEditor.message : "";
  const messagePending = messageEditor.pending === true;
  const pending = rollback.pending === true || messagePending;
  const close = () => {
    if (!pending) emitIntent("capability.record-history.close", {});
  };
  const formatTimestamp = (raw: JsonValue | undefined) => {
    const value = typeof raw === "number" || typeof raw === "string" ? raw : "";
    const date = new Date(value);
    if (!value || Number.isNaN(date.getTime())) return "-";
    const part = (number: number) => String(number).padStart(2, "0");
    return `${date.getFullYear()}-${part(date.getMonth() + 1)}-${part(date.getDate())} ${part(date.getHours())}:${part(date.getMinutes())}:${part(date.getSeconds())}`;
  };
  const shortCommitId = (raw: JsonValue | undefined) => {
    const value = stringValue(raw) ?? "";
    const withoutPrefix = value.startsWith("dvc_") ? value.slice(4) : value;
    return withoutPrefix ? withoutPrefix.slice(0, 10) : "-";
  };
  const formatValue = (value: JsonValue | undefined): string => {
    if (value === undefined || value === null || value === "")
      return translate("common.status.not-set");
    if (typeof value === "boolean")
      return translate(value ? "common.confirm.yes" : "common.confirm.no");
    if (typeof value === "string" || typeof value === "number")
      return String(value);
    return JSON.stringify(value);
  };
  const formatChange = (commit: JsonObject | undefined): string => {
    if (!commit)
      return translate(
        "components.data-management.version-history.data-change",
      );
    const changes = objectArray(commit.changes);
    if (!changes.length)
      return (
        stringValue(commit.summary) ??
        translate("components.data-management.version-history.data-change")
      );
    const first =
      changes.find(
        (change) =>
          JSON.stringify(change.before ?? null) !==
          JSON.stringify(change.after ?? null),
      ) ?? changes[0]!;
    const fieldName = copyValueOrFallback(
      first.label,
      first.labelKey,
      translate,
      stringValue(first.fieldId) ??
        translate("components.data-management.version-history.field"),
    );
    const field =
      changes.length > 1
        ? translate(
            "components.data-management.version-history.field-with-more",
            { field: String(fieldName), count: changes.length },
          )
        : String(fieldName);
    return translate(
      "components.data-management.version-history.change-format",
      {
        field,
        before: formatValue(first.before),
        after: formatValue(first.after),
      },
    );
  };
  const selectedId = stringValue(selectedCommit?.commitId) ?? "";
  const selectedChanges = objectArray(selectedCommit?.changes);
  const selectedChange =
    selectedChanges.find(
      (change) =>
        JSON.stringify(change.before ?? null) !==
        JSON.stringify(change.after ?? null),
    ) ?? selectedChanges[0];
  const selectedBefore =
    typeof selectedChange?.before === "string" ? selectedChange.before : "";
  const selectedAfter =
    typeof selectedChange?.after === "string" ? selectedChange.after : "";
  const selectedChangeUsesLongText = Boolean(
    selectedChange && (selectedBefore.length > 60 || selectedAfter.length > 60),
  );
  const isCurrentSelected = selectedId === headCommitId;
  const canRollback =
    rollback.enabled === true &&
    !pending &&
    Boolean(selectedId && !isCurrentSelected);
  const recordTitle = stringValue(model.recordTitle) ?? aggregateId;
  const title = copyValue(
    model.title,
    model.titleKey,
    translate,
    DESIGN_CAPABILITY_I18N_KEYS.recordHistory.title,
  );
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        ref={dialogContentRef}
        className="sm:max-w-[980px]"
        data-data-explorer-record-history
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          cancelButtonRef.current?.focus({ preventScroll: true });
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {translate(
              "components.data-management.version-history.description",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-col gap-3 rounded-md border bg-muted/20 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">
                {translate("components.data-management.version-history.asset")}
              </div>
              <div className="mt-0.5 truncate text-sm font-medium">
                {recordTitle || aggregateId || "-"}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
              <GitCommitHorizontal className="h-4 w-4" />
              <span>
                {translate(
                  "components.data-management.version-history.commit-count",
                  { count: commits.length },
                )}
              </span>
            </div>
          </div>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div className="rounded-md border bg-background">
              <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5">
                <div className="text-sm font-medium">
                  {translate(
                    "components.data-management.version-history.commit-graph",
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-success" />
                    {translate(
                      "components.data-management.version-history.current-mainline",
                    )}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-primary" />
                    {translate(
                      "components.data-management.version-history.rollback-target",
                    )}
                  </span>
                </div>
              </div>
              <ScrollArea
                className="h-[430px]"
                viewportClassName="overflow-x-auto"
              >
                <div className="min-w-[760px]">
                  {commits.length === 1 ? (
                    <div className="border-b px-4 py-3 text-sm text-muted-foreground">
                      {translate(
                        "components.data-management.version-history.only-current-version",
                      )}
                    </div>
                  ) : null}
                  {commits.map((commit, commitIndex) => {
                    const commitId = stringValue(commit.commitId) ?? "";
                    const current = commitId === headCommitId;
                    const selected = commitId === selectedId;
                    const dagRow = historyDag.rows[commitIndex];
                    const isOrphan = dagRow ? !dagRow.onMainLine : false;
                    const rollbackGenerated = [
                      stringValue(commit.systemMessage),
                      stringValue(commit.summary),
                    ]
                      .filter(Boolean)
                      .some((value) =>
                        /(?:rollback|restore)/i.test(String(value)),
                      );
                    const editing = commitId === editingCommitId;
                    const updatedTimestamp = Number(commit.updatedTimestamp);
                    const hasExactMetadataRevision =
                      Number.isSafeInteger(updatedTimestamp) &&
                      updatedTimestamp >= 0;
                    return (
                      <div
                        key={commitId}
                        onClick={() => {
                          if (!pending && !current)
                            emitIntent("capability.record-history.select", {
                              targetCommitId: commitId,
                            });
                        }}
                        className={`relative flex w-full gap-3 border-b px-4 py-3 text-left transition-colors last:border-b-0 ${current ? "cursor-not-allowed bg-success/10" : "cursor-pointer hover:bg-muted/50"} ${selected && !current ? "bg-primary/5" : ""} ${isOrphan && !selected ? "opacity-60" : ""}`}
                      >
                        {dagRow ? (
                          <RecordHistoryDagRail
                            row={dagRow}
                            laneCount={historyDag.laneCount}
                            selected={selected}
                            current={current}
                            rollback={rollbackGenerated}
                          />
                        ) : null}
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 items-start gap-2">
                            <button
                              type="button"
                              disabled={pending || current}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (!current)
                                  emitIntent(
                                    "capability.record-history.select",
                                    { targetCommitId: commitId },
                                  );
                              }}
                              className={`line-clamp-2 min-w-0 flex-1 break-words text-left text-sm font-medium leading-5 [overflow-wrap:anywhere] hover:text-primary disabled:cursor-not-allowed disabled:text-foreground${selected ? " text-primary" : ""}`}
                              title={formatChange(commit)}
                            >
                              {formatChange(commit)}
                            </button>
                            <div className="ml-auto flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                              <span>{formatTimestamp(commit.createdAt)}</span>
                              {current ? (
                                <span className="rounded border border-success/25 bg-success/10 px-1.5 py-0.5 text-success">
                                  {translate(
                                    "components.data-management.version-history.current-mainline",
                                  )}
                                </span>
                              ) : null}
                              {isOrphan ? (
                                <span className="rounded border border-muted-foreground/30 bg-muted px-1.5 py-0.5 text-muted-foreground">
                                  {translate(
                                    "components.data-management.version-history.bypassed",
                                  )}
                                </span>
                              ) : null}
                              {!editing ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  disabled={
                                    messagePending || !hasExactMetadataRevision
                                  }
                                  className="h-7 w-7 shrink-0"
                                  aria-label={translate(
                                    "components.data-management.version-history.edit-description",
                                  )}
                                  title={translate(
                                    "components.data-management.version-history.edit-description",
                                  )}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    emitIntent(
                                      "capability.record-history.message-edit",
                                      {
                                        commitId,
                                        expectedUpdatedTimestamp:
                                          updatedTimestamp,
                                        message:
                                          stringValue(commit.summary) ?? "",
                                      },
                                    );
                                  }}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              ) : null}
                            </div>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span>
                              {stringValue(commit.actorLabel) ??
                                translate(
                                  "components.data-management.version-history.system",
                                )}
                            </span>
                            {current ? (
                              <span>
                                {translate(
                                  "components.data-management.version-history.latest-version",
                                )}
                              </span>
                            ) : null}
                          </div>
                          {editing ? (
                            <div className="mt-3 space-y-2">
                              <Textarea
                                value={editingMessage}
                                maxLength={500}
                                rows={2}
                                disabled={messagePending}
                                placeholder={translate(
                                  "components.data-management.version-history.description-placeholder",
                                )}
                                className="min-h-[72px] resize-none text-sm"
                                onClick={(event) => event.stopPropagation()}
                                onKeyDown={(event) => event.stopPropagation()}
                                onChange={(event) =>
                                  emitIntent(
                                    "capability.record-history.message-change",
                                    { message: event.currentTarget.value },
                                  )
                                }
                              />
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs text-muted-foreground">
                                  {editingMessage.trim().length}/500
                                </span>
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={messagePending}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      emitIntent(
                                        "capability.record-history.message-cancel",
                                        {},
                                      );
                                    }}
                                  >
                                    <X className="mr-1.5 h-3.5 w-3.5" />
                                    {translate("common.actions.cancel")}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    disabled={
                                      messagePending ||
                                      !hasExactMetadataRevision
                                    }
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (!hasExactMetadataRevision) return;
                                      emitIntent(
                                        "capability.record-history.message-update",
                                        {
                                          commitId,
                                          expectedUpdatedTimestamp:
                                            updatedTimestamp,
                                          message: editingMessage.trim(),
                                        },
                                      );
                                    }}
                                  >
                                    {messagePending ? (
                                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Save className="mr-1.5 h-3.5 w-3.5" />
                                    )}
                                    {translate("common.actions.save")}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : stringValue(commit.summary) ? (
                            <div className="mt-2">
                              <div
                                className="line-clamp-2 min-w-0 flex-1 break-words text-sm leading-5 [overflow-wrap:anywhere]"
                                title={String(commit.summary)}
                              >
                                {String(commit.summary)}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </div>
            <div className="flex max-h-[430px] min-h-0 flex-col overflow-hidden rounded-md border bg-muted/10">
              <div className="shrink-0 px-4 pt-4 text-sm font-medium">
                {translate(
                  "components.data-management.version-history.rollback-target",
                )}
              </div>
              <ScrollArea
                className="min-h-0 flex-1 px-4 pb-4"
                viewportClassName="pr-3"
              >
                {selectedCommit ? (
                  <div className="mt-3 space-y-3 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">
                        {translate(
                          "components.data-management.version-history.commit",
                        )}
                      </div>
                      <div className="mt-1 flex min-w-0 items-center gap-2 font-mono">
                        <span className="truncate">
                          {shortCommitId(selectedCommit.commitId)}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 shrink-0 p-0"
                          onClick={() => {
                            const value = stringValue(selectedCommit.commitId);
                            if (value && typeof navigator !== "undefined")
                              void navigator.clipboard?.writeText(value);
                          }}
                          aria-label={translate("common.actions.copy")}
                          title={translate("common.actions.copy")}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">
                        {translate(
                          "components.data-management.version-history.time",
                        )}
                      </div>
                      <div className="mt-1">
                        {formatTimestamp(selectedCommit.createdAt)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">
                        {translate(
                          "components.data-management.version-history.actor",
                        )}
                      </div>
                      <div className="mt-1 break-words [overflow-wrap:anywhere]">
                        {stringValue(selectedCommit.actorLabel) ??
                          translate(
                            "components.data-management.version-history.system",
                          )}
                      </div>
                      {stringValue(selectedCommit.actorAccount) ? (
                        <div className="mt-0.5 break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
                          {String(selectedCommit.actorAccount)}
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">
                        {translate(
                          "components.data-management.version-history.manual-description",
                        )}
                      </div>
                      <div className="mt-1 line-clamp-2 break-words [overflow-wrap:anywhere]">
                        {stringValue(selectedCommit.summary) ??
                          translate(
                            "components.data-management.version-history.no-description",
                          )}
                      </div>
                    </div>
                    {stringValue(selectedCommit.systemMessage) ? (
                      <div>
                        <div className="text-xs text-muted-foreground">
                          {translate(
                            "components.data-management.version-history.system-record",
                          )}
                        </div>
                        <div className="mt-1 line-clamp-3 break-words [overflow-wrap:anywhere]">
                          {String(selectedCommit.systemMessage)}
                        </div>
                      </div>
                    ) : null}
                    <div>
                      <div className="text-xs text-muted-foreground">
                        {translate(
                          "components.data-management.version-history.change",
                        )}
                      </div>
                      {selectedChangeUsesLongText ? (
                        <div className="mt-1 space-y-2 text-sm">
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                              {translate(
                                "components.data-management.version-history.before",
                              )}
                            </div>
                            <div className="mt-0.5 max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded border bg-muted/30 p-2 text-xs [overflow-wrap:anywhere]">
                              {selectedBefore ||
                                translate(
                                  "components.data-management.version-history.empty-value",
                                )}
                            </div>
                          </div>
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                              {translate(
                                "components.data-management.version-history.after",
                              )}
                            </div>
                            <div className="mt-0.5 max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded border bg-muted/30 p-2 text-xs [overflow-wrap:anywhere]">
                              {selectedAfter ||
                                translate(
                                  "components.data-management.version-history.empty-value",
                                )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-1 line-clamp-4 break-words font-medium [overflow-wrap:anywhere]">
                          {formatChange(selectedCommit)}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-sm text-muted-foreground">
                    {translate(
                      "components.data-management.version-history.select-non-current",
                    )}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            ref={cancelButtonRef}
            variant="outline"
            disabled={pending}
            onClick={close}
          >
            {translate("common.actions.cancel")}
          </Button>
          <Button
            disabled={!canRollback}
            onClick={() => {
              if (canRollback)
                emitIntent("capability.record-history.rollback", {
                  aggregateId,
                  targetCommitId: selectedId,
                  expectedHeadCommitId: headCommitId,
                  reason: reason.trim(),
                });
            }}
          >
            {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {translate(
              "components.data-management.version-history.confirm-rollback",
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RecordHistoryCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  childrenBySlot,
  translate,
  emitIntent,
}) => {
  if (model.presentation === "data-explorer-dialog") {
    return (
      <DataExplorerRecordHistoryDialog
        model={model}
        translate={translate}
        emitIntent={emitIntent}
      />
    );
  }
  const aggregateId = stringValue(model.aggregateId);
  const headCommitId = stringValue(model.headCommitId);
  const selectedTargetCommitId = stringValue(model.selectedTargetCommitId);
  const commits = objectArray(model.commits);
  const pagination = cursorPagination(model.pagination);
  const diff = isJsonObject(model.diff) ? objectArray(model.diff.items) : [];
  const reason = typeof model.reason === "string" ? model.reason : "";
  const rollback = isJsonObject(model.rollback) ? model.rollback : {};
  const deletion = isJsonObject(model.deletion) ? model.deletion : {};
  const pending = rollback.pending === true;
  const deleting = deletion.pending === true;
  const rollbackEnabled = rollback.enabled === true;
  const canRollback = Boolean(
    rollbackEnabled &&
      !pending &&
      !deleting &&
      aggregateId &&
      headCommitId &&
      selectedTargetCommitId &&
      reason.trim(),
  );
  const title = copyValue(
    model.title,
    model.titleKey,
    translate,
    DESIGN_CAPABILITY_I18N_KEYS.recordHistory.title,
  );
  const closeLabel = translate(
    stringValue(model.closeLabelKey) ??
      DESIGN_CAPABILITY_I18N_KEYS.recordHistory.close,
  );

  const content = (
    <BaseStack gap="var(--global-spacing)">
      <section
        aria-label={translate(
          DESIGN_CAPABILITY_I18N_KEYS.recordHistory.commits,
        )}
      >
        <ol>
          {commits.map((commit) => {
            const commitId = stringValue(commit.commitId);
            if (!commitId) return null;
            const selected = commitId === selectedTargetCommitId;
            const label = copyValueOrFallback(
              commit.label,
              commit.labelKey,
              translate,
              commitId,
            );
            const metadata = [
              stringValue(commit.actorLabel),
              stringValue(commit.createdAt),
              stringValue(commit.summary),
            ].filter(Boolean);
            return (
              <li key={commitId}>
                <BaseButton
                  label={
                    <span>
                      <span>{label}</span>
                      {metadata.length ? (
                        <span> — {metadata.join(" · ")}</span>
                      ) : null}
                    </span>
                  }
                  active={selected}
                  aria-pressed={selected}
                  disabled={commit.disabled === true}
                  variant="ghost"
                  onClick={() =>
                    emitIntent("capability.record-history.select", {
                      targetCommitId: commitId,
                    })
                  }
                />
              </li>
            );
          })}
        </ol>
        <CursorLoadMore
          pagination={pagination}
          label={translate(DESIGN_CAPABILITY_I18N_KEYS.recordHistory.loadMore)}
          loadingLabel={translate(
            DESIGN_CAPABILITY_I18N_KEYS.recordHistory.loadingMore,
          )}
          onLoadMore={(cursor) =>
            emitIntent("capability.record-history.load-more", { cursor })
          }
        />
      </section>

      <section
        aria-label={translate(DESIGN_CAPABILITY_I18N_KEYS.recordHistory.diff)}
      >
        {diff.length ? (
          <BaseTableContainer>
            <BaseTable>
              <BaseTableHeader>
                <BaseTableRow>
                  <BaseTableHead scope="col">
                    {translate(DESIGN_CAPABILITY_I18N_KEYS.recordHistory.field)}
                  </BaseTableHead>
                  <BaseTableHead scope="col">
                    {translate(
                      DESIGN_CAPABILITY_I18N_KEYS.recordHistory.before,
                    )}
                  </BaseTableHead>
                  <BaseTableHead scope="col">
                    {translate(DESIGN_CAPABILITY_I18N_KEYS.recordHistory.after)}
                  </BaseTableHead>
                </BaseTableRow>
              </BaseTableHeader>
              <BaseTableBody>
                {diff.map((item) => {
                  const fieldId = stringValue(item.fieldId);
                  if (!fieldId) return null;
                  return (
                    <BaseTableRow key={fieldId}>
                      <BaseTableHead scope="row">
                        {copyValueOrFallback(
                          item.label,
                          item.labelKey,
                          translate,
                          fieldId,
                        )}
                      </BaseTableHead>
                      <BaseTableCell>
                        {primitiveValue(item.before)}
                      </BaseTableCell>
                      <BaseTableCell>
                        {primitiveValue(item.after)}
                      </BaseTableCell>
                    </BaseTableRow>
                  );
                })}
              </BaseTableBody>
            </BaseTable>
          </BaseTableContainer>
        ) : (
          <p>{translate(DESIGN_CAPABILITY_I18N_KEYS.recordHistory.noDiff)}</p>
        )}
      </section>

      <BaseField
        label={translate(DESIGN_CAPABILITY_I18N_KEYS.recordHistory.reason)}
        description={translate(
          DESIGN_CAPABILITY_I18N_KEYS.recordHistory.reasonDescription,
        )}
        required
        disabled={pending || deleting}
      >
        {(controlProps) => (
          <BaseTextarea
            {...controlProps}
            value={reason}
            maxLength={2048}
            onChange={(event) =>
              emitIntent("capability.record-history.reason-change", {
                reason: event.currentTarget.value,
              })
            }
          />
        )}
      </BaseField>

      {stringValue(rollback.errorCode) ? (
        <CapabilityStatus
          kind="error"
          code={String(rollback.errorCode)}
          translate={translate}
        />
      ) : null}
      {stringValue(deletion.errorCode) ? (
        <CapabilityStatus
          kind="error"
          code={String(deletion.errorCode)}
          translate={translate}
        />
      ) : null}
      <BaseToolbar actions={childrenBySlot.footer}>
        <BaseButton
          label={translate(DESIGN_CAPABILITY_I18N_KEYS.recordHistory.rollback)}
          tone="warning"
          loading={pending}
          disabled={!canRollback}
          onClick={() => {
            if (
              !aggregateId ||
              !headCommitId ||
              !selectedTargetCommitId ||
              !reason.trim()
            )
              return;
            emitIntent("capability.record-history.rollback", {
              aggregateId,
              targetCommitId: selectedTargetCommitId,
              expectedHeadCommitId: headCommitId,
              reason: reason.trim(),
            });
          }}
        />
        <BaseButton
          label={translate(DESIGN_CAPABILITY_I18N_KEYS.recordHistory.delete)}
          tone="danger"
          loading={deleting}
          disabled={
            deletion.enabled !== true ||
            deletion.confirmationRequired !== true ||
            pending ||
            deleting ||
            !aggregateId ||
            !headCommitId ||
            !reason.trim()
          }
          onClick={() => {
            if (!aggregateId || !headCommitId || !reason.trim()) return;
            emitIntent("capability.record-history.delete", {
              aggregateId,
              expectedHeadCommitId: headCommitId,
              reason: reason.trim(),
            });
          }}
        />
      </BaseToolbar>
    </BaseStack>
  );

  if (model.presentation === "dialog") {
    return (
      <BaseDialog
        open
        portal
        title={title}
        closeLabel={closeLabel}
        closeOnEscape={!pending && !deleting}
        closeOnOverlayClick={!pending && !deleting}
        classNames={{ panel: "max-w-5xl" }}
        onOpenChange={(open) => {
          if (!open && !pending && !deleting) {
            emitIntent("capability.record-history.close", {});
          }
        }}
      >
        {childrenBySlot.header}
        {content}
      </BaseDialog>
    );
  }

  return (
    <DataExplorerDetailShell
      title={title}
      leading={childrenBySlot.header}
      preview={null}
      previewVisible={false}
      closeLabel={closeLabel}
      closeVisible
      onClose={() => emitIntent("capability.record-history.close", {})}
      sections={[{ id: "record-history", children: content }]}
    />
  );
};

const localizedModelCopy = (
  model: JsonObject,
  name: "eyebrow" | "title" | "description",
  translate: CapabilityTranslate,
) => {
  const key = stringValue(model[`${name}Key`]);
  return key ? translate(key) : stringValue(model[name]);
};

export const SectionHeaderCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  childrenBySlot,
  translate,
}) => (
  <BaseSectionHeader
    eyebrow={localizedModelCopy(model, "eyebrow", translate)}
    title={localizedModelCopy(model, "title", translate)}
    description={localizedModelCopy(model, "description", translate)}
    actions={childrenBySlot.actions}
  />
);

const NOTICE_TONES = new Set([
  "default",
  "primary",
  "success",
  "warning",
  "danger",
  "muted",
]);

export const NoticeCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  childrenBySlot,
  translate,
}) => {
  const requestedTone = stringValue(model.tone);
  const tone =
    requestedTone && NOTICE_TONES.has(requestedTone)
      ? (requestedTone as
          | "default"
          | "primary"
          | "success"
          | "warning"
          | "danger"
          | "muted")
      : "default";
  return (
    <BaseNotice
      tone={tone}
      icon={childrenBySlot.icon}
      title={localizedModelCopy(model, "title", translate)}
      description={localizedModelCopy(model, "description", translate)}
      actions={childrenBySlot.actions}
    />
  );
};

export const DescriptionListCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  childrenBySlot,
  translate,
}) => {
  const columns =
    model.columns === 2 || model.columns === 3 ? model.columns : 1;
  const items = objectArray(model.items).flatMap((item) => {
    const id = stringValue(item.id);
    if (!id) return [];
    return [
      {
        term:
          (stringValue(item.termKey)
            ? translate(String(item.termKey))
            : stringValue(item.term)) ?? "",
        value: item.valueType === 'timestamp' ? renderDateTimeValue(item.value, 'milliseconds', stringValue(item.locale)) :
          (stringValue(item.valueKey)
            ? translate(String(item.valueKey))
            : stringValue(item.value)) ?? "",
      },
    ];
  });
  const label = stringValue(model.labelKey)
    ? translate(String(model.labelKey))
    : stringValue(model.label);
  return (
    <BaseStack gap="var(--global-spacing)">
      <BaseDescriptionList items={items} columns={columns} aria-label={label} />
      {childrenBySlot.footer}
    </BaseStack>
  );
};

export const ContentPanelCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  childrenBySlot,
  translate,
}) => {
  const requestedDensity = stringValue(model.density);
  const density =
    requestedDensity === "compact" || requestedDensity === "comfortable"
      ? requestedDensity
      : "default";
  return (
    <BasePanel
      title={localizedModelCopy(model, "title", translate)}
      description={localizedModelCopy(model, "description", translate)}
      media={childrenBySlot.media}
      footer={childrenBySlot.footer}
      selected={model.selected === true}
      disabled={model.disabled === true}
      density={density}
    >
      {childrenBySlot.body}
    </BasePanel>
  );
};

export const ToolbarCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  childrenBySlot,
  translate,
}) => (
  <BaseToolbar
    leading={childrenBySlot.leading}
    title={localizedModelCopy(model, "title", translate)}
    description={localizedModelCopy(model, "description", translate)}
    actions={childrenBySlot.actions}
  >
    {childrenBySlot.content}
  </BaseToolbar>
);

export const StatusCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  childrenBySlot,
  translate,
  retry,
}) => {
  const requestedKind = stringValue(model.kind);
  const kind =
    requestedKind === "loading" ||
    requestedKind === "empty" ||
    requestedKind === "error" ||
    requestedKind === "forbidden"
      ? requestedKind
      : "error";
  if (model.presentation === "legacy-data-explorer") {
    return (
      <div
        className="col-span-full flex h-full min-h-0 flex-1 flex-col overflow-hidden"
        data-presentation="legacy-data-explorer-status"
      >
        {stringValue(model.noticeKey) ? (
          <div className="mx-4 mt-3 shrink-0 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
            {translate(String(model.noticeKey))}
          </div>
        ) : null}
        {kind === "loading" ? (
          <CapabilityStatus kind="loading" translate={translate} titleKey={stringValue(model.titleKey)} />
        ) : (
          <div className="flex h-64 items-center justify-center">
            <p className="text-muted-foreground">
              {translate(
                stringValue(model.emptyLabelKey) ??
                  DESIGN_CAPABILITY_I18N_KEYS.status.empty.title,
              )}
            </p>
          </div>
        )}
      </div>
    );
  }
  return (
    <CapabilityStatus
      kind={kind}
      code={stringValue(model.code)}
      titleKey={stringValue(model.titleKey)}
      descriptionKey={stringValue(model.descriptionKey)}
      actions={childrenBySlot.actions}
      onRetry={model.retryable === true ? retry : undefined}
      translate={translate}
    />
  );
};

export interface ProfessionalCapabilityBinding {
  render(context: DesignCapabilityRendererContext): ReactNode;
}

const isProfessionalBinding = (
  value: unknown,
): value is ProfessionalCapabilityBinding =>
  typeof value === "object" &&
  value !== null &&
  "render" in value &&
  typeof value.render === "function";

export const ProfessionalCapabilityLeafRenderer: DesignCapabilityRenderer = (
  context,
) => {
  if (!isProfessionalBinding(context.binding)) {
    return (
      <CapabilityStatus
        kind="error"
        code="capability.professional.renderer.missing"
        translate={context.translate}
      />
    );
  }
  const content = context.binding.render(context);
  const body = (
    <>
      {context.childrenBySlot.toolbar}
      {content}
      {context.childrenBySlot.footer}
    </>
  );
  return context.entry.provider.frameOwner === "provider" ? (
    body
  ) : (
    <BaseLayoutPane>{body}</BaseLayoutPane>
  );
};

export const NEUTRAL_CAPABILITY_RENDERERS = Object.freeze({
  'monkeys.design.renderer.choice-picker': ChoicePickerCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.runtimeTerminal]: RuntimeTerminalCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.orderedSelection]: OrderedSelectionCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.numericSeries]: NumericSeriesCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.summaryChart]: SummaryChartCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.filePicker]: FilePickerCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.shellLayout]: ShellLayoutCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.sidebarFilter]:
    SidebarFilterCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.gallery]: GalleryCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.ontologyNavigator]:
    OntologyNavigatorCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.dataToolbar]: DataToolbarCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.ontologyTable]:
    OntologyTableCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.ontologyTrendMatrix]:
    OntologyTrendMatrixCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.dataContextBar]:
    DataContextBarCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.cardGrid]: CardGridCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.pagination]: PaginationCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.recordDetail]:
    RecordDetailCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.recordEditor]:
    RecordEditorCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.recordHistory]:
    RecordHistoryCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.ontologyManagement]:
    OntologyManagementCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.featureColumnManager]:
    FeatureColumnManagerCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.sectionHeader]:
    SectionHeaderCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.notice]: NoticeCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.descriptionList]:
    DescriptionListCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.contentPanel]:
    ContentPanelCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.toolbar]: ToolbarCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.status]: StatusCapabilityRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.professionalLeaf]:
    ProfessionalCapabilityLeafRenderer,
  [DESIGN_CAPABILITY_RENDERER_KEYS.professionalExtensionLeaf]:
    ProfessionalCapabilityLeafRenderer,
});
